/**
 * InProgress.jsx — Phase C.4
 *
 * Two sections toggled by a pill:
 *   - Demos: upcoming + recently completed (with "Not logged" flag on missing outcome)
 *   - Trials: active trials with health, trend, progress
 *
 * Interactive: demos and trials can be edited inline and persisted to Google Sheets.
 *
 * Props:
 *   data           — from useSheetData
 *   onOpenTimeline — (contact) => void
 *   getToken       — from useAuth
 *   onRefresh      — () => void — reloads sheet data after a write
 */

import { useState, useMemo, useEffect, useRef, Component, startTransition } from 'react';
import { computeHealth, trialLastLogin, trialFeatureCount } from '../utils/health.js';
import { updateDemo as apiUpdateDemo, updateTrial as apiUpdateTrial, createTrial as apiCreateTrial, deleteDemos as apiDeleteDemos, deleteTrials as apiDeleteTrials, offboardContact as apiOffboardContact, appendOutreachLog, linkEmailToContact, searchContacts, apiAddTrialNote, createContact as apiCreateContact, createFirm as apiCreateFirm, updateFirm as apiUpdateFirm, updateContact as apiUpdateContact, fetchZoomNotesByDemo } from '../api/backend.js';
import { requestAmplitudeSync } from '../api/sync.js';
import { apiConvertTrial, apiChurnTrial, apiUnlockTrial, apiLockTrial, apiFlagClerkUser, apiSyncClerkRoster, apiGetTrialNotes, getContact, mergeTrialContact, lookupFirmByDomain } from '../api/backend.js';
import { LUMOSITY_ENABLED, createProductUser, copyProductUser, precheckCopyUser } from '../api/lumosity.js';
import { useLumosityAuth } from '../auth/useLumosityAuth.js';
import MatchContactPanel from '../components/MatchContactPanel.jsx';
import MergeContactModal from '../components/MergeContactModal.jsx';
import UsageJournalPanel from '../components/UsageJournalPanel.jsx';
import { InlineDate, InlineSelect } from '../components/InlineEdit.jsx';
import FirmCombobox from '../components/FirmCombobox.jsx';

const TRIAL_STATUS_OPTIONS = ['Active', 'Converted', 'Churned', 'Cancelled'];

// ── Error boundary — prevents a single section crash from white-screening the page ──
class SectionErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: '20px 16px', background: 'var(--red-bg)', border: '1px solid rgba(220,38,38,.2)', borderRadius: 'var(--radius)', color: 'var(--red)', fontSize: 13 }}>
          <strong>Section failed to render.</strong> Try refreshing the page.
          <br /><span style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4, display: 'block', wordBreak: 'break-all' }}>{String(this.state.error)}</span>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

// Parse a date string in local time. Pure date strings ("YYYY-MM-DD") are
// treated as local midnight so they don't shift a day in UTC-west timezones.
function parseDateLocal(dateStr) {
  const s = String(dateStr ?? '');
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(s + 'T00:00:00');
  return new Date(s);
}

function fmt(dateStr) {
  if (!dateStr) return '—';
  const d = parseDateLocal(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateTime(dateStr) {
  if (!dateStr) return '—';
  const d = parseDateLocal(dateStr);
  if (isNaN(d)) return dateStr;
  const datePart = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  const hasTime = /[T ]\d{2}:\d{2}/.test(String(dateStr));
  if (!hasTime) return datePart;
  const timePart = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return `${datePart} · ${timePart}`;
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = parseDateLocal(dateStr);
  if (isNaN(d)) return null;
  return Math.ceil((d.getTime() - Date.now()) / 86_400_000);
}

function relDate(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d)) return ts;
  const diff = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff < 14) return `${diff}d ago`;
  return fmt(ts);
}

function genId(prefix) {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

function getInitials(name = '') {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
}

const todayISO = () => new Date().toISOString().slice(0, 10);

function exportTrialsCsv(trials) {
  const headers = ['Contact', 'Firm', 'Role', 'Sector', 'Status', 'Start Date', 'End Date', 'Days Left', 'Health', 'Last Login', 'This Week Logins', 'Last Week Logins', 'Features Used Count', 'Features Used'];
  const lines = [
    headers.join(','),
    ...trials.map((t) => {
      const rowContacts = t._contacts?.length > 0 ? t._contacts : (t._contact ? [t._contact] : []);
      const names = rowContacts.map((c) => c.name || c.email).join('; ');
      return [
        names,
        t._firm?.firm_name ?? '',
        rowContacts.map((c) => c.role).filter(Boolean).join('; '),
        trialSectors(t).join('; '),
        t.status ?? '',
        t.start_date ?? '',
        t.end_date ?? '',
        t._daysLeft ?? '',
        t._health?.label ?? '',
        trialLastLogin(t) ?? '',
        t.logins_this_week ?? '',
        t.logins_last_week ?? '',
        t.features_used_count ?? '',
        t.features_used ?? '',
      ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',');
    }),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `trials_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Backend writers ────────────────────────────────────────────────────────────

async function saveDemo(getToken, demoId, edits) {
  await apiUpdateDemo(getToken, demoId, edits);
}

async function saveTrial(getToken, trialId, edits) {
  await apiUpdateTrial(getToken, trialId, edits);
}

async function logTouchpoint(getToken, { contact_id, firm_id, subject, channel = 'Note' }) {
  await appendOutreachLog(getToken, {
    log_id:     genId('log'),
    contact_id: contact_id ?? '',
    firm_id:    firm_id    ?? '',
    date:       todayISO(),
    channel,
    direction:  'outbound',
    type:       'note',
    subject:    subject ?? '',
  });
}

// ── Shared editor field styles ──────────────────────────────────────────────────

const inputStyle = {
  width: '100%', padding: '7px 10px', borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)', fontSize: 13, background: 'var(--surface)',
  color: 'var(--text)', fontFamily: 'inherit', outline: 'none',
};
const labelStyle = {
  display: 'block', fontWeight: 600, fontSize: 11, color: 'var(--text-3)',
  marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.4px',
};

const DEMO_STATUSES = ['Scheduled', 'Completed', 'No-show', 'Cancelled'];
const DEMO_OUTCOMES = ['Positive / Interested', 'Neutral', 'Not a fit', 'Needs follow-up'];
const NEXT_STEP_QUICKPICKS = ['Send proposal', 'Start trial', 'Schedule follow-up', 'Send materials', 'Dead'];
const TRIAL_STATUSES = ['Active', 'Converted', 'Churned'];

// ── Module-level constants (must live here, before component definitions,
//    to avoid TDZ errors in production minified bundles) ───────────────────────

const OUTCOME_PILLS = [
  { value: 'Positive / Interested', label: '✓ Positive',    color: 'var(--green)',  bg: 'var(--green-bg)',  border: 'rgba(5,150,105,.3)' },
  { value: 'Neutral',               label: '~ Neutral',     color: 'var(--amber)',  bg: 'var(--amber-bg)',  border: 'rgba(217,119,6,.3)' },
  { value: 'Needs follow-up',       label: '↩ Follow-up',   color: 'var(--blue)',   bg: 'var(--blue-bg)',   border: 'rgba(37,99,235,.3)' },
  { value: 'Not a fit',             label: '✗ Not a fit',   color: 'var(--red)',    bg: 'var(--red-bg)',    border: 'rgba(220,38,38,.3)' },
];

const GROUP_ORDER = ['act_today', 'this_week', 'expired', 'monitoring'];
const GROUP_META = {
  act_today:  { label: 'Act Today',               color: 'var(--red)',    bg: 'var(--red-bg)' },
  this_week:  { label: 'This Week',               color: 'var(--amber)',  bg: 'var(--amber-bg, #fffbeb)' },
  expired:    { label: 'Expired · Need Response', color: 'var(--text-3)', bg: 'var(--surface-2)' },
  monitoring: { label: 'On Track',                color: 'var(--green)',  bg: 'var(--green-bg)' },
};

const CAL_START_HOUR = 8;
const CAL_END_HOUR   = 20;
const HOUR_PX        = 56;
const DAY_ABBR       = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
const MONTH_ABBR     = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const TRIAL_DURATIONS = [
  { label: '2 weeks', days: 14 },
  { label: '1 month', days: 30 },
  { label: '3 months', days: 90 },
  { label: '6 months', days: 180 },
];

const NEXT_STEP_QUICKPICKS_NO_DEAD = ['Send proposal', 'Start trial', 'Schedule follow-up', 'Send materials'];

const MAX_WEEK_DOTS = 8;


const TIMELINE_PREF_KEY = 'trial_log_to_timeline_v1';

const TIER_COLOR = { healthy: 'var(--green)', watch: 'var(--amber)', 'at-risk': 'var(--orange, #ea580c)', churned: 'var(--red)' };
const TIER_BG    = { healthy: 'var(--green-bg)', watch: 'var(--amber-bg, #fffbeb)', 'at-risk': 'var(--orange-bg, #fff7ed)', churned: 'var(--red-bg)' };
const TIER_LABEL = { healthy: 'Healthy', watch: 'Watch', 'at-risk': 'At risk', churned: 'Churned' };

// ── Main component ────────────────────────────────────────────────────────────

export default function InProgress({ data, onOpenTimeline, onOpenAddContact, onOpenDraft, getToken, onRefresh, patchTrial, upsertContact, pendingUnknownCount = 0, highlightDemoId, onClearHighlight, highlightTrialId, onClearTrialHighlight, amplitudeScores, watchlistMap = {}, settings = {}, userEmail = '', userName = '' }) {
  const [section, setSection] = useState('trials');

  useEffect(() => {
    if (highlightDemoId) setSection('demos');
  }, [highlightDemoId]);

  useEffect(() => {
    if (highlightTrialId) setSection('trials');
  }, [highlightTrialId]);

  const { contacts = [], contactEmailMap = [], firms = [], demos = [], trials = [], outreachLog: _rawOutreachLog } = data ?? {};
  // Stabilize reference: prevents activeTrials from recomputing when data object
  // is recreated but outreachLog content hasn't changed.
  const outreachLog = useMemo(() => _rawOutreachLog ?? [], [_rawOutreachLog]);

  const contactMap = useMemo(() => {
    const m = {};
    for (const c of contacts) { if (c.contact_id) m[c.contact_id] = c; }
    return m;
  }, [contacts]);

  // email (lowercase) → contact — built from the full-table email map (all 40k+ contacts)
  // so attendee resolution is never limited by the 2k paginated contacts subset.
  const contactsByEmail = useMemo(() => {
    const m = {};
    // emailMap first (sparse, full table), then contacts overwrites with full-field data
    for (const c of contactEmailMap) { if (c.email) m[String(c.email).toLowerCase().trim()] = c; }
    for (const c of contacts) { if (c.email) m[String(c.email).toLowerCase().trim()] = c; }
    return m;
  }, [contactEmailMap, contacts]);

  const firmMap = useMemo(() => {
    const m = {};
    for (const f of firms) { if (f.firm_id) m[f.firm_id] = f; }
    return m;
  }, [firms]);

  // ── Demo data — map all demos, then split into 3 buckets ──────────────────
  const [optimisticDeletedIds, setOptimisticDeletedIds] = useState(new Set());
  const allDemos = useMemo(() => {
    return demos.filter((d) => !optimisticDeletedIds.has(d.demo_id)).map((d) => {
      const c = contactMap[d.contact_id] ?? {};
      const f = firmMap[c.firm_id ?? d.firm_id] ?? {};
      const status   = (d.status    ?? '').toLowerCase();
      const nextStep = (d.next_step ?? '').toLowerCase();
      const sd = d.scheduled_date ?? '';
      let isPastDue = false;
      if (sd && status === 'scheduled') {
        if (sd.includes('T')) {
          isPastDue = parseDateLocal(sd).getTime() < Date.now();
        } else {
          const raw = parseDateLocal(sd);
          const dayMs = new Date(raw.getFullYear(), raw.getMonth(), raw.getDate()).getTime();
          isPastDue = dayMs < new Date(new Date().setHours(0, 0, 0, 0)).getTime();
        }
      }
      const isFutureDate = sd
        ? (sd.includes('T') ? parseDateLocal(sd).getTime() > Date.now()
          : new Date(new Date(parseDateLocal(sd)).setHours(23, 59, 59, 999)).getTime() > Date.now())
        : false;
      const needsOutcome = (status === 'completed' && !d.outcome && !isFutureDate) || isPastDue;
      const nextDue = daysUntil(d.next_step_due);
      return {
        ...d,
        _contact: c,
        _firm: f,
        _status: status,
        _nextStep: nextStep,
        _isPastDue: isPastDue,
        _needsOutcome: needsOutcome,
        _nextOverdue: nextDue !== null && nextDue < 0,
        _nextDue: nextDue,
      };
    });
  }, [demos, contactMap, firmMap]);

  // ── Demo buckets (urgency-ordered) ───────────────────────────
  const todayDemos = useMemo(() => {
    const todayStr = new Date().toDateString();
    return allDemos
      .filter((d) => {
        if (d._status !== 'scheduled' || d._isPastDue) return false;
        const sd = d.scheduled_date ?? '';
        if (!sd) return false;
        return parseDateLocal(sd).toDateString() === todayStr;
      })
      .sort((a, b) => parseDateLocal(a.scheduled_date || 0) - parseDateLocal(b.scheduled_date || 0));
  }, [allDemos]);

  const thisWeekDemos = useMemo(() => {
    const todayStr = new Date().toDateString();
    return allDemos
      .filter((d) => {
        if (d._status !== 'scheduled' || d._isPastDue) return false;
        const sd = d.scheduled_date ?? '';
        if (!sd) return false;
        if (parseDateLocal(sd).toDateString() === todayStr) return false;
        const daysAway = Math.ceil((parseDateLocal(sd).getTime() - Date.now()) / 86_400_000);
        return daysAway > 0 && daysAway <= 7;
      })
      .sort((a, b) => parseDateLocal(a.scheduled_date || 0) - parseDateLocal(b.scheduled_date || 0));
  }, [allDemos]);

  const upcomingDemos = useMemo(() =>
    allDemos
      .filter((d) => {
        if (d._status !== 'scheduled' || d._isPastDue) return false;
        const sd = d.scheduled_date ?? '';
        if (!sd) return false;
        const daysAway = Math.ceil((parseDateLocal(sd).getTime() - Date.now()) / 86_400_000);
        return daysAway > 7;
      })
      .sort((a, b) => parseDateLocal(a.scheduled_date || 0) - parseDateLocal(b.scheduled_date || 0)),
  [allDemos]);

  // Needs outcome: completed without outcome, or past-due scheduled (next_step ≠ dead)
  const needsOutcomeDemos = useMemo(() =>
    allDemos
      .filter((d) => d._needsOutcome && d._nextStep !== 'dead')
      .sort((a, b) => parseDateLocal(b.scheduled_date || 0) - parseDateLocal(a.scheduled_date || 0)),
  [allDemos]);

  // Done: completed with outcome set
  const doneDemos = useMemo(() =>
    allDemos
      .filter((d) => d._status === 'completed' && d.outcome && !d._needsOutcome && d._nextStep !== 'dead')
      .sort((a, b) => parseDateLocal(b.scheduled_date || 0) - parseDateLocal(a.scheduled_date || 0)),
  [allDemos]);

  // Hidden by default: cancelled / no-show
  const hiddenDemos = useMemo(() =>
    allDemos.filter((d) => d._status === 'cancelled' || d._status === 'no-show'),
  [allDemos]);

  // Keep these for backward compat (used by tab count badge)
  const openDemos = useMemo(() =>
    allDemos.filter((d) => d._status === 'scheduled' && !d._isPastDue),
  [allDemos]);

  const completedDemos = useMemo(() =>
    allDemos.filter((d) => (d._status === 'completed' && d._nextStep !== 'dead') || d._isPastDue),
  [allDemos]);

  const needsOutcomeCount = useMemo(() => needsOutcomeDemos.length, [needsOutcomeDemos]);

  // Pre-build contact → max-touch-timestamp map so activeTrials doesn't do O(n×m)
  const lastTouchByContact = useMemo(() => {
    const m = new Map();
    for (const r of outreachLog) {
      if (!r.contact_id) continue;
      const t = new Date(r.date || 0).getTime();
      if (!isNaN(t) && t > 0) {
        const prev = m.get(r.contact_id) ?? 0;
        if (t > prev) m.set(r.contact_id, t);
      }
    }
    return m;
  }, [outreachLog]);

  // ── Active trials ──────────────────────────────────────────────────────────
  // allTrialsDerived carries the same per-trial derived fields (_health, _daysLeft,
  // _group, _weekLogs, etc.) for every trial regardless of status, so the Trials
  // tab's status filter (Active/Converted/Churned/Cancelled/All) can reuse one
  // mapping pass instead of re-deriving fields per status.
  const allTrialsDerived = useMemo(() => {
    const now = Date.now();
    return trials
      .map((t) => {
        const c = contactMap[t.contact_id] ?? {};
        const f = firmMap[c.firm_id ?? t.firm_id] ?? {};
        const ids = t.contact_ids
          ? String(t.contact_ids).split(',').map((id) => id.trim()).filter(Boolean)
          : (t.contact_id ? [t.contact_id] : []);
        const allContacts = ids.map((id) => contactMap[id]).filter(Boolean);
        const health    = computeHealth(t, settings);
        const startMs   = new Date(t.start_date || 0).getTime();
        const endMs     = new Date(t.end_date   || 0).getTime();
        const totalDays = endMs > startMs ? Math.max(1, Math.round((endMs - startMs) / 86_400_000)) : 14;
        const elapsed   = endMs > startMs ? Math.max(0, Math.min(1, (now - startMs) / (endMs - startMs))) : 0;
        const daysLeft  = Math.max(0, Math.ceil((endMs - now) / 86_400_000));
        const daysOverdue = endMs > 0 ? Math.floor((now - endMs) / 86_400_000) : -999;
        const weekThis  = parseInt(t.logins_this_week ?? '0') || 0;
        const weekLast  = parseInt(t.logins_last_week  ?? '0') || 0;
        const trend     = weekThis >= weekLast + 2 ? '↑' : weekThis <= weekLast - 2 ? '↓' : '→';
        const critical  = health.score === 'red' && elapsed > 0.5;

        // Last touch from outreach log (O(1) via pre-built map)
        const contactIdSet = new Set(ids.length ? ids : (t.contact_id ? [t.contact_id] : []));
        let lastTouchMs = 0;
        for (const cid of contactIdSet) {
          const ts = lastTouchByContact.get(cid) ?? 0;
          if (ts > lastTouchMs) lastTouchMs = ts;
        }
        const lastTouchDate = lastTouchMs > 0 ? new Date(lastTouchMs).toISOString() : null;
        const lastTouchDaysAgo = lastTouchDate ? Math.floor((now - lastTouchMs) / 86_400_000) : null;

        // Grouping
        let group = 'monitoring';
        if (daysOverdue > 0) group = 'expired';
        else if (daysLeft <= 1) group = 'act_today';
        else if (daysLeft <= 7) group = 'this_week';

        // Parse week-tagged logs from notes
        const weekLogs = {};
        if (t.notes) {
          for (const line of t.notes.split('\n').filter(Boolean)) {
            const m = line.match(/^\[W(\d+)[^\]]*\]\s*(.*)/);
            if (m) {
              const w = parseInt(m[1]);
              if (!weekLogs[w]) weekLogs[w] = [];
              weekLogs[w].push(m[2] || '');
            }
          }
        }

        const result = {
          ...t,
          _contact: c,
          _firm: f,
          _contacts: allContacts,
          _health: health,
          _elapsed: elapsed,
          _totalDays: totalDays,
          _daysLeft: daysLeft,
          _daysOverdue: daysOverdue,
          _trend: trend,
          _critical: critical,
          _lastTouchDate: lastTouchDate,
          _lastTouchDaysAgo: lastTouchDaysAgo,
          _group: group,
          _weekLogs: weekLogs,
        };

        // Suggested action text
        const never = lastTouchDaysAgo === null;
        if (group === 'act_today') {
          result._suggestedAction = daysLeft <= 0
            ? (never ? 'Expired — never contacted, reach out now' : 'Expired — push for conversion decision')
            : (never ? 'Ends tomorrow — no contact yet' : 'Ends tomorrow — confirm next steps');
        } else if (group === 'expired') {
          result._suggestedAction = never
            ? `Expired ${daysOverdue}d ago — never contacted`
            : lastTouchDaysAgo >= 7
              ? `Expired ${daysOverdue}d ago — last outreach ${lastTouchDaysAgo}d ago`
              : `Expired ${daysOverdue}d ago — last outreach ${lastTouchDaysAgo}d ago`;
        } else if (group === 'this_week') {
          result._suggestedAction = `Expires in ${daysLeft}d — schedule renewal conversation`;
        } else {
          result._suggestedAction = null;
        }

        return result;
      });
  }, [trials, contactMap, firmMap, lastTouchByContact, settings]);

  const activeTrials = useMemo(() => {
    return allTrialsDerived
      .filter((t) => (t.status ?? '').toLowerCase() === 'active')
      .sort((a, b) => {
        const order = { act_today: 0, this_week: 1, expired: 2, monitoring: 3 };
        const gDiff = (order[a._group] ?? 3) - (order[b._group] ?? 3);
        if (gDiff !== 0) return gDiff;
        if (a._group === 'expired') return b._daysOverdue - a._daysOverdue;
        return a._daysLeft - b._daysLeft;
      });
  }, [allTrialsDerived]);

  if (!data) {
    return (
      <div className="loading-page">
        <div className="spinner" />
        <span>Loading in-progress items…</span>
      </div>
    );
  }

  const criticalTrials = activeTrials.filter((t) => t._group === 'act_today').length;

  return (
    <div className={`inprogress-page inprogress-page--${section}`}>
      {/* Section toggle — underline tab style */}
      <div className="inprogress-page__tabs">
        {[
          { key: 'trials', label: 'Trials', count: activeTrials.length, badge: criticalTrials },
          { key: 'demos', label: 'Demos', count: openDemos.length + completedDemos.length, badge: needsOutcomeCount },
        ].map(({ key, label, count, badge }) => (
          <button
            key={key}
            onClick={() => startTransition(() => setSection(key))}
            className={`inprogress-page__tab${section === key ? ' is-active' : ''}`}
          >
            {label}
            <span className="inprogress-page__tab-count">
              {count}
            </span>
            {badge > 0 && (
              <span className="inprogress-page__tab-badge">
                {badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {section === 'demos' && (
        <SectionErrorBoundary>
          <DemosSection
            allDemos={allDemos}
            todayDemos={todayDemos}
            thisWeekDemos={thisWeekDemos}
            upcomingDemos={upcomingDemos}
            needsOutcomeDemos={needsOutcomeDemos}
            doneDemos={doneDemos}
            hiddenDemos={hiddenDemos}
            needsOutcomeCount={needsOutcomeCount}
            contactsByEmail={contactsByEmail}
            onOpenTimeline={onOpenTimeline}
            onOpenAddContact={onOpenAddContact}
            contacts={contacts}
            firms={firms}
            firmMap={firmMap}
            getToken={getToken}
            onRefresh={onRefresh}
            highlightDemoId={highlightDemoId}
            onClearHighlight={onClearHighlight}
            onOptimisticDelete={setOptimisticDeletedIds}
            settings={settings}
          />
        </SectionErrorBoundary>
      )}
      {section === 'trials' && (
        <SectionErrorBoundary>
          <TrialsSection
            trials={activeTrials}
            allTrials={allTrialsDerived}
            criticalTrials={criticalTrials}
            onOpenTimeline={onOpenTimeline}
            onOpenDraft={onOpenDraft}
            onOpenAddContact={onOpenAddContact}
            patchTrial={patchTrial}
            upsertContact={upsertContact}
            getToken={getToken}
            onRefresh={onRefresh}
            firms={firms ?? []}
            contacts={contacts ?? []}
            highlightTrialId={highlightTrialId}
            onClearTrialHighlight={onClearTrialHighlight}
            amplitudeScores={amplitudeScores}
            watchlistMap={watchlistMap}
            settings={settings}
            userEmail={userEmail}
            userName={userName}
            firmMap={firmMap}
          />
        </SectionErrorBoundary>
      )}
    </div>
  );
}

// ── Relative time label for demo cards ───────────────────────────────────────

function demoTimeChip(dateStr) {
  if (!dateStr) return { label: '—', cls: 'demo-chip--later' };
  const d = parseDateLocal(dateStr);
  if (isNaN(d)) return { label: dateStr, cls: 'demo-chip--later' };
  const now = Date.now();
  const diffMs = d.getTime() - now;
  const diffDays = Math.ceil(diffMs / 86_400_000);
  const todayStr = new Date().toDateString();
  const isToday = d.toDateString() === todayStr;

  const hasTime = /[T ]\d{2}:\d{2}/.test(String(dateStr));
  const timeStr = hasTime ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : null;

  if (diffMs < 0) {
    const daysAgo = Math.abs(diffDays);
    const base = daysAgo === 0 ? 'Earlier today' : daysAgo === 1 ? 'Yesterday' : `${daysAgo}d ago`;
    return { label: timeStr ? `${base} · ${timeStr}` : base, cls: 'demo-chip--past' };
  }
  if (isToday) return { label: timeStr ? `Today · ${timeStr}` : 'Today', cls: 'demo-chip--today' };
  if (diffDays === 1) return { label: timeStr ? `Tomorrow · ${timeStr}` : 'Tomorrow', cls: 'demo-chip--soon' };
  if (diffDays <= 7) return { label: timeStr ? `In ${diffDays}d · ${timeStr}` : `In ${diffDays}d`, cls: 'demo-chip--soon' };
  return { label: timeStr ? `${fmt(dateStr)} · ${timeStr}` : fmt(dateStr), cls: 'demo-chip--later' };
}

// ── Demos ─────────────────────────────────────────────────────────────────────

function DemoDeleteModal({ demos, contactsByEmail, contacts, busy, onConfirm, onCancel }) {
  // Collect all unique contacts whose email appears in any selected demo's attendee_emails.
  const attendeeContacts = useMemo(() => {
    const seen = new Map(); // contact_id → contact
    for (const d of demos) {
      const emails = String(d.attendee_emails ?? '').split(',').map((e) => e.trim().toLowerCase()).filter(Boolean);
      for (const email of emails) {
        const c = contactsByEmail[email];
        if (c && c.contact_id && !seen.has(c.contact_id)) seen.set(c.contact_id, c);
      }
    }
    return [...seen.values()];
  }, [demos, contactsByEmail]);

  // Pre-check contacts sourced from Calendar Sync (i.e. added via this flow).
  const [checkedIds, setCheckedIds] = useState(
    () => new Set(attendeeContacts.filter((c) => c.source === 'Calendar Sync').map((c) => c.contact_id))
  );

  function toggleContact(id) {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999,
    }}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}
    >
      <div style={{
        background: 'var(--surface)', borderRadius: 'var(--radius-md)',
        padding: '24px 28px', width: 480, maxWidth: '92vw',
        maxHeight: '80vh', overflowY: 'auto',
        boxShadow: 'var(--shadow-md)',
      }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>
          Delete {demos.length} demo{demos.length > 1 ? 's' : ''}?
        </div>

        {/* Demos list */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.3px', marginBottom: 8 }}>
            Demos to delete
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {demos.map((d) => (
              <div key={d.demo_id} style={{ fontSize: 13.5, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontWeight: 500 }}>{d.event_title || 'Demo'}</span>
                <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{fmt(d.scheduled_date)}</span>
                <span className={`badge badge-${(d._status ?? d.status ?? '').toLowerCase() === 'completed' ? 'customer' : 'demo'}`} style={{ fontSize: 11 }}>
                  {d.status ?? d._status ?? '—'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Contacts section */}
        {attendeeContacts.length > 0 && (
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginBottom: 20 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.3px', marginBottom: 4 }}>
              Also delete these contacts?
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 10 }}>
              Contacts added via Calendar Sync are pre-selected. Uncheck to keep.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {attendeeContacts.map((c) => (
                <label key={c.contact_id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13.5 }}>
                  <input
                    type="checkbox"
                    checked={checkedIds.has(c.contact_id)}
                    onChange={() => toggleContact(c.contact_id)}
                    style={{ cursor: 'pointer' }}
                  />
                  <div>
                    <span style={{ fontWeight: 500 }}>{c.name || c.email}</span>
                    {c.name && <span style={{ fontSize: 12, color: 'var(--text-3)', marginLeft: 6 }}>{c.email}</span>}
                    {c.source === 'Calendar Sync' && (
                      <span style={{ marginLeft: 8, fontSize: 11, color: '#0369a1', background: '#e0f2fe', borderRadius: 4, padding: '1px 6px' }}>
                        Calendar Sync
                      </span>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn btn--ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            className="btn btn-danger btn--sm"
            style={{ cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1 }}
            onClick={() => onConfirm([...checkedIds])}
            disabled={busy}
          >
            {busy ? 'Deleting…' : `Delete ${demos.length > 1 ? `${demos.length} demos` : 'demo'}${checkedIds.size > 0 ? ` + ${checkedIds.size} contact${checkedIds.size > 1 ? 's' : ''}` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Calendar helpers ──────────────────────────────────────────────────────────

function getWeekStart(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day; // Monday
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function isoDate(date) { return date.toISOString().slice(0, 10); }

function extractHourMinute(dateStr) {
  if (!dateStr) return null;
  // Must go through Date() so UTC timestamps are converted to local time.
  // Regex on the raw string reads UTC hours and produces wrong position + label.
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  const hasTime = /[T ]\d{2}:\d{2}/.test(String(dateStr));
  if (!hasTime) return null;
  return { h: d.getHours(), m: d.getMinutes() };
}

function fmtWeekRange(weekStart) {
  const end = addDays(weekStart, 6);
  const s = weekStart, e = end;
  if (s.getMonth() === e.getMonth())
    return `${MONTH_ABBR[s.getMonth()]} ${s.getDate()}–${e.getDate()}, ${s.getFullYear()}`;
  return `${MONTH_ABBR[s.getMonth()]} ${s.getDate()} – ${MONTH_ABBR[e.getMonth()]} ${e.getDate()}, ${e.getFullYear()}`;
}

// ── DemoCalendarPopover ───────────────────────────────────────────────────────

function DemoCalendarPopover({ demo, anchorRect, contactsByEmail, contacts, getToken, onRefresh, onClose }) {
  const popRef = useRef(null);
  const [outcome, setOutcome]   = useState(demo.outcome ?? '');
  const [notes,   setNotes]     = useState(demo.notes ?? '');
  const [saving,  setSaving]    = useState(false);
  const [saved,   setSaved]     = useState(false);
  const [error,   setError]     = useState(null);

  const attendees = resolveAttendees(demo, contactsByEmail);
  const primary   = attendees.find((a) => a.contact) ?? attendees[0] ?? {};
  const name      = primary.contact?.name ?? primary.email ?? demo.event_title ?? 'Demo';
  const firm      = demo._firm?.firm_name ?? '';
  const timeStr   = demo.scheduled_date ? fmtDateTime(demo.scheduled_date) : '—';
  const isLogMode = demo._status === 'completed' || demo._isPastDue;

  // Position: prefer below anchor, flip up if too low
  const [pos, setPos] = useState({ top: 0, left: 0 });
  useEffect(() => {
    if (!anchorRect || !popRef.current) return;
    const PW = 320, PH = popRef.current.offsetHeight || 340;
    const vw = window.innerWidth, vh = window.innerHeight;
    let top  = anchorRect.bottom + 8;
    let left = anchorRect.left;
    if (top + PH > vh - 16) top = anchorRect.top - PH - 8;
    if (left + PW > vw - 16) left = vw - PW - 16;
    if (left < 8) left = 8;
    setPos({ top, left });
  }, [anchorRect]);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    function onClick(e) { if (popRef.current && !popRef.current.contains(e.target)) onClose(); }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onClick); };
  }, [onClose]);

  async function handleSave() {
    setSaving(true); setError(null);
    try {
      await saveDemo(getToken, demo.demo_id, {
        status: 'Completed',
        outcome: outcome || null,
        notes: notes.trim() !== '' ? notes : (demo.notes ?? null),
      });
      setSaved(true);
      onRefresh?.();
      setTimeout(onClose, 900);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div
      ref={popRef}
      style={{
        position: 'fixed', zIndex: 9999, width: 320,
        top: pos.top, left: pos.left,
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius)', boxShadow: '0 8px 32px rgba(0,0,0,.14)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{ padding: '11px 14px 9px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 13.5, color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
          {firm && <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1 }}>{firm}</div>}
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>🕐 {timeStr}</div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--text-3)', lineHeight: 1, flexShrink: 0, padding: '0 2px' }}>×</button>
      </div>

      <div style={{ padding: '11px 14px' }}>
        {saved ? (
          <div style={{ textAlign: 'center', padding: '18px 0', color: 'var(--green)', fontWeight: 700, fontSize: 13 }}>✓ Saved</div>
        ) : isLogMode ? (
          <>
            {/* Outcome pills */}
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--text-3)', marginBottom: 6 }}>Outcome</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {OUTCOME_PILLS.map((p) => {
                  const sel = outcome === p.value;
                  return (
                    <button key={p.value} type="button" onClick={() => setOutcome(sel ? '' : p.value)}
                      style={{ padding: '4px 10px', borderRadius: 99, border: `1.5px solid ${sel ? p.border : 'var(--border)'}`, background: sel ? p.bg : 'var(--surface)', color: sel ? p.color : 'var(--text-3)', fontWeight: 600, fontSize: 11, cursor: 'pointer' }}>
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Notes */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--text-3)', marginBottom: 5 }}>Notes</div>
              <textarea autoFocus value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="What happened in this demo?" style={{ ...inputStyle, resize: 'none', fontSize: 12 }} />
            </div>
            {error && <div style={{ fontSize: 11.5, color: 'var(--red)', marginBottom: 8 }}>{error}</div>}
            <button
              className="btn btn--primary btn--sm"
              style={{ width: '100%', background: 'var(--orange)', borderColor: 'var(--orange)', fontWeight: 700 }}
              onClick={handleSave} disabled={saving || !outcome}
            >{saving ? 'Saving…' : 'Save outcome'}</button>
          </>
        ) : (
          <div style={{ color: 'var(--text-2)', fontSize: 12.5, padding: '8px 0' }}>
            <div style={{ fontWeight: 600, color: 'var(--blue)', marginBottom: 4 }}>📅 Scheduled</div>
            {demo.event_title && <div style={{ marginBottom: 4 }}>"{demo.event_title}"</div>}
            {attendees.length > 1 && (
              <div style={{ fontSize: 11, color: 'var(--text-3)' }}>+ {attendees.length - 1} more attendee{attendees.length > 2 ? 's' : ''}</div>
            )}
            {demo.outcome && (
              <div style={{ marginTop: 8, fontWeight: 600, color: 'var(--green)' }}>✓ {demo.outcome}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── DemosCalendar ─────────────────────────────────────────────────────────────

function DemosCalendar({ allDemos, contactsByEmail, contacts, getToken, onRefresh }) {
  // view: '3day' | 'week' | 'month'
  const [view,      setView]      = useState('3day');
  const [anchor,    setAnchor]    = useState(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }); // start of visible range
  const [popover,   setPopover]   = useState(null);

  const todayISO_ = todayISO();
  const hours = Array.from({ length: CAL_END_HOUR - CAL_START_HOUR }, (_, i) => CAL_START_HOUR + i);

  // Compute visible days
  const days = useMemo(() => {
    if (view === '3day') return Array.from({ length: 3 }, (_, i) => addDays(anchor, i));
    if (view === 'week') {
      const ws = getWeekStart(anchor);
      return Array.from({ length: 7 }, (_, i) => addDays(ws, i));
    }
    // month: full weeks covering the month
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const start = getWeekStart(first);
    const last  = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    const total = Math.ceil((last.getDate() + ((first.getDay() + 6) % 7)) / 7) * 7;
    return Array.from({ length: total }, (_, i) => addDays(start, i));
  }, [view, anchor]);

  // Map dateISO → demos (sorted by time)
  const demosByDate = useMemo(() => {
    const map = {};
    for (const d of allDemos) {
      const key = (d.scheduled_date ?? '').slice(0, 10);
      if (!key) continue;
      (map[key] ??= []).push(d);
    }
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => {
        const ta = extractHourMinute(a.scheduled_date), tb = extractHourMinute(b.scheduled_date);
        if (!ta && !tb) return 0;
        if (!ta) return 1; if (!tb) return -1;
        return (ta.h * 60 + ta.m) - (tb.h * 60 + tb.m);
      });
    }
    return map;
  }, [allDemos]);

  function navigate(dir) {
    setAnchor((a) => {
      if (view === '3day') return addDays(a, dir * 3);
      if (view === 'week') return addDays(a, dir * 7);
      // month
      const d = new Date(a.getFullYear(), a.getMonth() + dir, 1);
      return d;
    });
  }

  function goToday() {
    const d = new Date(); d.setHours(0,0,0,0);
    setAnchor(d);
  }

  function rangeLabel() {
    if (view === 'month') return `${MONTH_ABBR[anchor.getMonth()]} ${anchor.getFullYear()}`;
    if (view === 'week') {
      const ws = getWeekStart(anchor);
      return fmtWeekRange(ws);
    }
    // 3day
    const end = addDays(anchor, 2);
    if (anchor.getMonth() === end.getMonth())
      return `${MONTH_ABBR[anchor.getMonth()]} ${anchor.getDate()}–${end.getDate()}, ${anchor.getFullYear()}`;
    return `${MONTH_ABBR[anchor.getMonth()]} ${anchor.getDate()} – ${MONTH_ABBR[end.getMonth()]} ${end.getDate()}`;
  }

  function openPopover(e, demo) {
    setPopover({ demo, rect: e.currentTarget.getBoundingClientRect() });
  }

  function demoColor(d) {
    if (d._needsOutcome)           return { bg: 'var(--amber-bg)', border: 'var(--amber)', text: '#b45309' };
    if (d._status === 'completed') return { bg: 'var(--green-bg)', border: 'var(--green)', text: 'var(--green)' };
    if (d._isPastDue)              return { bg: 'var(--red-bg)',   border: 'var(--red)',   text: 'var(--red)' };
    return { bg: '#eff6ff', border: 'var(--blue)', text: 'var(--blue)' };
  }

  function demoLabel(demo) {
    const attendees = resolveAttendees(demo, contactsByEmail);
    const primary   = attendees.find((a) => a.contact) ?? attendees[0] ?? {};
    return primary.contact?.name ?? primary.email ?? demo.event_title ?? 'Demo';
  }

  const isTimeGrid = view !== 'month';

  // Month view: group days into weeks
  const weeks = view === 'month'
    ? Array.from({ length: days.length / 7 }, (_, i) => days.slice(i * 7, i * 7 + 7))
    : null;

  return (
    <div style={{ position: 'relative' }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <button className="btn btn--ghost btn--sm" onClick={goToday}>Today</button>
          <button className="btn btn--ghost btn--sm" style={{ padding: '4px 9px', fontSize: 14 }} onClick={() => navigate(-1)}>‹</button>
          <button className="btn btn--ghost btn--sm" style={{ padding: '4px 9px', fontSize: 14 }} onClick={() => navigate(1)}>›</button>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)', minWidth: 140 }}>{rangeLabel()}</span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 3 }}>
          {[['3day','3 Days'],['week','Week'],['month','Month']].map(([v, label]) => (
            <button
              key={v}
              className={`btn btn--sm ${view === v ? 'btn--primary' : 'btn--ghost'}`}
              style={view === v ? { background: 'var(--navy)', borderColor: 'var(--navy)' } : {}}
              onClick={() => setView(v)}
            >{label}</button>
          ))}
        </div>
      </div>

      {/* ── TIME GRID (3day / week) ── */}
      {isTimeGrid && (
        <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', background: 'var(--surface)' }}>
          {/* Time gutter */}
          <div style={{ width: 44, flexShrink: 0, borderRight: '1px solid var(--border)', background: 'var(--surface-2)' }}>
            <div style={{ height: 36 }} />
            {hours.map((h) => (
              <div key={h} style={{ height: HOUR_PX, borderTop: '1px solid var(--border)', padding: '3px 5px 0', fontSize: 10, color: 'var(--text-3)', fontWeight: 600, lineHeight: 1 }}>
                {h === 12 ? '12p' : h < 12 ? `${h}a` : `${h - 12}p`}
              </div>
            ))}
          </div>
          {/* Day columns */}
          <div style={{ flex: 1, display: 'flex' }}>
            {days.map((day, di) => {
              const iso = isoDate(day);
              const isToday = iso === todayISO_;
              const dayDemos = demosByDate[iso] ?? [];
              return (
                <div key={iso} style={{ flex: 1, minWidth: view === '3day' ? 0 : 72, borderLeft: di > 0 ? '1px solid var(--border)' : 'none', position: 'relative' }}>
                  {/* Day header */}
                  <div style={{ height: 36, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: isToday ? 'var(--navy)' : 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: isToday ? 'rgba(255,255,255,.65)' : 'var(--text-3)' }}>{DAY_ABBR[(day.getDay() + 6) % 7]}</span>
                    <span style={{ fontSize: 13, fontWeight: 800, color: isToday ? '#fff' : 'var(--text)' }}>{day.getDate()}</span>
                  </div>
                  {/* Hour rows */}
                  {hours.map((h) => (
                    <div key={h} style={{ height: HOUR_PX, borderTop: '1px solid var(--border)', position: 'relative' }}>
                      <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, borderTop: '1px dashed var(--border)', opacity: .35 }} />
                    </div>
                  ))}
                  {/* Demo chips */}
                  {dayDemos.map((demo, idx) => {
                    const hm  = extractHourMinute(demo.scheduled_date);
                    const topPx = hm
                      ? 36 + (hm.h - CAL_START_HOUR + hm.m / 60) * HOUR_PX
                      : 36 + idx * 26;
                    const c   = demoColor(demo);
                    const lbl = demoLabel(demo);
                    const timeStr = hm ? `${hm.h > 12 ? hm.h - 12 : hm.h === 0 ? 12 : hm.h}:${String(hm.m).padStart(2,'0')}${hm.h >= 12 ? 'p' : 'a'} ` : '';
                    return (
                      <button key={demo.demo_id} onClick={(e) => openPopover(e, demo)} style={{
                        position: 'absolute', left: 2, right: 2, top: topPx, minHeight: 22, padding: '2px 5px',
                        background: c.bg, border: `1px solid ${c.border}`, borderLeft: `3px solid ${c.border}`,
                        borderRadius: 4, cursor: 'pointer', textAlign: 'left', zIndex: 2,
                        fontSize: 10.5, fontWeight: 700, color: c.text,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        boxShadow: '0 1px 3px rgba(0,0,0,.07)',
                      }}>
                        {timeStr}{lbl}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── MONTH GRID ── */}
      {!isTimeGrid && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
          {/* Day-of-week header row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)' }}>
            {DAY_ABBR.map((d) => (
              <div key={d} style={{ padding: '6px 0', textAlign: 'center', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--text-3)' }}>{d}</div>
            ))}
          </div>
          {/* Week rows */}
          {weeks.map((week, wi) => (
            <div key={wi} style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', borderTop: wi > 0 ? '1px solid var(--border)' : 'none' }}>
              {week.map((day, di) => {
                const iso = isoDate(day);
                const isToday   = iso === todayISO_;
                const isThisMonth = day.getMonth() === anchor.getMonth();
                const dayDemos  = demosByDate[iso] ?? [];
                const MAX_SHOW  = 3;
                const overflow  = dayDemos.length - MAX_SHOW;
                return (
                  <div key={iso} style={{ borderLeft: di > 0 ? '1px solid var(--border)' : 'none', minHeight: 90, padding: '0 0 4px', background: isThisMonth ? 'var(--surface)' : 'var(--surface-2)' }}>
                    {/* Date number */}
                    <div style={{ padding: '5px 7px 4px', display: 'flex', justifyContent: 'flex-end' }}>
                      <span style={{
                        fontSize: 12, fontWeight: 700, lineHeight: 1,
                        color: isToday ? '#fff' : isThisMonth ? 'var(--text)' : 'var(--text-3)',
                        background: isToday ? 'var(--navy)' : 'transparent',
                        borderRadius: '50%', width: 22, height: 22,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      }}>{day.getDate()}</span>
                    </div>
                    {/* Demo chips */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '0 3px' }}>
                      {dayDemos.slice(0, MAX_SHOW).map((demo) => {
                        const c   = demoColor(demo);
                        const hm  = extractHourMinute(demo.scheduled_date);
                        const lbl = demoLabel(demo);
                        const timeStr = hm ? `${hm.h > 12 ? hm.h - 12 : hm.h === 0 ? 12 : hm.h}${hm.h >= 12 ? 'p' : 'a'} ` : '';
                        return (
                          <button key={demo.demo_id} onClick={(e) => openPopover(e, demo)} style={{
                            width: '100%', textAlign: 'left', padding: '2px 5px',
                            background: c.bg, border: `1px solid ${c.border}`, borderLeft: `3px solid ${c.border}`,
                            borderRadius: 3, cursor: 'pointer',
                            fontSize: 10, fontWeight: 700, color: c.text,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {timeStr}{lbl}
                          </button>
                        );
                      })}
                      {overflow > 0 && (
                        <span style={{ fontSize: 10, color: 'var(--text-3)', fontWeight: 600, paddingLeft: 4 }}>+{overflow} more</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Popover */}
      {popover && (
        <DemoCalendarPopover
          demo={popover.demo}
          anchorRect={popover.rect}
          contactsByEmail={contactsByEmail}
          contacts={contacts}
          getToken={getToken}
          onRefresh={onRefresh}
          onClose={() => setPopover(null)}
        />
      )}
    </div>
  );
}

// ── TrialCalendarPopover ──────────────────────────────────────────────────────

function TrialCalendarPopover({ trial, anchorRect, onClose, onCheckIn, getToken, onRefresh }) {
  const popRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!anchorRect || !popRef.current) return;
    const PW = 280, PH = popRef.current.offsetHeight || 200;
    const vw = window.innerWidth, vh = window.innerHeight;
    let top  = anchorRect.bottom + 8;
    let left = anchorRect.left;
    if (top + PH > vh - 16) top = anchorRect.top - PH - 8;
    if (left + PW > vw - 16) left = vw - PW - 16;
    if (left < 8) left = 8;
    setPos({ top, left });
  }, [anchorRect]);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    function onMd(e) { if (popRef.current && !popRef.current.contains(e.target)) onClose(); }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onMd);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onMd); };
  }, [onClose]);

  const daysLeft = trial._daysLeft ?? daysUntil(trial.end_date);
  const groupMeta = GROUP_META[trial._group] ?? GROUP_META.monitoring;

  return (
    <div ref={popRef} style={{
      position: 'fixed', zIndex: 9999, width: 280,
      top: pos.top, left: pos.left,
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius)', boxShadow: '0 8px 32px rgba(0,0,0,.14)',
      overflow: 'hidden',
    }}>
      <div style={{ padding: '10px 13px 8px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          {(() => {
            const cs = trial._contacts?.length > 0 ? trial._contacts : (trial._contact ? [trial._contact] : []);
            const name = cs[0]?.name ?? cs[0]?.email ?? trial._firm?.firm_name ?? trial.contact_id;
            const extra = cs.length > 1 ? ` +${cs.length - 1}` : '';
            return <div style={{ fontWeight: 800, fontSize: 13, color: 'var(--navy)' }}>{name}{extra}</div>;
          })()}
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1 }}>{trial._firm?.firm_name ?? ''}</div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--text-3)', padding: '0 2px' }}>×</button>
      </div>
      <div style={{ padding: '10px 13px' }}>
        <div style={{ display: 'flex', gap: 7, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
          {getToken ? (
            <InlineSelect
              value={trial.status ?? 'Active'}
              options={TRIAL_STATUS_OPTIONS}
              style={{ background: groupMeta.bg, color: groupMeta.color, borderRadius: 99, padding: '2px 9px', fontSize: 11, fontWeight: 700 }}
              onSave={async (v) => { await apiUpdateTrial(getToken, trial.trial_id, { status: v }); onRefresh?.(); onClose(); }}
            />
          ) : (
            <span style={{ background: groupMeta.bg, color: groupMeta.color, border: `1px solid ${groupMeta.color}22`, borderRadius: 99, padding: '2px 9px', fontSize: 11, fontWeight: 700 }}>
              {groupMeta.label}
            </span>
          )}
          {daysLeft != null && (
            <span style={{ fontSize: 12, fontWeight: 700, color: daysLeft <= 7 ? 'var(--red)' : daysLeft <= 14 ? 'var(--amber)' : 'var(--text-2)' }}>
              {daysLeft > 0 ? `${daysLeft}d left` : daysLeft === 0 ? 'Ends today' : `${Math.abs(daysLeft)}d overdue`}
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 10, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {trial.start_date && <span>Started {fmt(trial.start_date)}</span>}
          {(trial.start_date && trial.end_date) && <span>·</span>}
          {getToken ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              Ends{' '}
              <InlineDate
                value={(trial.end_date ?? '').slice(0, 10)}
                display={trial.end_date ? fmt(trial.end_date) : null}
                style={{ fontSize: 11, color: 'var(--text-3)' }}
                onSave={async (v) => { await apiUpdateTrial(getToken, trial.trial_id, { end_date: v }); onRefresh?.(); }}
              />
            </span>
          ) : (
            trial.end_date && <span>Ends {fmt(trial.end_date)}</span>
          )}
        </div>
        {trial._suggestedAction && (
          <div style={{ fontSize: 11.5, fontWeight: 600, color: trial._group === 'act_today' ? 'var(--red)' : 'var(--amber)', marginBottom: 10 }}>
            {trial._suggestedAction}
          </div>
        )}
        <button
          className="btn btn--primary btn--sm"
          style={{ width: '100%', background: 'var(--orange)', borderColor: 'var(--orange)', fontWeight: 700 }}
          onClick={() => { onCheckIn(trial); onClose(); }}
        >📝 Check in</button>
      </div>
    </div>
  );
}

// ── TrialsCalendar ────────────────────────────────────────────────────────────

function TrialsCalendar({ trials, onCheckIn, getToken, onRefresh }) {
  const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()));
  const [popover,   setPopover]   = useState(null);

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const todayISO_ = todayISO();

  // Bucket trials into calendar events per day
  const eventsByDate = useMemo(() => {
    const map = {};
    const add = (iso, type, trial) => { (map[iso] ??= []); if (!map[iso].some((e) => e.trial.trial_id === trial.trial_id && e.type === type)) map[iso].push({ type, trial }); };

    for (const t of trials) {
      const endISO   = (t.end_date ?? '').slice(0, 10);
      const startISO = (t.start_date ?? '').slice(0, 10);

      // Expiring: end_date falls this week
      if (endISO && endISO >= isoDate(weekStart) && endISO <= isoDate(addDays(weekStart, 6))) {
        add(endISO, 'expiring', t);
      }

      // Started: start_date falls this week
      if (startISO && startISO >= isoDate(weekStart) && startISO <= isoDate(addDays(weekStart, 6))) {
        add(startISO, 'started', t);
      }

      // Check-in due: every Monday that falls within an active trial's date range
      if (t._group !== 'expired' && t.status !== 'Converted' && t.status !== 'Churned') {
        const startISO = (t.start_date ?? '').slice(0, 10);
        const endISO   = (t.end_date   ?? '').slice(0, 10);
        for (const day of days) {
          const dayISO = isoDate(day);
          const isMon  = day.getDay() === 1; // Monday
          if (isMon && dayISO >= startISO && dayISO <= endISO) {
            add(dayISO, 'checkin', t);
          }
        }
      }
    }
    return map;
  }, [trials, weekStart]);

  const TYPE_META = {
    expiring: { icon: '🔴', label: 'Expiring', color: 'var(--red)',   bg: 'var(--red-bg)',   border: 'rgba(220,38,38,.3)' },
    checkin:  { icon: '📝', label: 'Check-in', color: 'var(--blue)',  bg: 'var(--blue-bg,#eff6ff)',  border: 'rgba(37,99,235,.25)' },
    started:  { icon: '🟢', label: 'Started',  color: 'var(--green)', bg: 'var(--green-bg)', border: 'rgba(5,150,105,.3)' },
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button className="btn btn--ghost btn--sm" onClick={() => setWeekStart(getWeekStart(new Date()))}>Today</button>
        <button className="btn btn--ghost btn--sm" style={{ padding: '4px 8px' }} onClick={() => setWeekStart((w) => addDays(w, -7))}>‹</button>
        <button className="btn btn--ghost btn--sm" style={{ padding: '4px 8px' }} onClick={() => setWeekStart((w) => addDays(w, 7))}>›</button>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>{fmtWeekRange(weekStart)}</span>
        {/* Legend */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
          {Object.entries(TYPE_META).map(([k, m]) => (
            <span key={k} style={{ fontSize: 10.5, color: m.color, fontWeight: 600 }}>{m.icon} {m.label}</span>
          ))}
        </div>
      </div>

      {/* 7-day grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
        {days.map((day, di) => {
          const iso = isoDate(day);
          const isToday = iso === todayISO_;
          const events = eventsByDate[iso] ?? [];
          return (
            <div key={iso} style={{ borderLeft: di > 0 ? '1px solid var(--border)' : 'none', minHeight: 120 }}>
              {/* Day header */}
              <div style={{ padding: '6px 8px 5px', background: isToday ? 'var(--navy)' : 'var(--surface-2)', borderBottom: '1px solid var(--border)', textAlign: 'center' }}>
                <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', color: isToday ? 'rgba(255,255,255,.7)' : 'var(--text-3)' }}>{DAY_ABBR[(day.getDay() + 6) % 7]}</div>
                <div style={{ fontSize: 13, fontWeight: 800, color: isToday ? '#fff' : 'var(--text)' }}>{day.getDate()}</div>
              </div>
              {/* Events */}
              <div style={{ padding: '6px 4px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {events.map(({ type, trial }, idx) => {
                  const m = TYPE_META[type];
                  const trialContacts = trial._contacts?.length > 0 ? trial._contacts : (trial._contact ? [trial._contact] : []);
                  const label = trialContacts[0]?.name ?? trialContacts[0]?.email ?? trial._firm?.firm_name ?? trial.trial_id;
                  return (
                    <button
                      key={`${type}-${trial.trial_id}-${idx}`}
                      onClick={(e) => setPopover({ trial, rect: e.currentTarget.getBoundingClientRect(), checkInType: type })}
                      style={{
                        width: '100%', textAlign: 'left', padding: '3px 6px',
                        background: m.bg, border: `1px solid ${m.border}`, borderLeft: `3px solid ${m.color}`,
                        borderRadius: 4, cursor: 'pointer', fontSize: 10, fontWeight: 700, color: m.color,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        boxShadow: '0 1px 2px rgba(0,0,0,.05)',
                      }}
                    >
                      {m.icon} {label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {popover && (
        <TrialCalendarPopover
          trial={popover.trial}
          anchorRect={popover.rect}
          onClose={() => setPopover(null)}
          onCheckIn={onCheckIn}
          getToken={getToken}
          onRefresh={onRefresh}
        />
      )}
    </div>
  );
}

// ── DemosSection ──────────────────────────────────────────────────────────────

// Extracted from DemosSection to avoid the React anti-pattern of defining
// components inside render functions (causes unnecessary remounts + TDZ in bundles).
function DemoGroupSection({ label, count, barColor, labelColor, demos, badge, defaultCollapsed = false, cardProps }) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  return (
    <div style={{ marginBottom: 20 }}>
      <button
        onClick={() => setCollapsed((v) => !v)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 10px 0', width: '100%', textAlign: 'left' }}
      >
        <div style={{ width: 3, height: 16, borderRadius: 2, background: barColor, flexShrink: 0 }} />
        <span style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.6px', color: labelColor }}>{label}</span>
        <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 600 }}>({count})</span>
        {badge}
        <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--text-3)' }}>{collapsed ? '▸' : '▾'}</span>
      </button>
      {!collapsed && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {demos.map((d, i) => (
            <DemoCard key={d.demo_id ?? i} d={d} {...cardProps} />
          ))}
        </div>
      )}
    </div>
  );
}

function DemosSection({ allDemos, todayDemos, thisWeekDemos, upcomingDemos, needsOutcomeDemos, doneDemos, hiddenDemos, needsOutcomeCount, contactsByEmail, onOpenTimeline, onOpenAddContact, contacts, firms, firmMap, getToken, onRefresh, highlightDemoId, onClearHighlight, onOptimisticDelete, settings = {} }) {
  const [editingId,   setEditingId]   = useState(null);
  const [busyId,      setBusyId]      = useState(null);
  const [error,       setError]       = useState(null);
  const [showHidden,  setShowHidden]  = useState(false);
  const [showDone,    setShowDone]    = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showModal,   setShowModal]   = useState(false);
  const [deleteBusy,  setDeleteBusy]  = useState(false);
  const [viewMode,    setViewMode]    = useState('list'); // 'list' | 'calendar'

  const totalVisible = todayDemos.length + thisWeekDemos.length + upcomingDemos.length + needsOutcomeDemos.length + doneDemos.length + hiddenDemos.length;

  if (totalVisible === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state__icon">📅</div>
        <div className="empty-state__title">No demos yet</div>
        <div className="empty-state__sub">Demos appear here once synced from Calendar or booked from Pipeline</div>
      </div>
    );
  }

  function toggleSelect(demoId) {
    setSelectedIds((prev) => { const n = new Set(prev); n.has(demoId) ? n.delete(demoId) : n.add(demoId); return n; });
  }

  async function handleDeleteConfirmed(contactIdsToDelete) {
    const idsToDelete = [...selectedIds];
    // Optimistic: hide records immediately in the InProgress-level allDemos filter
    onOptimisticDelete?.((prev) => { const n = new Set(prev); idsToDelete.forEach((id) => n.add(id)); return n; });
    setSelectedIds(new Set()); setShowModal(false); setDeleteBusy(true); setError(null);
    try {
      await apiDeleteDemos(getToken, idsToDelete);
      await Promise.all(contactIdsToDelete.map((id) => apiOffboardContact(getToken, id)));
      onRefresh?.();
    } catch (e) {
      // Roll back optimistic hide and show error
      onOptimisticDelete?.((prev) => { const n = new Set(prev); idsToDelete.forEach((id) => n.delete(id)); return n; });
      setSelectedIds(new Set(idsToDelete));
      setError('Delete failed: ' + e.message);
    } finally {
      setDeleteBusy(false);
    }
  }

  async function markComplete(demo) {
    setBusyId(demo.demo_id); setError(null);
    try {
      await saveDemo(getToken, demo.demo_id, { status: 'Completed' });
      try {
        const contact = contacts.find((c) => c.contact_id === demo.contact_id) ?? {};
        const existing = JSON.parse(localStorage.getItem('addContactBanners') || '[]');
        if (!existing.some((b) => b.demo_id === demo.demo_id)) {
          existing.push({ demo_id: demo.demo_id, contact_name: contact.name || contact.email || 'Unknown', firm_name: (firmMap ?? {})[contact.firm_id]?.firm_name ?? '', demo_title: demo.event_title || '', completed_at: new Date().toISOString() });
          localStorage.setItem('addContactBanners', JSON.stringify(existing));
        }
      } catch {}
      onRefresh?.();
    } catch (e) { setError(`Could not mark complete: ${e.message}`); }
    finally { setBusyId(null); }
  }

  const internalDomains = useMemo(() => parseInternalDomains(settings), [settings]);
  const cardProps = { editingId, setEditingId, busyId, setBusyId, onMarkComplete: markComplete, contactsByEmail, onOpenTimeline, onOpenAddContact, contacts, firms, getToken, onRefresh, selectedIds, onToggle: toggleSelect, highlightDemoId, onClearHighlight, internalDomains };

  const allVisible = [...todayDemos, ...thisWeekDemos, ...upcomingDemos, ...needsOutcomeDemos, ...doneDemos, ...hiddenDemos];
  const selectedDemos = allVisible.filter((d) => selectedIds.has(d.demo_id));

  return (
    <div>
      {/* View toggle */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14, gap: 4 }}>
        {['list', 'calendar'].map((v) => (
          <button
            key={v}
            onClick={() => setViewMode(v)}
            className={`btn btn--sm ${viewMode === v ? 'btn--primary' : 'btn--ghost'}`}
            style={viewMode === v ? { background: 'var(--navy)', borderColor: 'var(--navy)' } : {}}
          >
            {v === 'list' ? '☰ List' : '📅 Calendar'}
          </button>
        ))}
      </div>

      {viewMode === 'calendar' && (
        <DemosCalendar
          allDemos={allDemos}
          contactsByEmail={contactsByEmail}
          contacts={contacts}
          getToken={getToken}
          onRefresh={onRefresh}
        />
      )}

      {viewMode === 'list' && error && (
        <div style={{ background: 'var(--red-bg)', color: 'var(--red)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 16, border: '1px solid rgba(220,38,38,.2)' }}>
          {error}
        </div>
      )}

      {viewMode === 'list' && (
      <div>
      {error && (
        <div style={{ background: 'var(--red-bg)', color: 'var(--red)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 16, border: '1px solid rgba(220,38,38,.2)' }}>
          {error}
        </div>
      )}

      {selectedIds.size > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, background: 'var(--navy)', color: '#fff', borderRadius: 'var(--radius)', padding: '10px 14px' }}>
          <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--orange)' }}>{selectedIds.size} selected</span>
          <button className="btn btn-danger btn--sm" onClick={() => setShowModal(true)}>Delete</button>
          <button className="btn btn--ghost btn--sm" style={{ color: 'rgba(255,255,255,.7)', borderColor: 'rgba(255,255,255,.2)' }} onClick={() => setSelectedIds(new Set())}>Clear</button>
        </div>
      )}

      {todayDemos.length > 0 && (
        <DemoGroupSection label="Today" count={todayDemos.length} barColor="var(--red)" labelColor="var(--red)" demos={todayDemos} cardProps={cardProps} />
      )}

      {needsOutcomeDemos.length > 0 && (
        <DemoGroupSection
          label="Needs Outcome"
          count={needsOutcomeDemos.length}
          barColor="var(--amber)"
          labelColor="var(--amber)"
          demos={needsOutcomeDemos}
          cardProps={cardProps}
          badge={
            <span style={{ background: 'var(--amber-bg)', color: 'var(--amber)', border: '1px solid rgba(217,119,6,.25)', borderRadius: 6, padding: '2px 9px', fontSize: 11, fontWeight: 600 }}>
              ⚠ log outcome to continue
            </span>
          }
        />
      )}

      {thisWeekDemos.length > 0 && (
        <DemoGroupSection label="This Week" count={thisWeekDemos.length} barColor="var(--blue)" labelColor="var(--blue)" demos={thisWeekDemos} cardProps={cardProps} />
      )}

      {upcomingDemos.length > 0 && (
        <DemoGroupSection label="Upcoming" count={upcomingDemos.length} barColor="var(--border-2)" labelColor="var(--text-3)" demos={upcomingDemos} defaultCollapsed={true} cardProps={cardProps} />
      )}

      {doneDemos.length > 0 && (
        <DemoGroupSection label="Done" count={doneDemos.length} barColor="var(--green)" labelColor="var(--green)" demos={doneDemos} defaultCollapsed={true} cardProps={cardProps} />
      )}

      {hiddenDemos.length > 0 && (
        <button
          onClick={() => setShowHidden((v) => !v)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12.5, color: 'var(--text-3)', fontWeight: 600, padding: '4px 0', display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <span>{showHidden ? '▾' : '▸'}</span>
          Show cancelled &amp; no-show ({hiddenDemos.length})
        </button>
      )}
      {showHidden && hiddenDemos.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
          {hiddenDemos.map((d, i) => <DemoCard key={d.demo_id ?? i} d={d} {...cardProps} />)}
        </div>
      )}

      {showModal && (
        <DemoDeleteModal demos={selectedDemos} contactsByEmail={contactsByEmail} contacts={contacts} busy={deleteBusy} onConfirm={handleDeleteConfirmed} onCancel={() => setShowModal(false)} />
      )}
      </div>
      )}
    </div>
  );
}

// Internal-domain emails (team's own domains, e.g. lumosity.ai, implied.com) are
// never real prospects — treat them as "not unmatched" everywhere attendees are
// resolved, so they never surface as "not in contacts". Domain list comes from
// ── Zoom Meeting Notes panel (used inside DemoCard) ──────────────────────────

function parseZoomExtracted(raw) {
  if (!raw) return { items: [], attendees: [] };
  try {
    const p = JSON.parse(raw);
    if (p && !Array.isArray(p)) return { items: p.items ?? [], attendees: p.attendees ?? [] };
    return { items: p.map(s => ({ assignee: '', task: s })), attendees: [] };
  } catch { return { items: [], attendees: [] }; }
}

function ZoomNotesPanel({ notes }) {
  const [open, setOpen] = useState(null);
  if (notes === null) return <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>;
  if (!notes.length) return <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-3)' }}>No linked meeting notes yet.</div>;
  return (
    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
      {notes.map(n => {
        const isOpen = open === n.id;
        const { items, attendees } = parseZoomExtracted(n.action_items);
        const ts = n.received_at?.value ?? n.received_at ?? '';
        const dateStr = ts ? new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
        return (
          <div key={n.id} style={{ border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface-1)', fontSize: 12 }}>
            <button onClick={() => setOpen(isOpen ? null : n.id)}
              style={{ width: '100%', textAlign: 'left', padding: '7px 10px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', gap: 8, alignItems: 'baseline' }}>
              <span style={{ fontWeight: 600, flex: 1 }}>{n.meeting_name || n.subject}</span>
              <span style={{ color: 'var(--text-3)', fontSize: 11 }}>{dateStr}</span>
              <span style={{ color: 'var(--text-3)', fontSize: 10 }}>{isOpen ? '▲' : '▼'}</span>
            </button>
            {isOpen && (
              <div style={{ padding: '0 10px 10px', borderTop: '1px solid var(--border)' }}>
                {attendees.length > 0 && (
                  <div style={{ marginTop: 7, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {attendees.map((a, i) => (
                      <span key={i} style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-3)' }}>{a}</span>
                    ))}
                  </div>
                )}
                {n.summary && (
                  <p style={{ marginTop: 7, lineHeight: 1.55, color: 'var(--text-1)' }}>{n.summary}</p>
                )}
                {items.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: 5 }}>Next Steps</div>
                    {items.map((it, i) => (
                      <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4, alignItems: 'flex-start' }}>
                        {it.assignee && <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, background: '#eff6ff', color: '#2563eb', fontWeight: 700, flexShrink: 0 }}>{it.assignee}</span>}
                        <span style={{ color: 'var(--text-1)' }}>{it.task || it}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Settings (settings.internalDomains), same setting the backend sync jobs use.
function parseInternalDomains(settings) {
  const raw = String(settings?.internalDomains ?? 'implied.com,lumosity.ai');
  return raw.split(/[,;]/).map((d) => d.trim().toLowerCase()).filter(Boolean);
}

function isInternalEmail(email, internalDomains) {
  const domain = String(email ?? '').toLowerCase().split('@')[1];
  return !!domain && internalDomains.includes(domain);
}

// Resolve all attendees for a demo from attendee_emails and contactsByEmail map.
// Returns: [{ email, contact: ContactObj | null, internal: bool }]
function resolveAttendees(demo, contactsByEmail, internalDomains = []) {
  const raw = String(demo.attendee_emails ?? '').trim();
  const emails = raw ? raw.split(',').map((e) => e.trim().toLowerCase()).filter(Boolean) : [];

  // If no attendee_emails stored, fall back to the primary contact
  if (emails.length === 0) {
    const email = demo._contact.email ?? '';
    return [{ email, contact: email ? demo._contact : null, internal: isInternalEmail(email, internalDomains) }];
  }

  return emails.map((email) => ({
    email,
    contact: contactsByEmail[email] ?? null,
    internal: isInternalEmail(email, internalDomains),
  }));
}

// Prefill data for adding a new contact found in a calendar event.
// Looks up other contacts with the same domain to infer firm info.
function calendarInvitePrefill(email, contacts) {
  const domain = email.split('@')[1] ?? '';
  const source = 'Calendar Sync';
  if (!domain) return { email, source };
  const sameOrg = contacts.filter((c) => String(c.email ?? '').toLowerCase().endsWith('@' + domain));
  if (sameOrg.length === 0) return { email, source };
  const ref = sameOrg[0];
  const firmId = ref.firm_id ?? '';
  // Return firm_id so ContactForm pre-selects the existing firm directly.
  // Fall back to firm_name only if the contact somehow has no firm_id (shouldn't happen).
  return firmId
    ? { email, source, firm_id: firmId }
    : { email, source, firm_name: ref._firm_name ?? ref.firm_name ?? '' };
}

// AttendeeList — shows only known (resolved) attendees in the expanded view.
// Unknown attendees are always rendered inline in DemoCard directly.
function AttendeeList({ attendees, onOpenTimeline }) {
  const known = attendees.filter((a) => a.contact);
  if (known.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {known.map(({ email, contact }) => (
        <div key={email}>
          <button
            className="link"
            style={{ fontWeight: 600, textAlign: 'left', fontSize: 13 }}
            onClick={() => onOpenTimeline?.(contact)}
          >
            {contact.name ?? email}
          </button>
          <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{email}</div>
        </div>
      ))}
    </div>
  );
}

function DemoCard({ d, editingId, setEditingId, busyId, setBusyId, onMarkComplete, contactsByEmail, onOpenTimeline, onOpenAddContact, contacts, firms, getToken, onRefresh, selectedIds, onToggle, highlightDemoId, onClearHighlight, internalDomains }) {
  const isEditing  = editingId === d.demo_id;
  const isBusy     = busyId === d.demo_id;
  const isSelected = selectedIds.has(d.demo_id);
  const status     = d._status ?? (d.status ?? '').toLowerCase();

  const attendees = resolveAttendees(d, contactsByEmail, internalDomains);
  const unknownAttendees = attendees.filter((a) => !a.contact && a.email && !a.internal);
  const unknownCount = unknownAttendees.length;
  const primaryContact = attendees.find((a) => a.contact && !a.internal)
    ?? attendees.find((a) => !a.internal && a.email)
    ?? attendees.find((a) => a.contact)
    ?? attendees[0] ?? {};
  const firstInitials = getInitials(primaryContact.contact?.name ?? primaryContact.email ?? d.event_title ?? 'D');
  const primaryName = primaryContact.contact?.name ?? primaryContact.email ?? d.event_title ?? 'Demo';

  const [showAllAttendees, setShowAllAttendees] = useState(false);
  const [matchItem, setMatchItem] = useState(null);
  const [zoomNotes, setZoomNotes] = useState(null);
  const [showZoom, setShowZoom] = useState(false);

  const handleMatchLinked = () => { setMatchItem(null); onRefresh?.(); };
  const handleMatchAddNew = (prefill) => { setMatchItem(null); onOpenAddContact?.(prefill); };

  // Lazy-load zoom notes when user first expands them
  useEffect(() => {
    if (!showZoom || zoomNotes !== null || !d.demo_id) return;
    fetchZoomNotesByDemo(getToken, d.demo_id)
      .then(r => setZoomNotes(r.notes ?? []))
      .catch(() => setZoomNotes([]));
  }, [showZoom, d.demo_id]);

  const chip = demoTimeChip(d.scheduled_date);
  const isLogMode = status === 'completed' || d._isPastDue;

  // left border colour
  const borderColor = d._needsOutcome ? 'var(--amber)' : status === 'scheduled' && !d._isPastDue ? 'var(--blue)' : status === 'completed' && d.outcome ? 'var(--green)' : 'var(--border-2)';

  const rowRef = (node) => {
    if (node && highlightDemoId === d.demo_id) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div
      ref={rowRef}
      className={`demo-card-v2${isSelected ? ' demo-card-v2--selected' : ''}${highlightDemoId === d.demo_id ? ' demo-row--highlight' : ''}`}
      style={{ borderLeft: `3px solid ${borderColor}` }}
      onAnimationEnd={highlightDemoId === d.demo_id ? onClearHighlight : undefined}
    >
      {/* ── Main row ─────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px' }}>
        <input type="checkbox" checked={isSelected} onChange={() => onToggle(d.demo_id)} style={{ cursor: 'pointer', flexShrink: 0 }} />

        <div className="demo-card__avatar" style={{ flexShrink: 0 }}>{firstInitials}</div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button className="link" style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--navy)' }} onClick={() => onOpenTimeline?.(primaryContact.contact)}>
              {primaryName}
            </button>
            {attendees.filter((a) => a.contact).length > 1 && (
              <button
                onClick={() => setShowAllAttendees((v) => !v)}
                style={{ fontSize: 11, color: 'var(--blue)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}
              >
                {showAllAttendees ? '▲ hide' : `+${attendees.filter((a) => a.contact).length - 1} more`}
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 2, alignItems: 'center' }}>
            {d._firm.firm_name && <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{d._firm.firm_name}</span>}
            {primaryContact.contact?.role && <span style={{ fontSize: 11, color: 'var(--text-3)' }}>· {primaryContact.contact.role}</span>}
            {d.event_title && <span style={{ fontSize: 11, color: 'var(--text-2)', fontStyle: 'italic' }}>· {d.event_title}</span>}
          </div>
        </div>

        {/* time chip */}
        <span className={`demo-time-chip ${chip.cls}`}>{chip.label}</span>

        {/* outcome badge (if set) */}
        {d.outcome && !d._needsOutcome && (
          <span style={{ fontSize: 11, color: 'var(--green)', background: 'var(--green-bg)', border: '1px solid rgba(5,150,105,.2)', borderRadius: 99, padding: '2px 8px', whiteSpace: 'nowrap', fontWeight: 600 }}>
            {d.outcome}
          </span>
        )}

        <button
          className={`btn btn--sm ${isEditing ? 'btn--ghost' : isLogMode ? 'btn--primary' : 'btn--ghost'}`}
          style={isLogMode && !isEditing ? { background: 'var(--orange)', borderColor: 'var(--orange)', color: '#fff' } : {}}
          onClick={() => isEditing ? setEditingId(null) : setEditingId(d.demo_id)}
          disabled={isBusy}
        >
          {isEditing ? 'Close' : isLogMode ? 'Log outcome' : 'Edit'}
        </button>
      </div>

      {/* ── MatchContactPanel (portal at DemoCard level) ─────── */}
      {matchItem && (
        <MatchContactPanel
          item={matchItem}
          firms={firms ?? []}
          getToken={getToken}
          onClose={() => setMatchItem(null)}
          onLinked={handleMatchLinked}
          onAddNew={handleMatchAddNew}
        />
      )}

      {/* ── Always-visible unknown attendees ─────────────────── */}
      {unknownCount > 0 && (
        <div style={{ margin: '0 12px 8px 52px', display: 'flex', flexDirection: 'column', gap: 5 }}>
          {unknownAttendees.map(({ email }) => (
            <div key={email} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', background: 'var(--amber-bg)', border: '1px solid rgba(217,119,6,.2)', borderRadius: 'var(--radius-sm)' }}>
              <span style={{ fontSize: 12, color: 'var(--amber)', flexShrink: 0 }}>⚠</span>
              <span style={{ fontSize: 12.5, color: 'var(--text-2)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email}</span>
              <span style={{ fontSize: 11, color: 'var(--text-3)', whiteSpace: 'nowrap', flexShrink: 0 }}>not in contacts</span>
              <button
                className="btn btn--sm btn--primary"
                style={{ whiteSpace: 'nowrap', flexShrink: 0, fontSize: 11 }}
                onClick={() => setMatchItem(calendarInvitePrefill(email, contacts))}
              >
                Match / Add
              </button>
            </div>
          ))}
        </div>
      )}

      {attendees.some((a) => !a.internal && (a.contact?.name || a.email)) && (
        <div style={{ margin: '0 12px 8px 52px', fontSize: 11.5, color: 'var(--text-3)' }}>
          Guests: {attendees.filter((a) => !a.internal).map(({ email, contact }) => contact?.name || email).filter(Boolean).join(' · ')}
        </div>
      )}

      {/* ── Expanded known-attendee list ──────────────────────── */}
      {showAllAttendees && attendees.some((a) => a.contact) && (
        <div style={{ margin: '0 12px 10px 52px', padding: '10px 12px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
          <AttendeeList attendees={attendees} onOpenTimeline={onOpenTimeline} />
        </div>
      )}

      {/* ── Needs-outcome banner ──────────────────────────────── */}
      {d._needsOutcome && !isEditing && (
        <div style={{ margin: '0 12px 10px 52px', padding: '7px 11px', background: 'var(--amber-bg)', border: '1px solid rgba(217,119,6,.25)', borderRadius: 'var(--radius-sm)', fontSize: 12, fontWeight: 600, color: 'var(--amber)' }}>
          ⚠ No outcome recorded — what happened in this demo?
        </div>
      )}

      {/* ── Overdue next-step warning ─────────────────────────── */}
      {d._nextOverdue && !isEditing && (
        <div style={{ padding: '0 12px 9px 52px', fontSize: 11.5, color: 'var(--red)', fontWeight: 600 }}>
          ⚠ Next step overdue: {d.next_step || 'action needed'} (was {fmt(d.next_step_due)})
        </div>
      )}

      {/* ── Next step row (non-overdue) ───────────────────────── */}
      {d.next_step && !d._nextOverdue && !isEditing && (
        <div style={{ padding: '0 12px 9px 52px', fontSize: 11.5, color: 'var(--text-3)' }}>
          → {d.next_step}{d.next_step_due ? ` · due ${fmt(d.next_step_due)}` : ''}
        </div>
      )}

      {/* ── Zoom Meeting Notes toggle ─────────────────────────── */}
      {!isEditing && (
        <div style={{ padding: '0 12px 10px 52px' }}>
          <button
            onClick={() => setShowZoom(v => !v)}
            style={{ fontSize: 11.5, color: 'var(--text-3)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            {showZoom ? '▲ Hide meeting notes' : '▼ Meeting notes'}
            {zoomNotes?.length ? ` (${zoomNotes.length})` : ''}
          </button>
          {showZoom && (
            <ZoomNotesPanel notes={zoomNotes} />
          )}
        </div>
      )}

      {/* ── Editor panel ─────────────────────────────────────── */}
      {isEditing && (
        <div style={{ borderTop: '1px solid var(--border)', background: 'var(--surface-2)' }}>
          <DemoEditor
            d={d}
            contactsByEmail={contactsByEmail}
            contacts={contacts}
            getToken={getToken}
            onRefresh={onRefresh}
            onOpenDraft={null}
            onClose={() => setEditingId(null)}
          />
        </div>
      )}
    </div>
  );
}

function addDaysISO(isoStr, days) {
  const d = new Date(isoStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function DemoEditor({ d, contactsByEmail, contacts, getToken, onRefresh, onOpenDraft, onClose }) {
  const isLogMode = (d._status ?? (d.status ?? '').toLowerCase()) === 'completed' || d._isPastDue;

  // ── Shared state ──────────────────────────────────────────────
  const [statusVal,  setStatusVal]  = useState(d.status || 'Scheduled');
  const [outcome,    setOutcome]    = useState(d.outcome || '');
  const [nextStep,   setNextStep]   = useState(d.next_step || '');
  const [nextDue,    setNextDue]    = useState(d.next_step_due || '');
  const [notes,      setNotes]      = useState(d.notes || '');
  const [trialStart, setTrialStart] = useState(todayISO());
  const [trialEnd,   setTrialEnd]   = useState('');
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState(null);

  const isStartTrial = nextStep === 'Start trial';

  const knownAttendees = useMemo(() => {
    if (!contactsByEmail) return d._contact?.contact_id ? [d._contact] : [];
    const resolved = resolveAttendees(d, contactsByEmail);
    const known = resolved.filter((a) => a.contact).map((a) => a.contact);
    if (known.length === 0 && d._contact?.contact_id) return [d._contact];
    return known;
  }, [d, contactsByEmail]);

  const [selectedIds, setSelectedIds] = useState(() => knownAttendees.map((c) => c.contact_id));

  function toggleContact(id) {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  function applyDuration(days) {
    const start = trialStart || todayISO();
    setTrialStart(start);
    setTrialEnd(addDaysISO(start, days));
  }

  const trialReady = !isStartTrial || (selectedIds.length > 0 && trialStart && trialEnd);

  async function handleSave(overrideStatus) {
    setSaving(true); setError(null);
    const finalStatus = overrideStatus ?? statusVal;
    try {
      await saveDemo(getToken, d.demo_id, { status: finalStatus, outcome, next_step: nextStep, next_step_due: nextDue, notes });
      if (isStartTrial && selectedIds.length > 0 && trialStart && trialEnd) {
        const primaryId  = selectedIds[0];
        const primaryCtx = knownAttendees.find((c) => c.contact_id === primaryId) ?? knownAttendees[0];
        const trialId    = 't_' + Math.random().toString(36).slice(2, 14);
        await apiCreateTrial(getToken, {
          trial_id: trialId, contact_id: primaryId, contact_ids: selectedIds.join(','),
          firm_id: primaryCtx?.firm_id || d._firm?.firm_id || '',
          start_date: trialStart, end_date: trialEnd, status: 'Active', source_demo_id: d.demo_id,
        });
      }
      onRefresh?.(); onClose();
    } catch (e) { setError(e.message); setSaving(false); }
  }

  const TrialSetupPanel = () => (
    <div style={{ marginBottom: 14, background: 'var(--blue-bg)', border: '1px solid rgba(37,99,235,.2)', borderRadius: 'var(--radius)', padding: '14px 16px' }}>
      <div style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--blue)', marginBottom: 12 }}>
        🧪 Trial setup — a new trial will be created on save
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Trial participants <span style={{ textTransform: 'none', fontWeight: 400, fontSize: 10 }}>(all attendees pre-selected)</span></label>
        {knownAttendees.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--red)', padding: '6px 0' }}>⚠ No contacts linked to this demo yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {knownAttendees.map((c) => (
              <label key={c.contact_id} style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', fontSize: 13.5 }}>
                <input type="checkbox" checked={selectedIds.includes(c.contact_id)} onChange={() => toggleContact(c.contact_id)} style={{ cursor: 'pointer' }} />
                <span style={{ fontWeight: 500 }}>{c.name || c.email}</span>
                {c.name && <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{c.email}</span>}
              </label>
            ))}
          </div>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div>
          <label style={labelStyle}>Start date</label>
          <input type="date" value={trialStart} onChange={(e) => setTrialStart(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>End date</label>
          <input type="date" value={trialEnd} onChange={(e) => setTrialEnd(e.target.value)} style={inputStyle} />
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 6 }}>
            {TRIAL_DURATIONS.map(({ label, days }) => (
              <button key={label} type="button" className="btn btn--ghost btn--sm" style={{ fontSize: 11.5 }} onClick={() => applyDuration(days)}>+{label}</button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  if (error) {
    // show error at top regardless of mode
  }

  // ── LOG OUTCOME mode ──────────────────────────────────────────
  if (isLogMode) {
    return (
      <div style={{ padding: '16px 20px' }}>
        {error && <div style={{ background: 'var(--red-bg)', color: '#b91c1c', padding: '10px 14px', borderRadius: 6, fontSize: 13, marginBottom: 14 }}>{error}</div>}

        {/* Auto-status indicator */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <span style={{ background: 'var(--green-bg)', border: '1px solid rgba(5,150,105,.25)', borderRadius: 'var(--radius-sm)', padding: '4px 10px', fontSize: 12, fontWeight: 600, color: 'var(--green)' }}>
            ✓ Marking as Completed
          </span>
          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{d._firm?.firm_name ? `${d._firm.firm_name} · ` : ''}{fmt(d.scheduled_date)}</span>
        </div>

        {/* 1. Notes FIRST */}
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>What happened? <span style={{ textTransform: 'none', fontWeight: 400, fontSize: 10, letterSpacing: 0 }}>(meeting notes)</span></label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            autoFocus
            placeholder="Key takeaways, objections, who said what…"
            style={{ ...inputStyle, resize: 'vertical', borderColor: 'var(--navy)', background: '#f8faff' }}
          />
        </div>

        {/* 2. Outcome pills */}
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Outcome</label>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
            {OUTCOME_PILLS.map((pill) => {
              const sel = outcome === pill.value;
              return (
                <button
                  key={pill.value}
                  type="button"
                  onClick={() => setOutcome(sel ? '' : pill.value)}
                  style={{
                    flex: 1, minWidth: 100, padding: '7px 10px', borderRadius: 'var(--radius-sm)',
                    border: `1.5px solid ${sel ? pill.border : 'var(--border)'}`,
                    background: sel ? pill.bg : 'var(--surface)', color: sel ? pill.color : 'var(--text-3)',
                    fontWeight: 600, fontSize: 12, cursor: 'pointer', textAlign: 'center',
                  }}
                >
                  {pill.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* 3. Next step */}
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Next step</label>
          <div style={{ display: 'flex', gap: 7, alignItems: 'center', marginBottom: 7 }}>
            <input type="text" value={nextStep} onChange={(e) => setNextStep(e.target.value)} placeholder="What's the next action?" style={{ ...inputStyle, flex: 1 }} />
            <input type="date" value={nextDue} onChange={(e) => setNextDue(e.target.value)} style={{ ...inputStyle, width: 150 }} />
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {NEXT_STEP_QUICKPICKS_NO_DEAD.map((q) => (
              <button key={q} type="button" className="btn btn--ghost btn--sm" style={{ fontWeight: nextStep === q ? 700 : 400, borderColor: nextStep === q ? 'var(--navy)' : undefined }} onClick={() => setNextStep(nextStep === q ? '' : q)}>{q}</button>
            ))}
          </div>
          {/* Dead — separated */}
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>or close this deal:</span>
            <button
              type="button"
              className="btn btn--sm"
              style={{ borderColor: '#fca5a5', color: 'var(--red)', background: 'var(--red-bg)', fontSize: 11.5 }}
              onClick={() => { setNextStep('Dead'); handleSave('Completed'); }}
              disabled={saving}
            >
              ✗ Mark as dead / lost
            </button>
          </div>
        </div>

        {/* Trial setup panel */}
        {isStartTrial && <TrialSetupPanel />}

        {/* Actions */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            style={{ color: 'var(--blue)', borderColor: 'rgba(37,99,235,.3)' }}
            onClick={() => onOpenDraft?.(d._contact)}
          >
            ✉ Draft follow-up email
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="btn btn--primary" style={{ background: 'var(--orange)', borderColor: 'var(--orange)' }} onClick={() => handleSave('Completed')} disabled={saving || !trialReady}>
              {saving ? 'Saving…' : isStartTrial ? 'Save & Start Trial' : 'Save outcome'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── EDIT mode (scheduled) ─────────────────────────────────────
  return (
    <div style={{ padding: '16px 20px' }}>
      {error && <div style={{ background: 'var(--red-bg)', color: '#b91c1c', padding: '10px 14px', borderRadius: 6, fontSize: 13, marginBottom: 14 }}>{error}</div>}

      <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 14, padding: '7px 10px', background: 'var(--blue-bg)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(37,99,235,.15)' }}>
        📅 <strong style={{ color: 'var(--navy)' }}>Upcoming demo</strong> — {fmtDateTime(d.scheduled_date)}{d._firm?.firm_name ? ` · ${d._firm.firm_name}` : ''}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px', gap: 10, marginBottom: 14 }}>
        <div>
          <label style={labelStyle}>Date</label>
          <input type="date" value={(d.scheduled_date ?? '').slice(0, 10)} onChange={() => {}} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Time</label>
          <input type="time" value={d.scheduled_date?.includes('T') ? d.scheduled_date.slice(11, 16) : ''} onChange={() => {}} style={inputStyle} />
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Pre-meeting notes <span style={{ textTransform: 'none', fontWeight: 400, fontSize: 10, letterSpacing: 0 }}>(optional)</span></label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Prep notes, talking points, questions to cover…" style={{ ...inputStyle, resize: 'vertical' }} />
      </div>

      <div style={{ paddingTop: 12, borderTop: '1px solid var(--border)', marginBottom: 14 }}>
        <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginBottom: 8 }}>Did something change?</div>
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
          {['No-show', 'Cancelled'].map((s) => (
            <button key={s} type="button" className="btn btn--ghost btn--sm" onClick={() => handleSave(s)} disabled={saving}>
              Mark as {s}
            </button>
          ))}
          <button
            type="button"
            className="btn btn--sm"
            style={{ background: 'var(--orange)', color: '#fff', borderColor: 'var(--orange)', fontWeight: 700 }}
            onClick={() => { setStatusVal('Completed'); /* switch to log mode */ onClose(); }}
            disabled={saving}
          >
            Demo happened → Log outcome
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button className="btn btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn btn--primary" style={{ background: 'var(--orange)', borderColor: 'var(--orange)' }} onClick={() => handleSave()} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

// ── CoverageListChip ──────────────────────────────────────────────────────────

function CoverageListChip({ watchlist }) {
  if (!watchlist) return null;
  const { totalSaves, lastSave } = watchlist;
  const daysAgo = lastSave ? Math.round((Date.now() - new Date(lastSave).getTime()) / 86_400_000) : null;
  const tip = `Coverage list changed ${totalSaves}x · last: ${lastSave ?? '—'}`;
  return (
    <span
      title={tip}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 3,
        fontSize: 10, fontWeight: 600,
        color: '#0369a1', background: '#e0f2fe',
        borderRadius: 4, padding: '1px 5px',
        marginTop: 2, whiteSpace: 'nowrap', cursor: 'default',
      }}
    >
      📋 Coverage updated{daysAgo !== null ? ` · ${daysAgo}d ago` : ''}
    </span>
  );
}

// ── Trials ────────────────────────────────────────────────────────────────────

function fmtAUM(raw) {
  if (!raw) return null;
  const n = parseFloat(String(raw).replace(/[^0-9.]/g, ''));
  if (isNaN(n) || n <= 0) return null;
  // stored in millions USD
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}B`;
  return `$${Math.round(n)}M`;
}

function WeekDots({ totalWeeks, weekLogs, currentWeek, onClickWeek, isExpired = false }) {
  let startW = 1, endW = totalWeeks;
  let showLeftEllipsis = false, showRightEllipsis = false;

  if (totalWeeks > MAX_WEEK_DOTS) {
    const half = Math.floor(MAX_WEEK_DOTS / 2);
    startW = Math.max(1, currentWeek - half);
    endW   = startW + MAX_WEEK_DOTS - 1;
    if (endW > totalWeeks) { endW = totalWeeks; startW = endW - MAX_WEEK_DOTS + 1; }
    showLeftEllipsis  = startW > 1;
    showRightEllipsis = endW < totalWeeks;
  }

  const dots = [];
  for (let w = startW; w <= endW; w++) dots.push(w);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
      {showLeftEllipsis && <span style={{ fontSize: 9, color: 'var(--text-3)', lineHeight: 1 }}>…</span>}
      {dots.map((w) => {
        const hasLog = (weekLogs[w]?.length ?? 0) > 0;
        const isPast = w < currentWeek;
        const isCurrent = w === currentWeek;
        // Expired trials: logged weeks stay green, unlogged weeks go muted gray (not red — trial is done)
        const dotColor = hasLog
          ? 'var(--green)'
          : isExpired
            ? 'var(--border)'
            : isPast ? 'var(--red)' : isCurrent ? 'var(--amber)' : 'var(--border)';
        return (
          <button
            key={w}
            title={`W${w}${hasLog ? ': logged' : isPast ? ': no log' : isCurrent ? ': log this week' : ''}`}
            onClick={() => onClickWeek(w)}
            style={{
              width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
              background: hasLog ? dotColor : 'transparent',
              border: `2px solid ${dotColor}`,
              cursor: 'pointer', padding: 0, boxSizing: 'border-box',
            }}
          />
        );
      })}
      {showRightEllipsis && <span style={{ fontSize: 9, color: 'var(--text-3)', lineHeight: 1 }}>…</span>}
    </div>
  );
}

const CHURN_REASONS = ['Low usage', 'Product gap', 'No budget', 'Timing', 'No response', 'Chose competitor', 'Security-compliance', 'Internal change', 'Other'];

// ── ConvertTrialModal — Phase 2: Convert to Customer ─────────────────────────
function ConvertTrialModal({ trial, getToken, onRefresh, onClose }) {
  const primaryContacts = trial._contacts?.length > 0 ? trial._contacts : (trial._contact?.contact_id ? [trial._contact] : []);
  const [convertedDate,   setConvertedDate]   = useState(todayISO());
  const [summary,         setSummary]         = useState('');
  const [owner,           setOwner]           = useState(trial._owner ?? trial.owner ?? '');
  const [renewalDate,     setRenewalDate]     = useState('');
  const [renewalUnknown,  setRenewalUnknown]  = useState(false);
  const [contractValue,   setContractValue]   = useState('');
  const [productPlan,     setProductPlan]     = useState('');
  const [followUpDate,    setFollowUpDate]    = useState('');
  const [followUpNote,    setFollowUpNote]    = useState('');
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);
  const [result, setResult] = useState(null);

  const renewalValid = renewalUnknown || Boolean(renewalDate);
  const canSave = summary.trim().length > 0 && renewalValid && !saving;

  async function handleSave() {
    if (!canSave) return;
    setSaving(true); setError(null);
    try {
      const res = await apiConvertTrial(getToken, trial.trial_id, {
        converted_date: convertedDate,
        conversion_summary: summary.trim(),
        customer_owner: owner || '',
        renewal_date: renewalUnknown ? null : (renewalDate || null),
        renewal_date_not_known: renewalUnknown,
        contract_value: contractValue || null,
        product_plan: productPlan || null,
        follow_up_date: followUpDate || null,
        follow_up_note: followUpNote || null,
      });
      setResult(res);
      onRefresh?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (result) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-md)', padding: '24px 28px', width: 420, maxWidth: '92vw', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8, color: 'var(--green)' }}>🎉 Converted to Customer</div>
          <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 20 }}>
            {primaryContacts[0]?.name || primaryContacts[0]?.email || 'This contact'} is now marked as a Customer.
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <a className="btn btn--ghost btn--sm" href="/customers" onClick={(e) => e.stopPropagation()}>Create contract</a>
            <a className="btn btn--ghost btn--sm" href="/customers">Open customer</a>
            <button className="btn btn--primary btn--sm" onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    // whiteSpace:normal — rendered inside the table's nowrap Actions <td>; see ChurnTrialModal.
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, whiteSpace: 'normal' }}
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-md)', padding: '24px 28px', width: 480, maxWidth: '92vw', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Convert to Customer</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginBottom: 18 }}>
          {primaryContacts.map((c) => c.name ?? c.email).join(', ') || '—'} · {trial._firm?.firm_name ?? ''}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>Converted date</label>
            <input type="date" value={convertedDate} onChange={(e) => setConvertedDate(e.target.value)} style={inputStyle} />
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>Conversion summary *</label>
            <textarea rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="Why did this convert? What sealed it?" style={{ ...inputStyle, resize: 'vertical' }} />
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>Customer owner</label>
            <input type="text" value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="Owner name" style={inputStyle} />
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>Renewal date</label>
            <input type="date" value={renewalDate} disabled={renewalUnknown} onChange={(e) => setRenewalDate(e.target.value)} style={{ ...inputStyle, opacity: renewalUnknown ? 0.5 : 1 }} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 12, color: 'var(--text-2)', cursor: 'pointer' }}>
              <input type="checkbox" checked={renewalUnknown} onChange={(e) => { setRenewalUnknown(e.target.checked); if (e.target.checked) setRenewalDate(''); }} />
              Not known yet
            </label>
            {!renewalValid && <div style={{ fontSize: 11.5, color: 'var(--red)', marginTop: 4 }}>Pick a renewal date or check "Not known yet".</div>}
          </div>

          <details>
            <summary style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', cursor: 'pointer' }}>Optional details</summary>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
              <div>
                <label style={{ fontSize: 11.5, color: 'var(--text-3)', display: 'block', marginBottom: 3 }}>Contract value / expected ARR</label>
                <input type="text" value={contractValue} onChange={(e) => setContractValue(e.target.value)} placeholder="$" style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 11.5, color: 'var(--text-3)', display: 'block', marginBottom: 3 }}>Product plan</label>
                <input type="text" value={productPlan} onChange={(e) => setProductPlan(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 11.5, color: 'var(--text-3)', display: 'block', marginBottom: 3 }}>Follow-up date</label>
                <input type="date" value={followUpDate} onChange={(e) => setFollowUpDate(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 11.5, color: 'var(--text-3)', display: 'block', marginBottom: 3 }}>Notes / next step</label>
                <textarea rows={2} value={followUpNote} onChange={(e) => setFollowUpNote(e.target.value)} style={{ ...inputStyle, resize: 'vertical' }} />
              </div>
            </div>
          </details>
        </div>

        {error && <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 14 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
          <button className="btn btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn--primary" style={{ background: 'var(--orange, #ea580c)', borderColor: 'var(--orange, #ea580c)' }} onClick={handleSave} disabled={!canSave}>
            {saving ? 'Saving…' : 'Convert to Customer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── ChurnTrialModal — Phase 2: Mark churned ──────────────────────────────────

// IMP-1808: the feedback ask is saved as an internal trial note (not a Gmail
// draft) — copy/paste it into an email when reaching out. Template is a first
// pass; expected to evolve.
function churnFeedbackTemplate(firstName, lockDateStr) {
  const name = firstName ? ` ${firstName}` : '';
  const lockLine = lockDateStr
    ? `Your account will be locked on ${lockDateStr}, but nothing gets deleted — if the timing just wasn't right, you can always get back in touch and we'll pick up right where you left off.`
    : `Nothing gets deleted — if the timing just wasn't right, you can always get back in touch and we'll pick up right where you left off.`;
  return `Hi${name},

Your Implied trial has wrapped up, so I wanted to check in. What held you back from converting? Any feedback — missing features, pricing, timing — genuinely helps us.

${lockLine}

Thanks for giving Implied a try,`;
}

function ChurnTrialModal({ trial, getToken, onRefresh, onClose }) {
  const [churnDate,    setChurnDate]    = useState(todayISO());
  const [churnReason,  setChurnReason]  = useState('');
  const [churnSummary, setChurnSummary] = useState('');
  const [nextStage,    setNextStage]    = useState('nurture'); // nurture | dead | follow_up
  const [followUpDate, setFollowUpDate] = useState('');
  // Date the Clerk account gets locked by the daily lock sweep
  // (/internal/cron/lock-churned). Default: today + 7d — NOT trial end + 7d,
  // which for long-expired trials lands in the past and would lock on the
  // very next sweep.
  const [lockedDate, setLockedDate] = useState(() =>
    new Date(new Date(`${todayISO()}T12:00:00`).getTime() + 7 * 86_400_000).toISOString().slice(0, 10)
  );
  const firstName = String(trial._contacts?.[0]?.name ?? trial._contact?.name ?? trial.clerk_name ?? '').trim().split(/\s+/)[0] ?? '';
  const [feedbackNote, setFeedbackNote] = useState(() => churnFeedbackTemplate(firstName, lockedDate));
  const feedbackEdited = useRef(false);
  const [saveFeedback, setSaveFeedback] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);
  const [noteWarning, setNoteWarning] = useState(null);
  const [done,   setDone]   = useState(false);

  // Keep the lock date inside the template in sync until the user edits the text.
  function handleLockedDateChange(v) {
    setLockedDate(v);
    if (!feedbackEdited.current) setFeedbackNote(churnFeedbackTemplate(firstName, v));
  }

  const canSave = churnReason && churnSummary.trim().length > 0
    && (nextStage !== 'follow_up' || Boolean(followUpDate)) && !saving;

  async function handleSave() {
    if (!canSave) return;
    setSaving(true); setError(null);
    try {
      await apiChurnTrial(getToken, trial.trial_id, {
        churn_date: churnDate,
        churn_reason: churnReason,
        churn_summary: churnSummary.trim(),
        next_stage: nextStage,
        follow_up_date: nextStage === 'follow_up' ? followUpDate : null,
        locked_date: lockedDate || null,
      });
      // Non-fatal: the churn itself succeeded; a failed note save just warns.
      if (saveFeedback && feedbackNote.trim()) {
        try {
          await apiAddTrialNote(getToken, trial.trial_id, {
            note: `[Churn feedback draft]\n${feedbackNote.trim()}`,
            add_to_timeline: false,
          });
        } catch (e) {
          setNoteWarning(`Churned, but saving the feedback note failed: ${e.message}`);
        }
      }
      setDone(true);
      onRefresh?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    // whiteSpace:normal — the modal renders inside the table's Actions <td>,
    // which is nowrap; without the reset every helper text inherits it and
    // clips instead of wrapping.
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, whiteSpace: 'normal' }}
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-md)', padding: '24px 28px', width: 560, maxWidth: '92vw', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>Mark churned</div>

        {done ? (
          <>
            <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 8 }}>
              Trial marked as churned.{lockedDate ? ` Account locks automatically on ${lockedDate}.` : ''}
            </div>
            {noteWarning && <div style={{ fontSize: 12, color: 'var(--amber)', marginBottom: 12 }}>{noteWarning}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <button className="btn btn--primary btn--sm" onClick={onClose}>Done</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>Churn date</label>
                <input type="date" value={churnDate} onChange={(e) => setChurnDate(e.target.value)} style={inputStyle} />
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>Churn reason *</label>
                <select value={churnReason} onChange={(e) => setChurnReason(e.target.value)} style={inputStyle}>
                  <option value="">Select a reason…</option>
                  {CHURN_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>Churn summary *</label>
                <textarea rows={3} value={churnSummary} onChange={(e) => setChurnSummary(e.target.value)} placeholder="What happened?" style={{ ...inputStyle, resize: 'vertical' }} />
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>Account lock date</label>
                <input type="date" value={lockedDate} min={todayISO()} onChange={(e) => handleLockedDateChange(e.target.value)} style={inputStyle} />
                <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 4 }}>
                  The Implied account is locked automatically on this date (default: 7 days from today).
                  Converting before then — or clicking Unlock on the churned row — cancels it. No data is deleted.
                </div>
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>
                  Feedback ask (internal note)
                </label>
                <textarea
                  rows={7}
                  value={feedbackNote}
                  onChange={(e) => { feedbackEdited.current = true; setFeedbackNote(e.target.value); }}
                  style={{ ...inputStyle, resize: 'vertical', fontSize: 12 }}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--text-3)', marginTop: 4, cursor: 'pointer' }}>
                  <input type="checkbox" checked={saveFeedback} onChange={(e) => setSaveFeedback(e.target.checked)} />
                  Save to the trial's notes — nothing is emailed; copy it into your outreach when ready.
                </label>
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>Next stage *</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                    <input type="radio" name="nextStage" checked={nextStage === 'nurture'} onChange={() => setNextStage('nurture')} />
                    Keep warm / nurture
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                    <input type="radio" name="nextStage" checked={nextStage === 'dead'} onChange={() => setNextStage('dead')} />
                    Dead / do not pursue
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                    <input type="radio" name="nextStage" checked={nextStage === 'follow_up'} onChange={() => setNextStage('follow_up')} />
                    Follow up later
                  </label>
                  {nextStage === 'follow_up' && (
                    <input type="date" value={followUpDate} onChange={(e) => setFollowUpDate(e.target.value)} style={{ ...inputStyle, marginLeft: 24, width: 'calc(100% - 24px)' }} />
                  )}
                </div>
              </div>
            </div>

            {error && <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 14 }}>{error}</div>}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <button className="btn btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
              <button className="btn btn--primary" style={{ background: 'var(--orange, #ea580c)', borderColor: 'var(--orange, #ea580c)' }} onClick={handleSave} disabled={!canSave}>
                {saving ? 'Saving…' : 'Mark churned'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TrialDeleteModal({ trials, busy, onConfirm, onCancel }) {
  // Rows with an Implied app account (roster-synced, or resolvable by email):
  // deleting those without locking is a no-op — the roster merge re-creates
  // the row — so the lock option defaults ON when any account is affected.
  const clerkCount = trials.filter((t) => String(t.clerk_user_id ?? t.amplitude_user_id ?? '').trim()).length;
  const [lockClerk, setLockClerk] = useState(clerkCount > 0);
  // IMP-1833 full offboard: also soft-delete the linked contact(s) so the
  // person disappears from Contacts/Customers too and BD imports skip them.
  // Defaults ON — "delete" here normally means the person, not just the row.
  const [deleteUser, setDeleteUser] = useState(true);
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999,
    }}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}
    >
      <div style={{
        background: 'var(--surface)', borderRadius: 'var(--radius-md)',
        padding: '24px 28px', width: 440, maxWidth: '92vw',
        maxHeight: '80vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>
          Delete {trials.length} trial{trials.length > 1 ? 's' : ''}?
        </div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.3px', marginBottom: 8 }}>
            Trials to delete
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {trials.map((t) => {
              // Unlinked roster rows have no contact — fall back to the Clerk
              // name, then the login email, so the row is still identifiable.
              const names = (t._contacts?.length > 0 ? t._contacts : (t._contact ? [t._contact] : []))
                .map((c) => c.name || c.email).filter(Boolean).join(', ')
                || t.clerk_name || t.amplitude_user_id || '';
              return (
                <div key={t.trial_id} style={{ fontSize: 13.5, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontWeight: 500 }}>{names || '—'}</span>
                  {t._firm?.firm_name && (
                    <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{t._firm.firm_name}</span>
                  )}
                  <span className="badge badge-demo" style={{ fontSize: 11 }}>{t.status}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div style={{ fontSize: 13, color: '#b91c1c', background: 'var(--red-bg)', borderRadius: 6, padding: '10px 14px', marginBottom: 14 }}>
          {deleteUser
            ? <>⚠ This deletes the selected user{trials.length > 1 ? 's' : ''} everywhere — trials, contacts, and account access.</>
            : <>⚠ This will permanently delete the selected trial record{trials.length > 1 ? 's' : ''}. This cannot be undone.</>}
        </div>
        {clerkCount > 0 && (
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, marginBottom: 20, cursor: 'pointer' }}>
            <input type="checkbox" checked={lockClerk} onChange={(e) => setLockClerk(e.target.checked)} style={{ marginTop: 2, cursor: 'pointer' }} />
            <span>
              <b>Lock the Implied app account{clerkCount > 1 ? 's' : ''} & remove from the trial roster</b> ({clerkCount} account{clerkCount > 1 ? 's' : ''})
              <span style={{ display: 'block', color: 'var(--text-3)', fontSize: 11.5, marginTop: 2 }}>
                They can no longer sign in, and the Clerk sync won't re-create these rows.
                Unchecked, the account stays active and flagged — the roster sync brings the row back.
                Re-flagging someone as a trial later unlocks them.
              </span>
            </span>
          </label>
        )}
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, marginBottom: 20, cursor: 'pointer' }}>
          <input type="checkbox" checked={deleteUser} onChange={(e) => setDeleteUser(e.target.checked)} style={{ marginTop: 2, cursor: 'pointer' }} />
          <span>
            <b>Delete the user{trials.length > 1 ? 's' : ''} entirely</b>
            <span style={{ display: 'block', color: 'var(--text-3)', fontSize: 11.5, marginTop: 2 }}>
              Also removes the linked contact{trials.length > 1 ? 's' : ''} from every page (Contacts, Customers),
              locks the account, and BD imports will not re-add them.
            </span>
          </span>
        </label>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn btn--ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            className="btn btn-danger btn--sm"
            style={{ cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1 }}
            onClick={() => onConfirm(lockClerk, deleteUser)}
            disabled={busy}
          >
            {busy ? 'Deleting…' : deleteUser
              ? `Delete ${trials.length} user${trials.length > 1 ? 's' : ''}`
              : `Delete ${trials.length} trial${trials.length > 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── AmplitudeSyncButton ───────────────────────────────────────────────────────

function AmplitudeSyncButton({ busy, result, error, onSync }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <button
        className="btn btn--ghost btn--sm"
        onClick={onSync}
        disabled={busy}
        style={{ display: 'flex', alignItems: 'center', gap: 5 }}
        title="Fetch latest Amplitude usage for all active trials"
      >
        {busy ? <><span className="spinner" style={{ width: 11, height: 11 }} /> Syncing…</> : '⟳ Refresh usage data'}
      </button>
      {result && !busy && (
        <span style={{ fontSize: 11.5, color: 'var(--green)', background: 'var(--green-bg)', border: '1px solid rgba(22,163,74,.2)', borderRadius: 999, padding: '2px 9px' }}>
          ✓ {result.updated} updated{result.errors > 0 ? ` · ${result.errors} errors` : ''}
        </span>
      )}
      {error && !busy && <span style={{ fontSize: 11.5, color: 'var(--red)' }}>Failed: {error}</span>}
    </div>
  );
}

// ── Health reason / next-action derivation (Phase 1 redesign) ────────────────

function healthReasonLine(trial) {
  const health = trial._health ?? {};
  const weekThis = parseInt(trial.logins_this_week ?? '0') || 0;
  const weekLast = parseInt(trial.logins_last_week ?? '0') || 0;
  const parts = [];
  if (weekLast > 0 && weekThis < weekLast) {
    const pctDown = Math.round((1 - weekThis / weekLast) * 100);
    if (pctDown > 0) parts.push(`Usage down ${pctDown}%`);
  } else if (weekThis > weekLast) {
    parts.push(weekLast > 0 ? `Usage up, ${weekThis} sessions this wk` : `${weekThis} sessions this wk`);
  }
  if (health.loginDays !== null && health.loginDays !== undefined) {
    parts.push(health.loginDays === 0 ? 'logged in today' : `no login ${health.loginDays}d`);
  }
  const hasCurrentCheckin = (trial._weekLogs?.[trial._currentWeek ?? 0]?.length ?? 0) > 0;
  if (!hasCurrentCheckin && (trial._group === 'this_week' || trial._group === 'act_today')) {
    parts.push('check-in due');
  }
  if (parts.length === 0) {
    return trial._lastTouchDaysAgo === null ? 'Never contacted' : `Last touch ${trial._lastTouchDaysAgo}d ago`;
  }
  return parts.slice(0, 2).join(' · ');
}

function nextActionLabel(trial) {
  if (trial._group === 'expired') return 'Decide status';
  const totalWeeks = Math.max(1, Math.ceil(trial._totalDays / 7));
  const startMs = new Date(trial.start_date || 0).getTime();
  const currentWeek = Math.min(totalWeeks, Math.max(1, Math.ceil((Date.now() - startMs) / (7 * 86_400_000))));
  const hasCurrentCheckin = (trial._weekLogs?.[currentWeek]?.length ?? 0) > 0;
  if ((trial._group === 'this_week' || trial._group === 'act_today') && !hasCurrentCheckin) return 'Check in';
  if (trial._health?.score === 'green' && trial._daysLeft <= 14) return 'Conversion follow-up';
  return '—';
}

// ── TrialDatePopover — Start/End date editor (Phase 1 redesign) ──────────────

function TrialDatePopover({ trial, targets, getToken, onRefresh, onClose, pos }) {
  const targetTrials = targets?.length ? targets : [trial];
  const [start, setStart] = useState(trial.start_date ? trial.start_date.slice(0, 10) : '');
  const [end,   setEnd]   = useState(trial.end_date   ? trial.end_date.slice(0, 10)   : '');
  const [pendingStartChoice, setPendingStartChoice] = useState(null); // { newStart } while asking keep-duration vs keep-end
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    function onClick(e) { if (ref.current && !ref.current.contains(e.target)) onClose(); }
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    // position:fixed (escapes the table's scroll clip) — close on outside scroll.
    function onScroll(e) { if (ref.current && ref.current.contains(e.target)) return; onClose(); }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [onClose]);

  const durationDays = start && end ? Math.round((new Date(end) - new Date(start)) / 86_400_000) : null;

  function handleStartChange(newStart) {
    if (end && start) {
      // Ask: keep duration (shift end) or keep end date fixed?
      setPendingStartChoice({ newStart });
    } else {
      setStart(newStart);
    }
  }

  function resolveStartChoice(keepDuration) {
    const { newStart } = pendingStartChoice;
    if (keepDuration && durationDays !== null) {
      const d = new Date(newStart);
      d.setDate(d.getDate() + durationDays);
      setEnd(d.toISOString().slice(0, 10));
    }
    setStart(newStart);
    setPendingStartChoice(null);
  }

  function quickDuration(days) {
    const base = start || todayISO();
    const d = new Date(base);
    d.setDate(d.getDate() + days);
    setStart(base);
    setEnd(d.toISOString().slice(0, 10));
  }

  function quickEndAdjust(days) {
    const base = end || start || todayISO();
    const d = new Date(base);
    d.setDate(d.getDate() + days);
    setEnd(d.toISOString().slice(0, 10));
  }

  async function handleSave() {
    if (start && end && new Date(end) < new Date(start)) { setError('End date must be after start date'); return; }
    setSaving(true); setError(null);
    try {
      await Promise.all(targetTrials.map((t) => saveTrial(getToken, t.trial_id, { start_date: start, end_date: end })));
      onRefresh?.();
      onClose();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div ref={ref} onClick={(e) => e.stopPropagation()} style={{
      position: 'fixed', top: pos?.top, left: pos?.left, zIndex: 9000, width: 240,
      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
      boxShadow: '0 8px 24px rgba(0,0,0,.14)', padding: 12, fontSize: 12.5,
      whiteSpace: 'normal', // anchored inside the table's nowrap Ends <td>
    }}>
      {pendingStartChoice ? (
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 10 }}>
            Changing the start date — keep the trial's current duration ({durationDays}d) or keep the current end date?
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn--ghost btn--sm" style={{ flex: 1 }} onClick={() => resolveStartChoice(false)}>Keep end date</button>
            <button className="btn btn--ghost btn--sm" style={{ flex: 1 }} onClick={() => resolveStartChoice(true)}>Keep duration</button>
          </div>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <label style={{ ...labelStyle, marginBottom: 0 }}>Start</label>
            <input type="date" value={start} onChange={(e) => handleStartChange(e.target.value)} style={{ fontSize: 12, border: '1px solid var(--border)', borderRadius: 6, padding: '3px 5px', background: 'var(--surface)', color: 'var(--text)' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <label style={{ ...labelStyle, marginBottom: 0 }}>End</label>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} style={{ fontSize: 12, border: '1px solid var(--border)', borderRadius: 6, padding: '3px 5px', background: 'var(--surface)', color: 'var(--text)' }} />
          </div>
          {durationDays !== null && (
            <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginBottom: 8 }}>Duration: {durationDays} days</div>
          )}
          <div style={{ display: 'flex', gap: 5, marginBottom: 8, flexWrap: 'wrap' }}>
            {[7, 14, 30].map((d) => (
              <button key={d} type="button" className="btn btn--ghost btn--sm" style={{ fontSize: 11, padding: '2px 7px' }} onClick={() => quickDuration(d)}>{d}d</button>
            ))}
          </div>
          <div style={{ fontSize: 10.5, color: 'var(--text-3)', marginBottom: 4 }}>Quick end-date adjust</div>
          <div style={{ display: 'flex', gap: 5, marginBottom: 10, flexWrap: 'wrap' }}>
            {[7, 14, 30].map((d) => (
              <button key={d} type="button" className="btn btn--ghost btn--sm" style={{ fontSize: 11, padding: '2px 7px' }} onClick={() => quickEndAdjust(d)}>+{d}</button>
            ))}
            <button type="button" className="btn btn--ghost btn--sm" style={{ fontSize: 11, padding: '2px 7px' }} onClick={() => setEnd(todayISO())}>Today</button>
          </div>
          {targetTrials.length > 1 && (
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--orange)', marginBottom: 6 }}>
              Applies to {targetTrials.length} selected trials
            </div>
          )}
          {error && <div style={{ fontSize: 11, color: 'var(--red)', marginBottom: 6 }}>{error}</div>}
          <button className="btn btn--primary btn--sm" style={{ width: '100%' }} onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : `Save${targetTrials.length > 1 ? ` (${targetTrials.length})` : ''}`}</button>
        </>
      )}
    </div>
  );
}

// ── TrialExtendPopover — numeric stepper + unit toggle (Phase 1 redesign) ────

// ── TrialLockPopover — schedule / edit the churn account lock (IMP-1808) ─────
// Opens from the churned row's lock controls. Same fixed-position + scroll-
// close behavior as the other row popups.
function TrialLockPopover({ trial, initialDate, pos, getToken, onRefresh, patchTrial, onClose }) {
  const [date,   setDate]   = useState(initialDate);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    function onClick(e) { if (ref.current && !ref.current.contains(e.target)) onClose(); }
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    function onScroll(e) { if (ref.current && ref.current.contains(e.target)) return; onClose(); }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [onClose]);

  async function save() {
    if (!date) return;
    setSaving(true); setError(null);
    try {
      await apiLockTrial(getToken, trial.trial_id, date);
      patchTrial?.(trial.trial_id, { locked_date: date, locked_at: null });
      onRefresh?.();
      onClose();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div ref={ref} onClick={(e) => e.stopPropagation()} style={{
      position: 'fixed', top: pos?.top, right: pos?.right, zIndex: 9000, width: 230,
      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
      boxShadow: '0 8px 24px rgba(0,0,0,.14)', padding: 12, fontSize: 12.5, whiteSpace: 'normal',
    }}>
      <label style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>Account lock date</label>
      <input type="date" value={date} min={todayISO()} onChange={(e) => setDate(e.target.value)} style={inputStyle} />
      <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6 }}>
        The daily sweep locks the Implied account on this date. No data is deleted.
      </div>
      {error && <div style={{ fontSize: 11.5, color: 'var(--red)', marginTop: 6 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
        <button className="btn btn--primary btn--sm" style={{ flex: 1, fontSize: 11.5 }} disabled={!date || saving} onClick={save}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button className="btn btn--ghost btn--sm" style={{ fontSize: 11.5 }} disabled={saving} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

function TrialExtendPopover({ trial, targets, getToken, onRefresh, onClose, pos }) {
  const targetTrials = targets?.length ? targets : [trial];
  const [amount, setAmount] = useState(1);
  const [unit,   setUnit]   = useState('weeks'); // 'weeks' | 'days'
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    function onClick(e) { if (ref.current && !ref.current.contains(e.target)) onClose(); }
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    // position:fixed (escapes the table's scroll clip) — close on outside scroll.
    function onScroll(e) { if (ref.current && ref.current.contains(e.target)) return; onClose(); }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [onClose]);

  const days = unit === 'weeks' ? amount * 7 : amount;
  const base = trial.end_date || todayISO();
  const newEnd = new Date(base);
  newEnd.setDate(newEnd.getDate() + days);
  const newEndStr = newEnd.toISOString().slice(0, 10);
  const newEndLabel = fmt(newEndStr);

  function setQuick(days_, unit_) { setAmount(days_); setUnit(unit_); }

  async function handleExtend() {
    setSaving(true); setError(null);
    try {
      // Bulk extend is relative: each selected trial extends from its own end date.
      await Promise.all(targetTrials.map((t) => {
        const d = new Date(t.end_date || todayISO());
        d.setDate(d.getDate() + days);
        return saveTrial(getToken, t.trial_id, { end_date: d.toISOString().slice(0, 10), status: 'Active' });
      }));
      onRefresh?.();
      onClose();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div ref={ref} onClick={(e) => e.stopPropagation()} style={{
      position: 'fixed', top: pos?.top, right: pos?.right, zIndex: 9000, width: 220,
      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
      boxShadow: '0 8px 24px rgba(0,0,0,.14)', padding: 12, fontSize: 12.5,
      whiteSpace: 'normal', // anchored inside the table's nowrap Actions <td>
    }}>
      <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginBottom: 8 }}>
        {targetTrials.length > 1
          ? <>Extends <b style={{ color: 'var(--orange)' }}>{targetTrials.length} selected trials</b> by {days}d, each from its own end date</>
          : <>New end date: <b style={{ color: 'var(--text)' }}>{newEndLabel}</b></>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <button className="step-btn" style={{ width: 22, height: 22, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface-2)', cursor: 'pointer' }} onClick={() => setAmount((a) => Math.max(1, a - 1))}>–</button>
        <span style={{ fontWeight: 700, minWidth: 16, textAlign: 'center' }}>{amount}</span>
        <button className="step-btn" style={{ width: 22, height: 22, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface-2)', cursor: 'pointer' }} onClick={() => setAmount((a) => a + 1)}>+</button>
        <div style={{ display: 'flex', gap: 3, marginLeft: 'auto', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, padding: 2 }}>
          {['weeks', 'days'].map((u) => (
            <button key={u} type="button" onClick={() => setUnit(u)} style={{
              fontSize: 11, padding: '2px 7px', borderRadius: 5, border: 'none', cursor: 'pointer',
              background: unit === u ? 'var(--orange, #f97316)' : 'transparent',
              color: unit === u ? '#fff' : 'var(--text-3)', fontWeight: unit === u ? 700 : 500,
              textTransform: 'capitalize',
            }}>{u}</button>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 5, marginBottom: 10 }}>
        <button type="button" className="btn btn--ghost btn--sm" style={{ fontSize: 11, padding: '2px 7px' }} onClick={() => setQuick(1, 'weeks')}>1w</button>
        <button type="button" className="btn btn--ghost btn--sm" style={{ fontSize: 11, padding: '2px 7px' }} onClick={() => setQuick(2, 'weeks')}>2w</button>
        <button type="button" className="btn btn--ghost btn--sm" style={{ fontSize: 11, padding: '2px 7px' }} onClick={() => setQuick(4, 'weeks')}>4w</button>
      </div>
      {error && <div style={{ fontSize: 11, color: 'var(--red)', marginBottom: 6 }}>{error}</div>}
      <button className="btn btn--primary btn--sm" style={{ width: '100%' }} onClick={handleExtend} disabled={saving}>
        {saving ? 'Saving…' : targetTrials.length > 1 ? `Extend ${targetTrials.length} trials by ${days}d` : `Extend to ${newEndLabel}`}
      </button>
    </div>
  );
}

// ── QuickNoteForm — expanded-panel note capture (Phase 1 redesign) ───────────

function QuickNoteForm({ trial, getToken, onRefresh }) {
  const [text, setText] = useState('');
  const [addToTimeline, setAddToTimeline] = useState(true);
  const [followUpDate, setFollowUpDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);
  const [done,   setDone]   = useState(false);

  async function handleSave() {
    if (!text.trim()) return;
    setSaving(true); setError(null); setDone(false);
    try {
      await apiAddTrialNote(getToken, trial.trial_id, {
        contact_id: trial.contact_id ?? trial._contact?.contact_id ?? '',
        firm_id: trial.firm_id ?? trial._firm?.firm_id ?? '',
        note: text.trim(),
        add_to_timeline: addToTimeline,
        follow_up_date: followUpDate || null,
      });
      setText(''); setFollowUpDate(''); setDone(true);
      onRefresh?.();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
      <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.5px' }}>Quick note</span>
      <textarea
        value={text} onChange={(e) => setText(e.target.value)} rows={2}
        placeholder="Add a quick trial note…"
        style={{ ...inputStyle, resize: 'vertical', marginTop: 8 }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-2)' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
          <input type="checkbox" checked={addToTimeline} onChange={(e) => setAddToTimeline(e.target.checked)} />
          Add to Contact Timeline
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          Follow-up date
          <input type="date" value={followUpDate} onChange={(e) => setFollowUpDate(e.target.value)} style={{ fontSize: 11.5, border: '1px solid var(--border)', borderRadius: 5, padding: '2px 5px' }} />
        </label>
        <button className="btn btn--ghost btn--sm" style={{ marginLeft: 'auto' }} onClick={handleSave} disabled={saving || !text.trim()}>
          {saving ? 'Saving…' : 'Save note'}
        </button>
      </div>
      {error && <div style={{ fontSize: 11.5, color: 'var(--red)', marginTop: 6 }}>{error}</div>}
      {done && !error && <div style={{ fontSize: 11.5, color: 'var(--green)', marginTop: 6 }}>Saved.</div>}
    </div>
  );
}

// ── Add Trial User — inline fuzzy scoring (no library) ──────────────────────
// Tokenize the query, score each contact on {exact > starts-with > contains >
// per-token subsequence/char-overlap}, weighted name > email > firm, sum, sort desc.
function _fuzzyFieldScore(query, field) {
  if (!field) return 0;
  const f = field.toLowerCase();
  const q = query.toLowerCase();
  if (!q) return 0;
  if (f === q) return 100;
  if (f.startsWith(q)) return 70;
  if (f.includes(q)) return 45;
  // Per-token subsequence / char-overlap fallback: how many query tokens
  // appear (as a contiguous substring) somewhere in the field, plus a small
  // bonus for shared characters (cheap proxy for fuzzy/typo tolerance).
  const qTokens = q.split(/\s+/).filter(Boolean);
  let tokenHits = 0;
  for (const t of qTokens) {
    if (t.length >= 2 && f.includes(t)) tokenHits += 1;
  }
  let score = qTokens.length > 0 ? (tokenHits / qTokens.length) * 30 : 0;
  // Character-overlap proxy: fraction of query chars that appear in field, in order (subsequence).
  let fi = 0; let matched = 0;
  for (const ch of q) {
    const idx = f.indexOf(ch, fi);
    if (idx !== -1) { matched += 1; fi = idx + 1; }
  }
  if (q.length > 0) score += (matched / q.length) * 10;
  return score;
}

function scoreContactAgainstQuery(query, contact, firmName) {
  const name  = contact.name ?? '';
  const email = contact.email ?? '';
  const firm  = firmName ?? '';
  const nameScore  = _fuzzyFieldScore(query, name)  * 3;
  const emailScore = _fuzzyFieldScore(query, email) * 2;
  const firmScore  = _fuzzyFieldScore(query, firm)  * 1;
  return nameScore + emailScore + firmScore;
}

function rankContactsFuzzy(query, contacts, firmMap) {
  const q = query.trim();
  if (!q) return [];
  const scored = contacts.map((c) => ({
    contact: c,
    score: scoreContactAgainstQuery(q, c, firmMap?.[c.firm_id]?.firm_name),
  }));
  return scored
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 15)
    .map((s) => s.contact);
}

// Trial status for a contact from already-loaded allTrials — used both to
// show "current stage" in search results and to enforce one-active-trial-per-contact.
function activeTrialForContact(contactId, allTrials) {
  return allTrials.find((t) => t.contact_id === contactId && (t.status ?? '').toLowerCase() === 'active') ?? null;
}
function latestTrialStatusForContact(contactId, allTrials) {
  const rows = allTrials.filter((t) => t.contact_id === contactId);
  if (rows.length === 0) return null;
  const active = rows.find((t) => (t.status ?? '').toLowerCase() === 'active');
  if (active) return 'Active trial';
  const converted = rows.find((t) => (t.status ?? '').toLowerCase() === 'converted');
  if (converted) return 'Customer';
  return rows[0].status ?? null;
}

// Clerk hooks throw outside <ClerkProvider> (mounted only when
// LUMOSITY_ENABLED — see useLumosityAuth.js), so the hook call lives in a
// wrapper that only renders when the integration is on. Without it the modal
// works exactly as before, minus the create-product-user option.
function AddTrialUserModal(props) {
  return LUMOSITY_ENABLED
    ? <AddTrialUserModalWithClerk {...props} />
    : <AddTrialUserModalBase {...props} />;
}

function AddTrialUserModalWithClerk(props) {
  const { getClerkToken, clerkSignedIn, signInToLumosity } = useLumosityAuth();
  return (
    <AddTrialUserModalBase
      {...props}
      getClerkToken={getClerkToken}
      clerkSignedIn={clerkSignedIn}
      signInToLumosity={signInToLumosity}
    />
  );
}

function AddTrialUserModalBase({ contacts = [], allTrials = [], firms = [], firmMap = {}, settings = {}, userEmail = '', userName = '', getToken, onClose, onSaved, onOpenTimeline, getClerkToken = null, clerkSignedIn = false, signInToLumosity = null }) {
  const defaultTrialDays = Number(settings?.trialDefaultDays ?? 30);
  const todayStr = new Date().toLocaleDateString('sv');
  const defaultEndFor = (start) => new Date(new Date(start || todayStr).getTime() + defaultTrialDays * 86_400_000).toISOString().slice(0, 10);

  const [step, setStep] = useState('search'); // 'search' | 'confirm-existing' | 'confirm-new'
  const [query, setQuery] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);

  // Shared trial fields (kept across "Save & add another" batches)
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate]     = useState(defaultEndFor(todayStr));
  const [owner, setOwner]         = useState(userName || userEmail || '');
  const [source, setSource]       = useState('Dashboard');
  const [note, setNote]           = useState('');

  // New-contact fields (person-specific, reset on "Save & add another")
  const [newName, setNewName]   = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [firmMode, setFirmMode] = useState('existing'); // 'existing' | 'new'
  const [firmId, setFirmId]     = useState('');
  const [newFirmName, setNewFirmName] = useState('');

  // Implied product account (IMP-1804): same POST /v1/user/create the /admin
  // Create User page calls, then flagged lifecycle=trial via our backend so
  // the new user lands on the Clerk trial roster. On by default when the
  // Lumosity integration is configured.
  const [createAccount,   setCreateAccount]   = useState(Boolean(getClerkToken));
  const [accountPassword, setAccountPassword] = useState('');
  // Watchlist options mirror /admin Create User: S&P 500 or a custom ticker
  // list (with the same paste-from-Excel suffix cleanup).
  const [watchlistOption,  setWatchlistOption]  = useState('sp500'); // 'sp500' | 'custom'
  const [watchlistTickers, setWatchlistTickers] = useState('');
  // IMP-1812: 'fresh' seeds S&P 500/blank via /v1/user/create; 'copy' clones an
  // existing user's data via /v1/admin/copy-user (same as /admin Copy / Merge).
  const [accountMode,     setAccountMode]     = useState('fresh');
  const [copySourceEmail, setCopySourceEmail] = useState('');
  const [precheck,        setPrecheck]        = useState(null);
  const [precheckBusy,    setPrecheckBusy]    = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);
  const [blockedTrial, setBlockedTrial] = useState(null);

  const results = useMemo(() => rankContactsFuzzy(query, contacts, firmMap), [query, contacts, firmMap]);

  function resetPersonFields() {
    setSelectedContact(null);
    setQuery('');
    setNewName('');
    setNewEmail('');
    setFirmMode('existing');
    setFirmId('');
    setNewFirmName('');
    setBlockedTrial(null);
    setAccountPassword('');
    setCopySourceEmail('');
    setPrecheck(null);
    setWatchlistTickers('');
  }

  // Same Excel-paste cleanup as /admin Create User: strips ' Equity' / ' US'
  // suffixes and normalizes separators to a comma list.
  function cleanTickerList(input) {
    return input
      .split(/[\t\n,]/)
      .map((t) => t.trim())
      .filter(Boolean)
      .map((t) => t.replace(' Equity', '').replace(' US', '').trim())
      .filter(Boolean)
      .join(', ');
  }

  function handleTickerPaste(e) {
    e.preventDefault();
    setWatchlistTickers(cleanTickerList(e.clipboardData.getData('text')));
  }

  function pickExisting(contact) {
    const active = activeTrialForContact(contact.contact_id, allTrials);
    setSelectedContact(contact);
    setError(null);
    if (active) {
      setBlockedTrial(active);
    } else {
      setBlockedTrial(null);
    }
    setStep('confirm-existing');
  }

  function pickCreateNew() {
    setSelectedContact(null);
    setBlockedTrial(null);
    setError(null);
    setStep('confirm-new');
  }

  async function writeInitialNote(trialId, contactId, fId) {
    if (!note.trim()) return;
    try {
      await apiAddTrialNote(getToken, trialId, {
        contact_id: contactId,
        firm_id: fId || '',
        note: `Trial started · ${startDate}–${endDate} · Initial context: ${note.trim()}`,
        add_to_timeline: true,
        owner: owner || undefined,
      });
    } catch (e) {
      // Note failure shouldn't block the overall save — surface but don't
      // roll back the trial that was just created.
      console.warn('[AddTrialUserModal] initial note write failed:', e.message);
    }
  }

  async function handleSaveExisting(addAnother) {
    if (blockedTrial) return;
    setSaving(true); setError(null);
    try {
      const trialId = genId('t');
      const fId = selectedContact.firm_id ?? '';
      const notesPrefix = [source ? `Source: ${source}` : '', owner ? `Owner: ${owner}` : ''].filter(Boolean).join(' · ');
      await apiCreateTrial(getToken, {
        trial_id: trialId,
        contact_id: selectedContact.contact_id,
        firm_id: fId,
        start_date: startDate,
        end_date: endDate,
        status: 'Active',
        notes: notesPrefix,
      });
      await writeInitialNote(trialId, selectedContact.contact_id, fId);
      // Sync contact stage so Contacts view reflects the trial immediately
      await apiUpdateContact(getToken, selectedContact.contact_id, { stage_override: 'trial' }).catch((e) => {
        console.error('[AddTrialUserModal] stage sync failed:', e);
        setError('Trial saved, but contact stage could not be updated to Trial. You can set it manually in Contacts.');
      });
      if (addAnother) {
        resetPersonFields();
        setStep('search');
      } else {
        onSaved?.();
        onClose?.();
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  // Create the Implied product account + flag it on-trial. Runs after the CRM
  // writes; failures surface as a warning rather than rolling anything back
  // (the CRM trial row is already valid on its own).
  async function createProductAccount(name, email, trialId) {
    const parts = name.split(/\s+/);
    const firstName = parts[0];
    const lastName  = parts.slice(1).join(' ') || parts[0]; // infra requires a non-empty last name

    let clerkUserId = null;
    if (accountMode === 'copy') {
      // Copy-create: /v1/admin/copy-user makes the Clerk account then clones
      // the source user's data onto it (no S&P seeding — data comes over).
      await copyProductUser(getClerkToken, {
        sourceEmail: copySourceEmail.trim(), newEmail: email,
        firstName, lastName, password: accountPassword,
      });
    } else {
      const tickers = watchlistOption === 'custom'
        ? watchlistTickers.split(',').map((t) => t.trim()).filter(Boolean)
        : [];
      const result = await createProductUser(getClerkToken, {
        firstName, lastName, email, password: accountPassword,
        useSp500: watchlistOption === 'sp500', watchlistTickers: tickers,
      });
      clerkUserId = result?.clerk_user_id ?? null;
    }
    // Flag lifecycle=trial via our backend so the user is on the trial roster
    // immediately (infra stamps it at creation too once PR #2833 deploys —
    // this also writes clerk_user_id onto the trial row either way).
    await apiFlagClerkUser(getToken, {
      ...(clerkUserId ? { clerk_user_id: clerkUserId } : {}), email, lifecycle: 'trial', trial_id: trialId,
    });
    return clerkUserId;
  }

  async function runCopyPrecheck() {
    const src = copySourceEmail.trim().toLowerCase();
    const dst = newEmail.trim().toLowerCase();
    if (!/\S+@\S+\.\S+/.test(src)) { setError('Enter a valid source email to check.'); return; }
    if (!/\S+@\S+\.\S+/.test(dst)) { setError('Enter the new user’s email first — the check compares both accounts.'); return; }
    setPrecheckBusy(true); setPrecheck(null); setError(null);
    try {
      setPrecheck(await precheckCopyUser(getClerkToken, src, dst));
    } catch (e) {
      setError(`Source check failed: ${e.message}`);
    } finally {
      setPrecheckBusy(false);
    }
  }

  async function handleSaveNew(addAnother) {
    setSaving(true); setError(null);
    const name = newName.trim();
    const email = newEmail.trim().toLowerCase();
    if (!name) { setError('Name is required.'); setSaving(false); return; }
    if (!email || !/\S+@\S+\.\S+/.test(email)) { setError('A valid email is required.'); setSaving(false); return; }
    if (firmMode === 'new' && !newFirmName.trim()) { setError('Firm name is required when creating a new firm.'); setSaving(false); return; }
    const wantsAccount = createAccount && Boolean(getClerkToken);
    if (wantsAccount && !clerkSignedIn) { setError('Sign in to Implied (button below) to create app accounts, or untick the option.'); setSaving(false); return; }
    if (wantsAccount && accountPassword.length < 8) { setError('Account password must be at least 8 characters.'); setSaving(false); return; }
    if (wantsAccount && accountMode === 'copy' && !/\S+@\S+\.\S+/.test(copySourceEmail.trim())) { setError('A valid source email is required to copy from.'); setSaving(false); return; }
    if (wantsAccount && accountMode === 'fresh' && watchlistOption === 'custom' && !watchlistTickers.trim()) { setError('Provide ticker symbols for the custom watchlist (or switch to S&P 500).'); setSaving(false); return; }

    let newContactId = null;
    try {
      let fId = firmMode === 'existing' ? firmId : '';
      if (firmMode === 'new') {
        fId = genId('firm');
        await apiCreateFirm(getToken, { firm_id: fId, firm_name: newFirmName.trim() });
      }
      newContactId = genId('c');
      await apiCreateContact(getToken, {
        contact_id: newContactId,
        name,
        email,
        firm_id: fId,
        source: source || 'Dashboard',
        added_date: new Date().toISOString().slice(0, 10),
      });

      // Contact created — now create the trial. If this fails, the contact
      // still exists; say so explicitly rather than a generic error.
      try {
        const trialId = genId('t');
        const notesPrefix = [source ? `Source: ${source}` : '', owner ? `Owner: ${owner}` : ''].filter(Boolean).join(' · ');
        await apiCreateTrial(getToken, {
          trial_id: trialId,
          contact_id: newContactId,
          firm_id: fId,
          start_date: startDate,
          end_date: endDate,
          status: 'Active',
          notes: notesPrefix,
        });
        await writeInitialNote(trialId, newContactId, fId);
        await apiUpdateContact(getToken, newContactId, { stage_override: 'trial' }).catch((e) => {
          console.error('[AddTrialUserModal] stage sync failed:', e);
        });
        if (wantsAccount) {
          try {
            await createProductAccount(name, email, trialId);
          } catch (acctErr) {
            setError(`Trial saved, but the Implied app account could not be created: ${acctErr.message}. Create it from /admin (or retry here) and it will sync onto this trial.`);
            setSaving(false);
            onSaved?.();
            return;
          }
        }
      } catch (trialErr) {
        setError(`Contact "${name}" was created, but the trial failed to save: ${trialErr.message}. Open the contact and add the trial manually.`);
        setSaving(false);
        onSaved?.(); // refresh so the new contact shows up even though the trial didn't
        return;
      }

      if (addAnother) {
        resetPersonFields();
        setStep('search');
      } else {
        onSaved?.();
        onClose?.();
      }
    } catch (contactErr) {
      setError(contactErr.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
    }}
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose?.(); }}
    >
      <div style={{
        background: 'var(--surface)', borderRadius: 'var(--radius-md)',
        padding: '24px 28px', width: 460, maxWidth: '92vw',
        maxHeight: '86vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Add trial user</div>

        {step === 'search' && (
          <>
            <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginBottom: 14 }}>
              Search existing contacts by name, email, or company — or create a new one.
            </div>
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name, email, or company…"
              style={inputStyle}
            />
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 320, overflowY: 'auto' }}>
              {results.map((c) => {
                const status = latestTrialStatusForContact(c.contact_id, allTrials);
                return (
                  <button
                    key={c.contact_id}
                    onClick={() => pickExisting(c)}
                    style={{
                      display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'left',
                      padding: '8px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
                      background: 'var(--surface-2)', cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}>
                      {c.name || '—'}
                      {status && <span className="chip" style={{ fontSize: 10.5, padding: '1px 6px' }}>{status}</span>}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                      {firmMap?.[c.firm_id]?.firm_name ?? '—'} · {c.email}
                    </div>
                  </button>
                );
              })}
              {query.trim() && results.length === 0 && (
                <div style={{ fontSize: 12.5, color: 'var(--text-3)', padding: '6px 2px' }}>No matching contacts.</div>
              )}
            </div>
            <button
              onClick={pickCreateNew}
              className="btn btn--ghost btn--sm"
              style={{ marginTop: 12, width: '100%', justifyContent: 'center' }}
            >
              + Create new contact
            </button>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn--ghost btn--sm" onClick={onClose}>Cancel</button>
            </div>
          </>
        )}

        {step === 'confirm-existing' && selectedContact && (
          <>
            <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 4 }}>
              {selectedContact.name || selectedContact.email}
              {firmMap?.[selectedContact.firm_id]?.firm_name ? ` · ${firmMap[selectedContact.firm_id].firm_name}` : ''}
            </div>
            {blockedTrial ? (
              <div style={{ fontSize: 13, color: '#b91c1c', background: 'var(--red-bg)', borderRadius: 6, padding: '10px 14px', margin: '10px 0' }}>
                This contact already has an active trial ({blockedTrial.start_date}–{blockedTrial.end_date}). Only one active trial per contact is allowed.
                <div style={{ marginTop: 8 }}>
                  <button
                    className="btn btn--ghost btn--sm"
                    onClick={() => { onOpenTimeline?.(selectedContact); onClose?.(); }}
                  >
                    Open existing trial
                  </button>
                </div>
              </div>
            ) : (
              <TrialFieldsForm
                startDate={startDate} setStartDate={setStartDate}
                endDate={endDate} setEndDate={setEndDate}
                owner={owner} setOwner={setOwner}
                source={source} setSource={setSource}
                note={note} setNote={setNote}
              />
            )}
            {error && <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 10 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
              <button className="btn btn--ghost btn--sm" onClick={() => setStep('search')} disabled={saving}>Back</button>
              {!blockedTrial && (
                <>
                  <button className="btn btn--ghost btn--sm" onClick={() => handleSaveExisting(true)} disabled={saving}>
                    {saving ? 'Saving…' : 'Save & add another'}
                  </button>
                  <button className="btn btn--primary btn--sm" onClick={() => handleSaveExisting(false)} disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </>
              )}
            </div>
          </>
        )}

        {step === 'confirm-new' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 14px', margin: '10px 0 14px' }}>
              <div>
                <label style={labelStyle}>Name</label>
                <input value={newName} onChange={(e) => setNewName(e.target.value)} style={inputStyle} placeholder="Full name" />
              </div>
              <div>
                <label style={labelStyle}>Email</label>
                <input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} style={inputStyle} placeholder="name@firm.com" />
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Company / firm</label>
              <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                <button type="button" className={`btn btn--sm ${firmMode === 'existing' ? 'btn--primary' : 'btn--ghost'}`} onClick={() => setFirmMode('existing')}>Existing</button>
                <button type="button" className={`btn btn--sm ${firmMode === 'new' ? 'btn--primary' : 'btn--ghost'}`} onClick={() => setFirmMode('new')}>New firm</button>
              </div>
              {firmMode === 'existing' ? (
                <select value={firmId} onChange={(e) => setFirmId(e.target.value)} style={inputStyle}>
                  <option value="">— No firm —</option>
                  {firms.map((f) => <option key={f.firm_id} value={f.firm_id}>{f.firm_name}</option>)}
                </select>
              ) : (
                <input value={newFirmName} onChange={(e) => setNewFirmName(e.target.value)} style={inputStyle} placeholder="New firm name" />
              )}
            </div>
            <TrialFieldsForm
              startDate={startDate} setStartDate={setStartDate}
              endDate={endDate} setEndDate={setEndDate}
              owner={owner} setOwner={setOwner}
              source={source} setSource={setSource}
              note={note} setNote={setNote}
            />

            {getClerkToken && (
              <div style={{ marginTop: 14, padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'var(--surface-2)' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  <input type="checkbox" checked={createAccount} onChange={(e) => setCreateAccount(e.target.checked)} />
                  Create Implied app account
                </label>
                <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 4, marginLeft: 24 }}>
                  Creates the Clerk user + org + watchlist (same as /admin Create User) and flags them as on trial.
                </div>
                {createAccount && (clerkSignedIn ? (
                  <div style={{ marginTop: 10, marginLeft: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button type="button" className={`btn btn--sm ${accountMode === 'fresh' ? 'btn--primary' : 'btn--ghost'}`} onClick={() => setAccountMode('fresh')}>Fresh account</button>
                      <button type="button" className={`btn btn--sm ${accountMode === 'copy' ? 'btn--primary' : 'btn--ghost'}`} onClick={() => setAccountMode('copy')}>Copy from existing user</button>
                    </div>
                    <div>
                      <label style={labelStyle}>Password (min 8 characters)</label>
                      <input type="password" value={accountPassword} onChange={(e) => setAccountPassword(e.target.value)} style={inputStyle} placeholder="Initial password" autoComplete="new-password" />
                    </div>
                    {accountMode === 'fresh' ? (
                      <div>
                        <label style={labelStyle}>Watchlist</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
                            <input type="radio" name="watchlistOption" checked={watchlistOption === 'sp500'} onChange={() => setWatchlistOption('sp500')} />
                            Use S&amp;P 500 tickers
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
                            <input type="radio" name="watchlistOption" checked={watchlistOption === 'custom'} onChange={() => setWatchlistOption('custom')} />
                            Provide custom ticker list
                          </label>
                        </div>
                        {watchlistOption === 'custom' && (
                          <div style={{ marginTop: 6 }}>
                            <textarea
                              rows={3}
                              value={watchlistTickers}
                              onChange={(e) => setWatchlistTickers(e.target.value)}
                              onPaste={handleTickerPaste}
                              placeholder="AAPL, MSFT, GOOGL or paste from Excel"
                              style={{ ...inputStyle, resize: 'vertical' }}
                            />
                            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 3 }}>
                              Paste from Excel: automatically removes &lsquo; Equity&rsquo; and &lsquo; US&rsquo; suffixes
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <label style={labelStyle}>Copy data from (email)</label>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <input value={copySourceEmail} onChange={(e) => { setCopySourceEmail(e.target.value); setPrecheck(null); }} style={{ ...inputStyle, flex: 1 }} placeholder="existing.user@firm.com" />
                          <button type="button" className="btn btn--ghost btn--sm" onClick={runCopyPrecheck} disabled={precheckBusy}>
                            {precheckBusy ? 'Checking…' : 'Check source'}
                          </button>
                        </div>
                        <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 4 }}>
                          Clones everything — watchlist, chats, sheets, workspaces, workflows, Protégé, vault — like the /admin Copy / Merge User tab.
                        </div>
                        {precheck?.old && (
                          <div style={{ fontSize: 11.5, marginTop: 6, padding: '6px 8px', borderRadius: 5, background: 'var(--surface)', border: '1px solid var(--border)' }}>
                            {precheck.old.exists_in_clerk === false ? (
                              <span style={{ color: 'var(--red)' }}>No Implied app account found for that source email.</span>
                            ) : (
                              <>
                                <span style={{ fontWeight: 700 }}>Will copy:</span>{' '}
                                {[
                                  `${precheck.old.chats ?? 0} chats`,
                                  `${precheck.old.watchlists ?? 0} watchlists`,
                                  `${precheck.old.workspaces ?? 0} workspaces`,
                                  `${precheck.old.workflows ?? 0} workflows`,
                                  `${precheck.old.datasheets ?? 0} sheets`,
                                  `${precheck.old.vault_notes ?? 0} vault notes`,
                                  `${(precheck.old.proteges ?? []).length} Protégés`,
                                ].join(' · ')}
                                {precheck.new?.exists_in_clerk && (
                                  <div style={{ color: 'var(--amber)', marginTop: 3 }}>
                                    Heads up: the new email already has an app account — replace-copy will overwrite its data.
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ marginTop: 10, marginLeft: 24, fontSize: 12.5 }}>
                    <button type="button" className="btn btn--ghost btn--sm" onClick={() => signInToLumosity?.()}>
                      Sign in to Implied to create accounts
                    </button>
                  </div>
                ))}
              </div>
            )}

            {error && <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 10 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
              <button className="btn btn--ghost btn--sm" onClick={() => setStep('search')} disabled={saving}>Back</button>
              <button className="btn btn--ghost btn--sm" onClick={() => handleSaveNew(true)} disabled={saving}>
                {saving ? 'Saving…' : 'Save & add another'}
              </button>
              <button className="btn btn--primary btn--sm" onClick={() => handleSaveNew(false)} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TrialFieldsForm({ startDate, setStartDate, endDate, setEndDate, owner, setOwner, source, setSource, note, setNote }) {
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 14px', marginBottom: 12 }}>
        <div>
          <label style={labelStyle}>Start date</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>End date</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Owner</label>
          <input value={owner} onChange={(e) => setOwner(e.target.value)} style={inputStyle} placeholder="Owner name" />
        </div>
        <div>
          <label style={labelStyle}>Source</label>
          <input value={source} onChange={(e) => setSource(e.target.value)} style={inputStyle} placeholder="Dashboard" />
        </div>
      </div>
      <div>
        <label style={labelStyle}>Initial note (optional)</label>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Context for this trial…" />
      </div>
    </div>
  );
}

function TrialsSection({ trials, allTrials = [], criticalTrials, onOpenTimeline, onOpenDraft, onOpenAddContact, patchTrial, upsertContact, getToken, onRefresh, firms, contacts, highlightTrialId, onClearTrialHighlight, amplitudeScores, watchlistMap = {}, settings = {}, userEmail = '', userName = '', firmMap = {} }) {
  const [showAddTrialUser, setShowAddTrialUser] = useState(false);
  const [selectedIds,          setSelectedIds]          = useState(new Set());
  const [showModal,            setShowModal]            = useState(false);
  // Calendar view moved out of the primary Trials UI — TrialsCalendar component
  // is kept for a future "More" menu entry (Phase 2), not rendered here.
  const [deleteBusy,           setDeleteBusy]           = useState(false);
  const [error,                setError]                = useState(null);
  const [optimisticDeletedIds, setOptimisticDeletedIds] = useState(new Set());
  const [ampBusy,         setAmpBusy]         = useState(false);
  const [clerkSyncBusy,   setClerkSyncBusy]   = useState(false);
  const [clerkSyncNote,   setClerkSyncNote]   = useState(null);
  const [ampResult,       setAmpResult]       = useState(null);
  const [ampError,        setAmpError]        = useState(null);
  const [statusTab,       setStatusTab]       = useState('Active');
  const [search,          setSearch]          = useState('');
  const [sortBy,          setSortBy]          = useState('urgency'); // urgency | end_date | health | last_activity
  const [quickFilter,     setQuickFilter]     = useState(null); // ending_week | expired | need_checkin | at_risk
  // Spreadsheet-style column filters (IMP-1806) — Sets of selected values per column.
  const [colFilters,      setColFilters]      = useState(EMPTY_COL_FILTERS);
  // Header sort: overrides the Sort dropdown while set. Click cycles asc → desc → off.
  const [tableSort,       setTableSort]       = useState(null); // { col, dir } | null

  function cycleTableSort(col) {
    setTableSort((prev) => {
      if (prev?.col !== col) return { col, dir: 'asc' };
      if (prev.dir === 'asc') return { col, dir: 'desc' };
      return null;
    });
  }
  const [moreOpen,        setMoreOpen]        = useState(false);
  const moreRef = useRef(null);

  const [matchItem,  setMatchItem]  = useState(null);
  const [matchError, setMatchError] = useState(null);

  useEffect(() => {
    if (!moreOpen) return;
    function onClick(e) { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [moreOpen]);

  function handleLinked(_email, _contact) {
    setMatchItem(null);
    onRefresh?.();
  }

  function handleAddNew(prefill) {
    setMatchItem(null);
    onOpenAddContact?.(prefill);
  }

  async function handleAmplitudeSync() {
    setAmpBusy(true); setAmpResult(null); setAmpError(null);
    try {
      const result = await requestAmplitudeSync(getToken);
      setAmpResult(result);
      onRefresh?.();
    } catch (e) {
      setAmpError(e.message);
    } finally {
      setAmpBusy(false);
    }
  }

  // activeTrials (already status='Active', has full grouping/derived fields)
  const activeVisible = trials.filter((t) => !optimisticDeletedIds.has(t.trial_id));

  const grouped = useMemo(() => {
    const g = { act_today: [], this_week: [], expired: [], monitoring: [] };
    for (const t of activeVisible) (g[t._group] ?? g.monitoring).push(t);
    return g;
  }, [activeVisible]);

  // Non-expired trials missing a log for their current week
  const missingLogCount = useMemo(() => {
    return activeVisible.filter((t) => {
      if (t._group === 'expired') return false;
      const startMs = new Date(t.start_date || 0).getTime();
      const totalWeeks = Math.max(1, Math.ceil(t._totalDays / 7));
      const w = Math.min(totalWeeks, Math.max(1, Math.ceil((Date.now() - startMs) / (7 * 86_400_000))));
      return !(t._weekLogs?.[w]?.length > 0);
    }).length;
  }, [activeVisible]);

  const atRiskCount = useMemo(() => activeVisible.filter((t) => t._health?.score === 'red').length, [activeVisible]);
  const endingThisWeekCount = grouped.this_week.length + grouped.act_today.length;
  // Trials with no linked Clerk product account (clerk_user_id stamped by the
  // roster sync / lifecycle writes) — CRM-only rows whose user can't log in.
  const noAppAccountCount = useMemo(
    () => activeVisible.filter((t) => !String(t.clerk_user_id ?? '').trim()).length,
    [activeVisible],
  );

  // ── Status tab base list ────────────────────────────────────────────────
  const statusBase = useMemo(() => {
    const pool = allTrials.filter((t) => !optimisticDeletedIds.has(t.trial_id));
    if (statusTab === 'All') return pool;
    return pool.filter((t) => (t.status ?? '').toLowerCase() === statusTab.toLowerCase());
  }, [allTrials, optimisticDeletedIds, statusTab]);

  const anyColFilter = Object.values(colFilters).some((s) => s.size > 0);

  // Option lists (with counts) for the column filters, computed from the
  // current status tab's pool — before column filtering, so unpicked values
  // stay visible and can be added to a selection.
  const colOptions = useMemo(() => {
    const pool = statusTab === 'Active' ? activeVisible : statusBase;
    const tally = (getVals) => {
      const m = new Map();
      for (const t of pool) {
        const vals = getVals(t);
        for (const v of (vals.length ? vals : ['—'])) m.set(v, (m.get(v) ?? 0) + 1);
      }
      return [...m.entries()]
        .sort((a, b) => b[1] - a[1] || String(a[0]).localeCompare(String(b[0])))
        .map(([value, count]) => ({ value, label: value, count }));
    };
    const featureNames = [...new Set(pool.flatMap(trialFeatureList))].sort();
    const featureOpts = featureNames.length === 0 ? [] : [
      { value: '_h_used', label: 'Has used', heading: true },
      ...featureNames.map((f) => ({ value: `used:${f}`, label: f, count: pool.filter((t) => trialFeatureList(t).includes(f)).length })),
      { value: '_h_unused', label: 'Has NOT used', heading: true },
      ...featureNames.map((f) => ({ value: `unused:${f}`, label: f, count: pool.filter((t) => !trialFeatureList(t).includes(f)).length })),
    ];
    return {
      org:     tally((t) => [t._firm?.firm_name].filter(Boolean)),
      sector:  tally(trialSectors),
      role:    tally(trialRoles),
      health:  tally((t) => [trialHealthLabel(t)]),
      usage:   tally((t) => [trialUsageTierLabel(t, amplitudeScores)]),
      login:   TRIAL_LOGIN_BUCKETS.map((b) => ({ value: b, label: b, count: pool.filter((t) => trialLoginBucket(t) === b).length })).filter((o) => o.count > 0),
      feature: featureOpts,
    };
  }, [statusTab, activeVisible, statusBase, amplitudeScores]);

  function matchesColFilters(t) {
    const f = colFilters;
    if (f.org.size    && !f.org.has(t._firm?.firm_name || '—')) return false;
    if (f.sector.size) { const vals = trialSectors(t); if (!(vals.length ? vals : ['—']).some((v) => f.sector.has(v))) return false; }
    if (f.role.size)   { const vals = trialRoles(t);   if (!(vals.length ? vals : ['—']).some((v) => f.role.has(v))) return false; }
    if (f.health.size && !f.health.has(trialHealthLabel(t))) return false;
    if (f.usage.size  && !f.usage.has(trialUsageTierLabel(t, amplitudeScores))) return false;
    if (f.login.size  && !f.login.has(trialLoginBucket(t))) return false;
    if (f.feature.size) {
      const used = new Set(trialFeatureList(t));
      // AND semantics: every selected used:/unused: constraint must hold.
      for (const sel of f.feature) {
        const i = sel.indexOf(':');
        const mode = sel.slice(0, i), feat = sel.slice(i + 1);
        if (mode === 'used'   && !used.has(feat)) return false;
        if (mode === 'unused' &&  used.has(feat)) return false;
      }
    }
    return true;
  }

  function matchesSearch(t) {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    const names = (t._contacts?.length ? t._contacts : (t._contact ? [t._contact] : [])).map((c) => `${c.name ?? ''} ${c.email ?? ''}`).join(' ');
    const firm = t._firm?.firm_name ?? '';
    // Pod shops: searching the parent firm ("millennium") also matches trials
    // at its child pods (santa rosa etc.), alongside any direct parent users.
    const parentFirm = firmMap[t._firm?.parent_firm_id]?.firm_name ?? '';
    // Unlinked roster rows have no contact — the table shows their Clerk name
    // and login email, so search must match those too.
    const rosterIdentity = `${t.clerk_name ?? ''} ${t.amplitude_user_id ?? ''}`;
    return `${names} ${firm} ${parentFirm} ${rosterIdentity}`.toLowerCase().includes(q);
  }

  function matchesQuickFilter(t) {
    if (!quickFilter) return true;
    if (quickFilter === 'ending_week') return t._group === 'this_week' || t._group === 'act_today';
    if (quickFilter === 'expired') return t._group === 'expired';
    if (quickFilter === 'at_risk') return t._health?.score === 'red';
    if (quickFilter === 'no_app') return !String(t.clerk_user_id ?? '').trim();
    if (quickFilter === 'need_checkin') {
      if (t._group === 'expired') return false;
      const startMs = new Date(t.start_date || 0).getTime();
      const totalWeeks = Math.max(1, Math.ceil(t._totalDays / 7));
      const w = Math.min(totalWeeks, Math.max(1, Math.ceil((Date.now() - startMs) / (7 * 86_400_000))));
      return !(t._weekLogs?.[w]?.length > 0);
    }
    return true;
  }

  const filteredFlat = useMemo(() => {
    const base = statusTab === 'Active' ? activeVisible : statusBase;
    let list = base.filter((t) => matchesSearch(t) && matchesQuickFilter(t) && matchesColFilters(t));
    const healthRank = { red: 0, amber: 1, green: 2 };
    const sortCol = tableSort && TRIAL_TABLE_COLUMNS.find((c) => c.key === tableSort.col);
    if (sortCol) {
      // Header sort wins over the Sort dropdown while active.
      const sign = tableSort.dir === 'asc' ? 1 : -1;
      list = [...list].sort((a, b) => {
        const va = sortCol.sortVal(a, amplitudeScores), vb = sortCol.sortVal(b, amplitudeScores);
        return (va < vb ? -1 : va > vb ? 1 : 0) * sign;
      });
    } else if (sortBy === 'end_date') {
      list = [...list].sort((a, b) => new Date(a.end_date || 0) - new Date(b.end_date || 0));
    } else if (sortBy === 'health') {
      list = [...list].sort((a, b) => (healthRank[a._health?.score] ?? 3) - (healthRank[b._health?.score] ?? 3));
    } else if (sortBy === 'last_activity') {
      list = [...list].sort((a, b) => (a._lastTouchDaysAgo ?? Infinity) - (b._lastTouchDaysAgo ?? Infinity));
    } else {
      // urgency (default)
      const order = { act_today: 0, this_week: 1, expired: 2, monitoring: 3 };
      list = [...list].sort((a, b) => {
        const gDiff = (order[a._group] ?? 3) - (order[b._group] ?? 3);
        if (gDiff !== 0) return gDiff;
        if (a._group === 'expired') return (b._daysOverdue ?? 0) - (a._daysOverdue ?? 0);
        return (a._daysLeft ?? 0) - (b._daysLeft ?? 0);
      });
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusTab, activeVisible, statusBase, search, quickFilter, sortBy, colFilters, tableSort, amplitudeScores]);

  const selectedTrials = activeVisible.filter((t) => selectedIds.has(t.trial_id));

  function toggleSelect(id) {
    setSelectedIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function toggleSelectAll(list) {
    const ids = list.map((t) => t.trial_id);
    const allOn = ids.every((id) => selectedIds.has(id));
    setSelectedIds(allOn ? new Set() : new Set(ids));
  }
  async function handleDeleteConfirmed(lockClerk = false, deleteUser = false) {
    const idsToDelete = [...selectedIds];
    setOptimisticDeletedIds((prev) => { const n = new Set(prev); idsToDelete.forEach((id) => n.add(id)); return n; });
    setSelectedIds(new Set()); setShowModal(false); setDeleteBusy(false); setError(null);
    try {
      if (deleteUser) {
        // Full offboard (IMP-1833): one call per linked contact — hides the
        // contact everywhere, deletes their trial rows, locks the account.
        const contactIds = [...new Set(
          selectedTrials.filter((t) => idsToDelete.includes(t.trial_id))
            .map((t) => String(t.contact_id ?? '').trim()).filter(Boolean),
        )];
        await Promise.all(contactIds.map((id) => apiOffboardContact(getToken, id)));
        // Trials with no linked contact still need deleting directly.
        const orphanIds = selectedTrials
          .filter((t) => idsToDelete.includes(t.trial_id) && !String(t.contact_id ?? '').trim())
          .map((t) => t.trial_id);
        if (orphanIds.length) await apiDeleteTrials(getToken, orphanIds, { lockClerk: true });
        onRefresh?.();
        return;
      }
      const r = await apiDeleteTrials(getToken, idsToDelete, { lockClerk });
      if (r?.clerk?.failed?.length) {
        setError(`Deleted, but ${r.clerk.failed.length} account lock${r.clerk.failed.length > 1 ? 's' : ''} failed: ${r.clerk.failed[0].error}`);
      }
      onRefresh?.();
    } catch (e) {
      setOptimisticDeletedIds((prev) => { const n = new Set(prev); idsToDelete.forEach((id) => n.delete(id)); return n; });
      setSelectedIds(new Set(idsToDelete));
      setError('Delete failed: ' + e.message);
    }
  }
  function toggleQuickFilter(key) {
    setQuickFilter((prev) => (prev === key ? null : key));
  }

  const displayList = filteredFlat;
  const allSelected = displayList.length > 0 && displayList.every((t) => selectedIds.has(t.trial_id));
  const someSelected = !allSelected && displayList.some((t) => selectedIds.has(t.trial_id));

  if (allTrials.length === 0 && trials.length === 0) {
    return (
      <div>
        {matchItem && <MatchContactPanel item={matchItem} firms={firms} getToken={getToken} onClose={() => setMatchItem(null)} onLinked={(e, c) => handleLinked(e, c)} onAddNew={handleAddNew} />}
        <div className="empty-state">
          <div className="empty-state__icon">🧪</div>
          <div className="empty-state__title">No active trials</div>
          <div className="empty-state__sub">Active trials appear here once started</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {showAddTrialUser && (
        <AddTrialUserModal
          contacts={contacts}
          allTrials={allTrials}
          firms={firms}
          firmMap={firmMap}
          settings={settings}
          userEmail={userEmail}
          userName={userName}
          getToken={getToken}
          onClose={() => setShowAddTrialUser(false)}
          onSaved={() => { onRefresh?.(); }}
          onOpenTimeline={onOpenTimeline}
        />
      )}
      {/* ── Status bar: clickable counts + primary toolbar ───────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
        background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
        padding: '8px 12px', marginBottom: 12,
      }}>
        <StatusBarStat label="Active" count={activeVisible.length} active={false} onClick={null} />
        <StatusBarDivider />
        <StatusBarStat label="ending this week" count={endingThisWeekCount} color="var(--amber)" active={quickFilter === 'ending_week'} onClick={() => toggleQuickFilter('ending_week')} />
        <StatusBarDivider />
        <StatusBarStat label="expired" count={grouped.expired.length} color="var(--red)" active={quickFilter === 'expired'} onClick={() => toggleQuickFilter('expired')} />
        <StatusBarDivider />
        <StatusBarStat label="need check-in" count={missingLogCount} active={quickFilter === 'need_checkin'} onClick={() => toggleQuickFilter('need_checkin')} />
        <StatusBarDivider />
        <StatusBarStat label="at risk" count={atRiskCount} color="var(--red)" active={quickFilter === 'at_risk'} onClick={() => toggleQuickFilter('at_risk')} />
        <StatusBarDivider />
        <StatusBarStat label="no app account" count={noAppAccountCount} color="var(--blue)" active={quickFilter === 'no_app'} onClick={() => toggleQuickFilter('no_app')} />
        <div style={{ flex: 1 }} />
        {clerkSyncNote && <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{clerkSyncNote}</span>}
        <button
          className="btn btn--ghost btn--sm"
          disabled={clerkSyncBusy}
          title="Force the Clerk roster sync now (bypasses the merge throttle and server caches)"
          onClick={async () => {
            setClerkSyncBusy(true); setClerkSyncNote(null);
            try {
              const r = await apiSyncClerkRoster(getToken);
              const cust = r.customers
                ? ` · ${r.customers.total} customers (${r.customers.created} created, ${r.customers.flagged} flagged)`
                : '';
              setClerkSyncNote(`Synced — ${r.clerk_linked}/${r.trials} trials Clerk-linked${cust}`);
              onRefresh?.();
            } catch (e) {
              setClerkSyncNote(`Sync failed: ${e.message}`);
            } finally {
              setClerkSyncBusy(false);
            }
          }}>
          {clerkSyncBusy ? 'Syncing…' : '⟳ Sync Clerk'}
        </button>
        <button className="btn btn--primary btn--sm" onClick={() => setShowAddTrialUser(true)}>+ Add trial user</button>
        <div ref={moreRef} style={{ position: 'relative' }}>
          <button className="btn btn--ghost btn--sm" onClick={() => setMoreOpen((v) => !v)}>More ▾</button>
          {moreOpen && (
            <div style={{
              position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 30, minWidth: 190,
              background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
              boxShadow: '0 8px 24px rgba(0,0,0,.14)', overflow: 'hidden',
            }}>
              <button
                onClick={() => { setMoreOpen(false); handleAmplitudeSync(); }}
                disabled={ampBusy}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12.5, color: 'var(--text)' }}
              >
                {ampBusy ? 'Refreshing…' : 'Refresh Amplitude'}
              </button>
              <button
                onClick={() => { setMoreOpen(false); exportTrialsCsv(displayList); }}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12.5, color: 'var(--text)', borderTop: '1px solid var(--border)' }}
              >
                Export CSV
              </button>
            </div>
          )}
        </div>
      </div>
      {ampError && <div style={{ fontSize: 11.5, color: 'var(--red)', marginBottom: 8 }}>{ampError}</div>}
      {ampResult && <div style={{ fontSize: 11.5, color: 'var(--green)', marginBottom: 8 }}>Amplitude sync complete.</div>}

      {/* ── Filter bar: status tabs + search + sort ───────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        {['Active', 'Converted', 'Churned', 'Cancelled', 'All'].map((s) => (
          <button
            key={s}
            onClick={() => setStatusTab(s)}
            className="chip"
            style={{
              fontSize: 12.5, fontWeight: 600, padding: '5px 12px', borderRadius: 999,
              border: `1px solid ${statusTab === s ? 'var(--orange)' : 'var(--border)'}`,
              background: statusTab === s ? 'var(--orange-bg, #fff7ed)' : 'var(--surface)',
              color: statusTab === s ? 'var(--orange)' : 'var(--text-3)', cursor: 'pointer',
            }}
          >
            {s}
          </button>
        ))}
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, email, company"
          style={{ flex: '1 1 260px', maxWidth: 520, fontSize: 13, padding: '6px 10px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)' }}
        />
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-3)' }}>
          Sort:
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={{ fontSize: 12.5, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', borderRadius: 6, padding: '4px 6px' }}>
            <option value="urgency">Urgency</option>
            <option value="end_date">End date</option>
            <option value="health">Health</option>
            <option value="last_activity">Last activity</option>
          </select>
        </div>
      </div>

      {/* Active column-filter summary — the filters themselves live in the
          table headers (contacts-style) */}
      {anyColFilter && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
            Column filters active — {filteredFlat.length} match{filteredFlat.length === 1 ? '' : 'es'}
          </span>
          <button
            onClick={() => setColFilters(EMPTY_COL_FILTERS)}
            style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--orange)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px' }}
          >
            Clear all
          </button>
        </div>
      )}

      {error && (
        <div style={{ background: 'var(--red-bg)', color: 'var(--red)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 16, border: '1px solid rgba(220,38,38,.2)' }}>
          {error}
        </div>
      )}

      {matchItem && <MatchContactPanel item={matchItem} firms={firms} getToken={getToken} onClose={() => setMatchItem(null)} onLinked={(e, c) => handleLinked(e, c)} onAddNew={handleAddNew} />}

      {/* Bug 5 fix: banner for expired Active trials that need a status decision */}
      {statusTab === 'Active' && grouped.expired.length > 0 && (
        <div style={{
          background: 'var(--amber-bg, #fffbeb)', border: '1px solid rgba(217,119,6,.35)',
          borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 14,
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 15 }}>⏰</span>
          <span style={{ fontWeight: 700, fontSize: 13, color: '#92400e' }}>
            {grouped.expired.length} trial{grouped.expired.length > 1 ? 's' : ''} past end date — still marked Active
          </span>
          <span style={{ fontSize: 12, color: '#92400e', opacity: 0.85 }}>
            Update each to Converted or Churned to keep the pipeline accurate.
          </span>
        </div>
      )}

      {/* ── Bulk actions (selection lives in the table) ─────────────────── */}
      {selectedIds.size > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--orange)' }}>{selectedIds.size} selected</span>
          <button className="btn btn-danger btn--sm" onClick={() => setShowModal(true)}>Delete</button>
          <button className="btn btn--ghost btn--sm" onClick={() => setSelectedIds(new Set())}>Clear</button>
        </div>
      )}

      {/* ── Table (IMP-1806): contacts-style, filter + sort in the headers ── */}
      {displayList.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__title">No trials match this view</div>
          <div className="empty-state__sub">Try a different status tab, filter, or search term</div>
        </div>
      ) : (
        /* Contacts-page pattern: the wide table scrolls inside its card instead
           of stretching the page (which pushed the whole layout sideways). */
        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <input type="checkbox" checked={allSelected} ref={(el) => { if (el) el.indeterminate = someSelected; }} onChange={() => toggleSelectAll(displayList)} style={{ cursor: 'pointer' }} />
                </th>
                {TRIAL_TABLE_COLUMNS.map(({ key, label, filterKey }) => (
                  <th key={key} style={{ whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span onClick={() => cycleTableSort(key)} style={{ cursor: 'pointer', userSelect: 'none' }}>
                        {label} {tableSort?.col === key ? (tableSort.dir === 'asc' ? '↑' : '↓') : '↕'}
                      </span>
                      {filterKey && (
                        <TrialColFilter
                          compact
                          label={label}
                          options={colOptions[filterKey]}
                          selected={colFilters[filterKey]}
                          onChange={(s) => setColFilters((p) => ({ ...p, [filterKey]: s }))}
                        />
                      )}
                    </div>
                  </th>
                ))}
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayList.map((t, i) => (
                <TrialRow
                  key={t.trial_id ?? i}
                  trial={t}
                  isSelected={selectedIds.has(t.trial_id)}
                  selectedTrials={selectedTrials}
                  onToggleSelect={() => toggleSelect(t.trial_id)}
                  onOpenTimeline={onOpenTimeline}
                  onOpenDraft={onOpenDraft}
                  getToken={getToken}
                  onRefresh={onRefresh}
                  contacts={contacts}
                  firms={firms}
                  onOpenAddContact={onOpenAddContact}
                  patchTrial={patchTrial}
                  upsertContact={upsertContact}
                  isHighlighted={highlightTrialId === t.trial_id}
                  onClearHighlight={onClearTrialHighlight}
                  amplitudeScores={amplitudeScores}
                  watchlistMap={watchlistMap}
                />
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {showModal && (
        <TrialDeleteModal trials={selectedTrials} busy={deleteBusy} onConfirm={handleDeleteConfirmed} onCancel={() => setShowModal(false)} />
      )}
    </div>
  );
}

function StatusBarDivider() {
  return <div style={{ width: 1, height: 16, background: 'var(--border)' }} />;
}

function StatusBarStat({ label, count, color, active, onClick }) {
  return (
    <button
      onClick={onClick ?? undefined}
      disabled={!onClick}
      style={{
        display: 'flex', alignItems: 'baseline', gap: 5, padding: '3px 6px', borderRadius: 6,
        border: 'none', background: active ? 'var(--surface-2)' : 'none', cursor: onClick ? 'pointer' : 'default',
        fontSize: 13.5, color: 'var(--text-3)',
      }}
    >
      <b style={{ fontSize: 14, color: color ?? 'var(--text)' }}>{count}</b> {label}
    </button>
  );
}

function getTimelinePref() {
  try { return localStorage.getItem(TIMELINE_PREF_KEY) !== 'false'; } catch { return true; }
}
function setTimelinePref(v) {
  try { localStorage.setItem(TIMELINE_PREF_KEY, String(v)); } catch {}
}

// ── TrialInternalNotesPanel — internal notes on the expanded row ─────────────
//
// Surfaces the trial_notes table (previously write-only from the UI: the
// check-in flow wrote there but nothing read it back). Home of the churn
// feedback draft (IMP-1808) and any ad-hoc internal-only notes — nothing here
// touches the contact timeline.
function TrialInternalNotesPanel({ trial, getToken }) {
  const [notes,  setNotes]  = useState(null); // null = loading
  const [error,  setError]  = useState(null);
  const [draft,  setDraft]  = useState('');
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [copiedId, setCopiedId] = useState(null);

  const _v = (x) => (x && typeof x === 'object' && 'value' in x) ? String(x.value ?? '') : String(x ?? '');

  async function load() {
    try { setNotes(await apiGetTrialNotes(getToken, trial.trial_id)); }
    catch (e) { setError(e.message); setNotes([]); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [trial.trial_id]);

  async function addNote() {
    if (!draft.trim()) return;
    setSaving(true); setError(null);
    try {
      await apiAddTrialNote(getToken, trial.trial_id, { note: draft.trim(), add_to_timeline: false });
      setDraft(''); setAdding(false);
      await load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  function copyNote(n) {
    navigator.clipboard?.writeText(n.note ?? '').then(() => {
      setCopiedId(n.id);
      setTimeout(() => setCopiedId(null), 1500);
    }).catch(() => {});
  }

  return (
    <div>
      {notes === null && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>}
      {error && <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 6 }}>{error}</div>}
      {notes?.length === 0 && !error && (
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>No internal notes yet.</div>
      )}
      {notes?.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {notes.map((n) => {
            const text = String(n.note ?? '');
            const long = text.length > 240;
            const isOpen = expandedIds.has(n.id);
            return (
              <div key={n.id} style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 7, padding: '8px 10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 10.5, color: 'var(--text-3)', fontWeight: 600 }}>
                    {[n.owner, fmtDateTime(_v(n.created_at))].filter(Boolean).join(' · ')}
                  </span>
                  <div style={{ flex: 1 }} />
                  <button className="link" style={{ fontSize: 10.5, color: copiedId === n.id ? 'var(--green)' : 'var(--blue)' }} onClick={() => copyNote(n)}>
                    {copiedId === n.id ? 'Copied ✓' : 'Copy'}
                  </button>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-2)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {long && !isOpen ? `${text.slice(0, 240)}…` : text}
                </div>
                {long && (
                  <button className="link" style={{ fontSize: 11, color: 'var(--blue)', marginTop: 3 }}
                    onClick={() => setExpandedIds((prev) => { const s = new Set(prev); s.has(n.id) ? s.delete(n.id) : s.add(n.id); return s; })}>
                    {isOpen ? 'Show less' : 'Show more'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div style={{ marginTop: 8 }}>
        {adding ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <textarea rows={3} autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Internal note — not logged to the contact timeline"
              style={{ width: '100%', fontSize: 12, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', resize: 'vertical', boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn--primary btn--sm" style={{ fontSize: 11 }} disabled={!draft.trim() || saving} onClick={addNote}>
                {saving ? 'Saving…' : 'Save note'}
              </button>
              <button className="btn btn--ghost btn--sm" style={{ fontSize: 11 }} disabled={saving} onClick={() => { setAdding(false); setDraft(''); }}>Cancel</button>
            </div>
          </div>
        ) : (
          <button className="btn btn--ghost btn--sm" style={{ fontSize: 11 }} onClick={() => setAdding(true)}>+ Add note</button>
        )}
      </div>
    </div>
  );
}

// ── Trial column filters (IMP-1806) ──────────────────────────────────────────
//
// Spreadsheet-style multi-select filters over the loaded rows — all
// client-side (the trial list is small), unlike Contacts.jsx's server-backed
// HeaderFilterDropdown. OR within a column, AND across columns; the Features
// column is the exception (each selection is ANDed) so "hasn't used
// watchlists" + "has used chat" works as a gap-hunting query.

const TRIAL_LOGIN_BUCKETS = ['Today', 'Last 7 days', '8–14 days', '15+ days', 'Never'];
function trialLoginBucket(t) {
  const lastLogin = trialLastLogin(t);
  if (!lastLogin) return 'Never';
  const days = Math.floor((Date.now() - new Date(lastLogin).getTime()) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days <= 7) return 'Last 7 days';
  if (days <= 14) return '8–14 days';
  return '15+ days';
}

function trialFeatureList(t) {
  return String(t.features_used ?? '').split(',').map((s) => s.trim()).filter(Boolean);
}

function _trialRowContacts(t) {
  return t._contacts?.length ? t._contacts : (t._contact?.contact_id ? [t._contact] : []);
}

// Sector: the live product sector wins (derived by the app server from the
// user's default watchlist, IMP-1843 — stamped as _sector on the trial row by
// its product login, or on a linked contact by email). BigDough's
// sector_coverage tags ("Tech, Energy") are the fallback.
function trialProductSector(t) {
  if (t._sector) return { sector: t._sector, pack: t._sector_pack ?? null };
  const c = _trialRowContacts(t).find((x) => x._sector);
  return c ? { sector: c._sector, pack: c._sector_pack ?? null } : null;
}
function trialSectors(t) {
  const product = trialProductSector(t);
  if (product) return [product.sector];
  const out = new Set();
  for (const c of _trialRowContacts(t)) {
    for (const s of String(c.sector_coverage ?? '').split(',').map((x) => x.trim()).filter(Boolean)) out.add(s);
  }
  return [...out];
}

function trialRoles(t) {
  const out = new Set();
  for (const c of _trialRowContacts(t)) {
    const r = String(c.role ?? '').trim();
    if (r) out.add(r);
  }
  return [...out];
}

function trialHealthLabel(t) {
  return t._health?.score === 'red' ? 'At risk' : t._health?.score === 'amber' ? 'Watch' : 'Healthy';
}

const EMPTY_COL_FILTERS = { org: new Set(), sector: new Set(), role: new Set(), feature: new Set(), login: new Set(), health: new Set(), usage: new Set() };

function trialPrimaryName(t) {
  return _trialRowContacts(t).map((c) => c.name ?? c.email).filter(Boolean).join(', ')
    || t.clerk_name || t.amplitude_user_id || '';
}

// Contact-level Amplitude usage score ({ score, tier, … }) for a trial row —
// keyed by the linked contact's email, falling back to the trial's own email
// for roster rows that aren't contact-linked yet.
function trialAmpScore(t, amplitudeScores) {
  if (!amplitudeScores) return null;
  const email = String(_trialRowContacts(t)[0]?.email ?? t.amplitude_user_id ?? '').toLowerCase();
  return (email && amplitudeScores[email]) || null;
}

function trialUsageTierLabel(t, amplitudeScores) {
  const s = trialAmpScore(t, amplitudeScores);
  return s ? (TIER_LABEL[s.tier] ?? s.tier) : 'No data';
}

// Table columns (IMP-1806). `sortVal(t, amplitudeScores)` feeds the header
// sort; `filterKey` wires the header dropdown to colFilters/colOptions.
const TRIAL_TABLE_COLUMNS = [
  { key: 'contact',  label: 'Contact',    filterKey: null,      sortVal: (t) => trialPrimaryName(t).toLowerCase() },
  { key: 'org',      label: 'Org',        filterKey: 'org',     sortVal: (t) => (t._firm?.firm_name ?? '').toLowerCase() },
  { key: 'role',     label: 'Role',       filterKey: 'role',    sortVal: (t) => (trialRoles(t)[0] ?? '').toLowerCase() },
  { key: 'sector',   label: 'Sector',     filterKey: 'sector',  sortVal: (t) => (trialSectors(t)[0] ?? '').toLowerCase() },
  { key: 'started',  label: 'Started',    filterKey: null,      sortVal: (t) => String(t.start_date ?? '') },
  { key: 'ends',     label: 'Ends',       filterKey: null,      sortVal: (t) => String(t.end_date ?? '') },
  { key: 'login',    label: 'Last login', filterKey: 'login',   sortVal: (t) => { const ll = trialLastLogin(t); return ll ? new Date(ll).getTime() : -Infinity; } },
  // Same sort as Customers' Features column (count field, list fallback)
  { key: 'features', label: 'Features',   filterKey: 'feature', sortVal: (t) => trialFeatureCount(t) },
  { key: 'usage',    label: 'Usage',      filterKey: 'usage',   sortVal: (t, amp) => trialAmpScore(t, amp)?.score ?? -1 },
  { key: 'health',   label: 'Health',     filterKey: 'health',  sortVal: (t) => ({ red: 0, amber: 1, green: 2 })[t._health?.score] ?? 3 },
];

// Fixed-position coords for a popup anchored to a button's rect. The trial
// table lives in an overflow scroll container that clips absolutely-positioned
// children (the status menu on bottom rows was half-hidden), so popups render
// position:fixed instead — and flip above the anchor when the estimated
// height doesn't fit below the viewport edge.
function fixedPopupPos(rect, { height = 200, width = 250, align = 'left' } = {}) {
  const top = rect.bottom + 4 + height > window.innerHeight
    ? Math.max(8, rect.top - 4 - height)
    : rect.bottom + 4;
  return align === 'right'
    ? { top, right: Math.max(8, window.innerWidth - rect.right) }
    : { top, left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)) };
}

function TrialColFilter({ label, options, selected, onChange, compact = false }) {
  const [open, setOpen] = useState(false);
  const [pos,  setPos]  = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    function onClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    function onKey(e) { if (e.key === 'Escape') setOpen(false); }
    // The panel is position:fixed, so close it when anything else scrolls
    // (scrolls inside the panel's own option list are fine).
    function onScroll(e) { if (ref.current && ref.current.contains(e.target)) return; setOpen(false); }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const active = selected.size > 0;
  function toggleOpen(e) {
    setPos(fixedPopupPos(e.currentTarget.getBoundingClientRect(), { height: 300, width: 240 }));
    setOpen((v) => !v);
  }
  function toggle(v) {
    const n = new Set(selected);
    n.has(v) ? n.delete(v) : n.add(v);
    onChange(n);
  }

  return (
    <div ref={ref} style={{ position: 'relative', display: compact ? 'inline-flex' : 'block' }}>
      {compact ? (
        // Header-embedded trigger (contacts-style): just the funnel glyph,
        // orange when the column has an active filter.
        <button
          onClick={toggleOpen}
          title={`Filter by ${label.toLowerCase()}${active ? ` (${selected.size} active)` : ''}`}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', lineHeight: 1,
            fontSize: 11, color: active ? 'var(--orange)' : 'var(--text-3)', fontWeight: active ? 800 : 400,
          }}
        >
          {active ? `▼${selected.size}` : '▽'}
        </button>
      ) : (
        <button
          onClick={toggleOpen}
          style={{
            fontSize: 11.5, fontWeight: 600, padding: '4px 9px', borderRadius: 6, cursor: 'pointer',
            border: `1px solid ${active ? 'var(--orange)' : 'var(--border)'}`,
            background: active ? 'var(--orange-bg, #fff7ed)' : 'var(--surface)',
            color: active ? 'var(--orange)' : 'var(--text-3)', whiteSpace: 'nowrap',
          }}
        >
          {label}{active ? ` · ${selected.size}` : ''} <span style={{ fontSize: 9 }}>▾</span>
        </button>
      )}
      {open && (
        <div style={{
          position: 'fixed', top: pos?.top, left: pos?.left, zIndex: 9000, minWidth: 200, maxWidth: 280,
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,.16)', padding: 6, maxHeight: 300, overflowY: 'auto',
          // Undo the th typography (uppercase/letter-spacing) when embedded in a header
          textTransform: 'none', letterSpacing: 'normal', fontWeight: 400, whiteSpace: 'normal',
        }}>
          {options.length === 0 && (
            <div style={{ fontSize: 11.5, color: 'var(--text-3)', padding: '6px 8px' }}>No values in this view</div>
          )}
          {options.map(({ value, label: optLabel, count, heading }) => heading ? (
            <div key={value} style={{ fontSize: 9.5, fontWeight: 800, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.05em', padding: '6px 8px 2px' }}>
              {optLabel}
            </div>
          ) : (
            <label key={value} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '4px 8px', borderRadius: 5, cursor: 'pointer', fontSize: 12.5 }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-2)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}>
              <input type="checkbox" checked={selected.has(value)} onChange={() => toggle(value)} style={{ cursor: 'pointer', margin: 0 }} />
              <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{optLabel}</span>
              {count != null && <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{count}</span>}
            </label>
          ))}
          {active && (
            <button
              onClick={() => onChange(new Set())}
              style={{ display: 'block', width: '100%', marginTop: 4, padding: '5px 8px', fontSize: 11.5, fontWeight: 600, color: 'var(--orange)', background: 'none', border: 'none', borderTop: '1px solid var(--border)', cursor: 'pointer', textAlign: 'left' }}
            >
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── TrialManageTabs — Check in | Manage trial ─────────────────────────────────

function TrialManageTabs({ trial, statusVal, setStatusVal, endDate, setEndDate, inlineNote, setInlineNote, logToTimeline, setLogToTimeline, logWeek, logText, logSaving, logError, totalWeeks, currentWeek, onClickWeek, onChangeText, onSave, onCancelWeekLog, isExpired, saving, error, done, handleSave, onClose }) {
  const [activeTab, setActiveTab] = useState('checkin');

  function extendEndDate(days) {
    const base = endDate || trial.end_date || todayISO();
    const d = new Date(base);
    d.setDate(d.getDate() + days);
    setEndDate(d.toISOString().slice(0, 10));
  }

  const showConvertedBox = statusVal === 'Converted';
  const showChurnedBox   = statusVal === 'Churned';

  const TabBtn = ({ id, label }) => (
    <button
      onClick={() => setActiveTab(id)}
      style={{
        flex: 1, padding: '6px 10px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 12,
        background: activeTab === id ? 'var(--navy)' : 'var(--surface-2)',
        color: activeTab === id ? '#fff' : 'var(--text-3)',
        borderRadius: activeTab === id ? 'var(--radius-sm)' : 0,
        transition: 'all .12s',
      }}
    >{label}</button>
  );

  return (
    <div style={{ background: 'var(--surface-2)', borderRadius: 'var(--radius)', border: '1px solid var(--border)', overflow: 'hidden', marginBottom: 4 }}>
      {/* Tab switcher */}
      <div style={{ display: 'flex', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)', padding: '4px 4px 0' }}>
        <TabBtn id="checkin" label="📝 Check in" />
        <TabBtn id="manage"  label="⚙ Manage trial" />
      </div>

      <div style={{ padding: '12px 14px' }}>
        {/* ── CHECK IN tab ── */}
        {activeTab === 'checkin' && (
          <div>
            {/* Week selector */}
            <div style={{ marginBottom: 8 }}>
              <label style={labelStyle}>Which week? <span style={{ textTransform: 'none', fontWeight: 400, fontSize: 10, letterSpacing: 0 }}>— pick the week this check-in is for</span></label>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {Array.from({ length: totalWeeks }, (_, i) => i + 1).map((w) => {
                  const hasLog = (trial._weekLogs?.[w]?.length ?? 0) > 0;
                  const isCurrent = w === currentWeek;
                  const selected = logWeek === w;
                  return (
                    <button
                      key={w}
                      type="button"
                      onClick={() => onClickWeek(w)}
                      style={{
                        padding: '3px 9px', borderRadius: 99, border: '1px solid',
                        fontSize: 11, fontWeight: 600, cursor: 'pointer',
                        background: selected ? 'var(--navy)' : hasLog ? 'var(--green-bg)' : isCurrent ? 'var(--amber-bg)' : 'var(--surface)',
                        borderColor: selected ? 'var(--navy)' : hasLog ? 'rgba(5,150,105,.3)' : isCurrent ? 'rgba(217,119,6,.3)' : 'var(--border)',
                        color: selected ? '#fff' : hasLog ? 'var(--green)' : isCurrent ? 'var(--amber)' : 'var(--text-3)',
                      }}
                    >
                      W{w}{isCurrent ? ' ·now' : ''}
                    </button>
                  );
                })}
              </div>
            </div>

            {logWeek && (
              <>
                <div style={{ marginBottom: 10 }}>
                  <label style={labelStyle}>W{logWeek} check-in notes</label>
                  <textarea
                    autoFocus
                    value={logText}
                    onChange={(e) => onChangeText(e.target.value)}
                    rows={3}
                    placeholder={`What happened in week ${logWeek}? Usage questions, blockers, feedback…`}
                    style={{ ...inputStyle, resize: 'vertical', borderColor: 'var(--navy)', background: '#f8faff' }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--text-2)', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={logToTimeline}
                      onChange={(e) => { setLogToTimeline(e.target.checked); setTimelinePref(e.target.checked); }}
                      style={{ cursor: 'pointer' }}
                    />
                    Log to contact timeline
                    <span style={{ fontSize: 10, color: 'var(--text-3)', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 4, padding: '1px 5px' }}>remembers preference</span>
                  </label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {logError && <span style={{ fontSize: 11.5, color: 'var(--red)' }}>{logError}</span>}
                    <button className="btn btn--ghost btn--sm" style={{ fontSize: 11 }} onClick={onCancelWeekLog} disabled={logSaving}>Cancel</button>
                    <button className="btn btn--primary btn--sm" style={{ fontSize: 11, background: 'var(--orange)', borderColor: 'var(--orange)' }} onClick={onSave} disabled={logSaving || !logText.trim()}>
                      {logSaving ? 'Saving…' : `Save W${logWeek} log`}
                    </button>
                  </div>
                </div>
              </>
            )}
            {!logWeek && (
              <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '4px 0' }}>← Select a week above to add a check-in note</div>
            )}
          </div>
        )}

        {/* ── MANAGE tab ── */}
        {activeTab === 'manage' && (
          <div>
            {/* Status pills */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Status</label>
              <div style={{ display: 'flex', gap: 7 }}>
                {[
                  { value: 'Active',    label: '● Active',    color: 'var(--blue)',  bg: 'var(--blue-bg)',  border: 'rgba(37,99,235,.3)' },
                  { value: 'Converted', label: '✓ Converted', color: 'var(--green)', bg: 'var(--green-bg)', border: 'rgba(5,150,105,.3)' },
                  { value: 'Churned',   label: '✗ Churned',   color: 'var(--red)',   bg: 'var(--red-bg)',   border: 'rgba(220,38,38,.3)' },
                ].map((s) => {
                  const sel = statusVal === s.value;
                  return (
                    <button
                      key={s.value}
                      type="button"
                      onClick={() => setStatusVal(s.value)}
                      style={{
                        flex: 1, padding: '7px 10px', borderRadius: 'var(--radius-sm)', cursor: 'pointer', textAlign: 'center',
                        border: `1.5px solid ${sel ? s.border : 'var(--border)'}`,
                        background: sel ? s.bg : 'var(--surface)', color: sel ? s.color : 'var(--text-3)', fontWeight: 600, fontSize: 12,
                      }}
                    >{s.label}</button>
                  );
                })}
              </div>
            </div>

            {/* Converted reason box */}
            {showConvertedBox && (
              <div style={{ marginBottom: 14, background: 'var(--green-bg)', border: '1px solid rgba(5,150,105,.25)', borderRadius: 'var(--radius-sm)', padding: '10px 12px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--green)', marginBottom: 8 }}>🎉 Marking as Converted</div>
                <label style={labelStyle}>Deal notes <span style={{ textTransform: 'none', fontWeight: 400, fontSize: 10, letterSpacing: 0 }}>(optional)</span></label>
                <textarea value={inlineNote} onChange={(e) => setInlineNote(e.target.value)} rows={2} placeholder="Deal size, who approved, close date, product tier…" style={{ ...inputStyle, resize: 'vertical' }} />
              </div>
            )}

            {/* Churned reason box */}
            {showChurnedBox && (
              <div style={{ marginBottom: 14, background: 'var(--red-bg)', border: '1px solid rgba(220,38,38,.2)', borderRadius: 'var(--radius-sm)', padding: '10px 12px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--red)', marginBottom: 8 }}>⚠ Marking as Churned — why didn't they convert?</div>
                <label style={labelStyle}>Reason <span style={{ textTransform: 'none', fontWeight: 400, fontSize: 10, letterSpacing: 0 }}>(optional)</span></label>
                <textarea value={inlineNote} onChange={(e) => setInlineNote(e.target.value)} rows={2} placeholder="Price objection, product gap, timing, competitor…" style={{ ...inputStyle, resize: 'vertical' }} />
              </div>
            )}

            {/* Generic note for Active */}
            {statusVal === 'Active' && (
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Note <span style={{ textTransform: 'none', fontWeight: 400, fontSize: 10, letterSpacing: 0 }}>(optional)</span></label>
                <textarea value={inlineNote} onChange={(e) => setInlineNote(e.target.value)} rows={2} placeholder="Any update to log on this trial…" style={{ ...inputStyle, resize: 'vertical' }} />
              </div>
            )}

            {/* End date with extend quickpicks */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>End date</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={inputStyle} />
              <div style={{ display: 'flex', gap: 5, marginTop: 6 }}>
                {[7, 14, 30].map((d) => (
                  <button key={d} type="button" onClick={() => extendEndDate(d)} style={{ padding: '3px 9px', borderRadius: 99, border: '1px solid var(--border)', background: 'var(--surface)', fontSize: 11, fontWeight: 600, cursor: 'pointer', color: 'var(--text-2)' }}>
                    +{d}d
                  </button>
                ))}
              </div>
            </div>

            {!showConvertedBox && !showChurnedBox && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--text-2)', cursor: 'pointer', marginBottom: 12 }}>
                <input type="checkbox" checked={logToTimeline} onChange={(e) => { setLogToTimeline(e.target.checked); setTimelinePref(e.target.checked); }} style={{ cursor: 'pointer' }} />
                Log to contact timeline
                <span style={{ fontSize: 10, color: 'var(--text-3)', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 4, padding: '1px 5px' }}>remembers preference</span>
              </label>
            )}

            {(error || done) && (
              <div style={{ background: error ? 'var(--red-bg)' : 'var(--green-bg)', color: error ? 'var(--red)' : 'var(--green)', padding: '8px 12px', borderRadius: 6, fontSize: 13, marginBottom: 10 }}>
                {error || done}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn--ghost btn--sm" onClick={onClose} disabled={saving}>Cancel</button>
              <button
                className="btn btn--primary btn--sm"
                style={{ background: showConvertedBox ? 'var(--green)' : showChurnedBox ? 'var(--red)' : 'var(--orange)', borderColor: showConvertedBox ? 'var(--green)' : showChurnedBox ? 'var(--red)' : 'var(--orange)' }}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : showConvertedBox ? 'Mark Converted' : showChurnedBox ? 'Mark Churned' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── TrialRow ──────────────────────────────────────────────────────────────────

// Inverted from the original "App · trial" chip (IMP-1816): every trial user
// is assumed to have an Implied app account, so only the EXCEPTION is tagged —
// manual CRM-only rows whose user can't actually log in.
function NoAppAccountChip({ trial }) {
  if (trial.clerk_user_id) return null;
  return (
    <span
      title="No Implied app account on the Clerk roster — CRM-only row; this user can't log in"
      style={{
        marginLeft: 6, fontSize: 9.5, fontWeight: 700, padding: '1px 6px', borderRadius: 5,
        background: 'var(--amber-bg, #fffbeb)', color: 'var(--amber)', border: '1px solid rgba(217,119,6,.25)',
        textTransform: 'uppercase', letterSpacing: '.3px', verticalAlign: 'middle',
      }}
    >
      No app account
    </span>
  );
}

// Confirm-merge dialog for an unlinked Clerk trial row (IMP-1816 job-change
// flow). The dialog itself now lives in components/MergeContactModal.jsx
// (shared with the Customers tab's Clerk-duplicate merge, IMP-1811); this
// wrapper supplies the trial-specific pieces.
function TrialMergeModal({ trial, contactId, firms = [], getToken, onClose, onMerged }) {
  return (
    <MergeContactModal
      title="Merge trial with contact"
      newEmail={String(trial.amplitude_user_id ?? '').toLowerCase()}
      contactId={contactId}
      firms={firms}
      getToken={getToken}
      onMerge={(cid, firmId) => mergeTrialContact(getToken, trial.trial_id, { contact_id: cid, firm_id: firmId })}
      onMerged={onMerged}
      onClose={onClose}
      tailNote="and this trial links to the contact."
    />
  );
}

function TrialRow({ trial, isSelected, selectedTrials = [], onToggleSelect, onOpenTimeline, onOpenDraft, getToken, onRefresh, contacts = [], firms = [], onOpenAddContact, patchTrial, upsertContact, isHighlighted, onClearHighlight, amplitudeScores, watchlistMap = {} }) {
  const [expanded,      setExpanded]      = useState(false);
  const [logWeek,       setLogWeek]       = useState(null);
  const [logText,       setLogText]       = useState('');
  const [logSaving,     setLogSaving]     = useState(false);
  const [logError,      setLogError]      = useState(null);
  const [logToTimeline, setLogToTimeline] = useState(true);
  const [manageOpen,    setManageOpen]    = useState(false);
  const [statusVal,     setStatusVal]     = useState(trial.status || 'Active');
  const [endDate,       setEndDate]       = useState(trial.end_date || '');
  const [inlineNote,    setInlineNote]    = useState('');
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState(null);
  const [done,          setDone]          = useState(null);
  const [linkOpen,      setLinkOpen]      = useState(false);
  const [linkPos,       setLinkPos]       = useState(null);
  const [mergeContactId, setMergeContactId] = useState(null); // contact the merge popover confirms against
  const [linkQuery,     setLinkQuery]     = useState('');
  const [linkBusy,      setLinkBusy]      = useState(false);
  const [linkResults,   setLinkResults]   = useState([]);
  const [convertOpen,   setConvertOpen]   = useState(false);
  const [churnOpen,     setChurnOpen]     = useState(false);
  const [rowMenuOpen,   setRowMenuOpen]   = useState(false);
  const [rowMenuPos,    setRowMenuPos]    = useState(null);
  const [unlockBusy,    setUnlockBusy]    = useState(false);
  const [lockPopoverOpen, setLockPopoverOpen] = useState(false);
  const [lockPopoverPos,  setLockPopoverPos]  = useState(null);
  const linkTimer = useRef(null);
  const rowRef    = useRef(null);
  const rowMenuRef = useRef(null);
  const linkBoxRef = useRef(null);

  useEffect(() => {
    if (!rowMenuOpen) return;
    function onClick(e) { if (rowMenuRef.current && !rowMenuRef.current.contains(e.target)) setRowMenuOpen(false); }
    function onKey(e) { if (e.key === 'Escape') setRowMenuOpen(false); }
    // Menu is position:fixed (escapes the table's scroll clip) — close on scroll.
    function onScroll() { setRowMenuOpen(false); }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [rowMenuOpen]);

  function closeLinkSearch() { setLinkOpen(false); setLinkQuery(''); setLinkResults([]); }

  // The link-contact search is a popover: close on outside click or Escape
  // (it previously could only be closed by re-clicking the button).
  useEffect(() => {
    if (!linkOpen) return;
    function onClick(e) {
      if (linkBoxRef.current && !linkBoxRef.current.contains(e.target)) {
        setLinkOpen(false); setLinkQuery(''); setLinkResults([]);
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') { setLinkOpen(false); setLinkQuery(''); setLinkResults([]); }
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onKey); };
  }, [linkOpen]);

  useEffect(() => {
    if (isHighlighted && rowRef.current) rowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [isHighlighted]);

  const primaryContacts = trial._contacts.length > 0 ? trial._contacts : (trial._contact?.contact_id ? [trial._contact] : []);
  const primaryName = primaryContacts.map((c) => c.name ?? c.email ?? '—').join(', ') || trial.amplitude_user_id || '—';
  const initials = getInitials(primaryContacts[0]?.name ?? primaryContacts[0]?.email ?? trial.amplitude_user_id ?? 'T');
  const aum = fmtAUM(trial._firm.aum);
  const noContact = !trial.contact_id && !(trial.contact_ids ?? '').trim() && trial._contacts.length === 0 && !trial._contact?.contact_id;

  // Amplitude score from contact-level scores (keyed by email; same lookup the
  // Usage column sort/filter uses, so cell and ordering always agree)
  const contactAmpScore = trialAmpScore(trial, amplitudeScores);

  // Coverage list change signal — did any contact on this trial fire save_watchlist?
  const watchlistHits = primaryContacts
    .map((c) => c.email ? watchlistMap[c.email.toLowerCase()] : null)
    .filter(Boolean);
  const watchlistData = watchlistHits[0] ?? null;

  const weekThis        = parseInt(trial.logins_this_week ?? '0') || 0;
  const hasAmpId        = Boolean(trial.amplitude_user_id?.trim());
  const rowLastLogin     = trialLastLogin(trial);
  const lastLoginDaysAgo = rowLastLogin ? Math.round((Date.now() - new Date(rowLastLogin).getTime()) / 86_400_000) : null;
  const rowFeatCount    = parseInt(trial.features_used_count ?? '0') || 0;
  const rowFeatList     = trialFeatureList(trial);
  const trendColor = trial._trend === '↑' ? 'var(--green)' : trial._trend === '↓' ? 'var(--red)' : 'var(--text-3)';

  const totalWeeks  = Math.max(1, Math.ceil(trial._totalDays / 7));
  const startMs     = new Date(trial.start_date || 0).getTime();
  const currentWeek = trial._daysOverdue > 0
    ? totalWeeks + 1
    : Math.min(totalWeeks, Math.max(1, Math.ceil((Date.now() - startMs) / (7 * 86_400_000))));

  function handleWeekClick(w) {
    setLogWeek(logWeek === w ? null : w);
    setLogText('');
    setLogError(null);
    if (!expanded) setExpanded(true);
  }

  async function saveWeekLog() {
    if (!logText.trim() || !logWeek) return;
    setLogSaving(true); setLogError(null);
    try {
      const stamp = `[W${logWeek} ${todayISO()}] ${logText.trim()}`;
      const newNotes = trial.notes ? `${trial.notes}\n${stamp}` : stamp;
      await saveTrial(getToken, trial.trial_id, { notes: newNotes });
      if (logToTimeline) {
        await logTouchpoint(getToken, {
          contact_id: trial.contact_id,
          firm_id: trial.firm_id || trial._firm?.firm_id || trial._contact?.firm_id || '',
          subject: `[W${logWeek}] ${logText.trim()}`,
        });
      }
      setLogWeek(null); setLogText(''); onRefresh?.();
    } catch (e) {
      setLogError(e.message);
    } finally {
      setLogSaving(false);
    }
  }

  async function handleSave() {
    setSaving(true); setError(null); setDone(null);
    try {
      const trimmed = inlineNote.trim();
      const edits = { status: statusVal, end_date: endDate };
      if (trimmed) {
        const stamp = `[${todayISO()}] ${trimmed}`;
        edits.notes = trial.notes ? `${trial.notes}\n${stamp}` : stamp;
      }
      await saveTrial(getToken, trial.trial_id, edits);
      if (trimmed && logToTimeline) {
        await logTouchpoint(getToken, {
          contact_id: trial.contact_id,
          firm_id: trial.firm_id || trial._firm?.firm_id || trial._contact?.firm_id || '',
          subject: trimmed,
        });
      }
      onRefresh?.(); setInlineNote(''); setDone('Saved.'); setSaving(false); setManageOpen(false);
    } catch (e) {
      setError(e.message); setSaving(false);
    }
  }

  function handleLinkQueryChange(e) {
    const val = e.target.value; setLinkQuery(val);
    clearTimeout(linkTimer.current);
    if (val.trim().length < 1) { setLinkResults([]); return; }
    linkTimer.current = setTimeout(async () => {
      try { setLinkResults((await searchContacts(getToken, val.trim())).slice(0, 8)); }
      catch { setLinkResults([]); }
    }, 250);
  }
  async function handleLinkContact(c) {
    // Picking a contact whose email differs from the trial's Clerk email is a
    // job-change merge (IMP-1816): confirm old vs new email/firm in the merge
    // popover instead of silently linking and leaving the contact's email stale.
    const trialEmail   = String(trial.amplitude_user_id ?? '').toLowerCase().trim();
    const contactEmail = String(c.email ?? '').toLowerCase().trim();
    if (trialEmail && contactEmail !== trialEmail) {
      closeLinkSearch();
      setMergeContactId(c.contact_id);
      return;
    }
    setLinkBusy(true); setError(null);
    try {
      await saveTrial(getToken, trial.trial_id, { contact_id: c.contact_id, firm_id: c.firm_id || trial.firm_id });
      // Optimistic: reflect the link immediately; onRefresh confirms in the background.
      upsertContact?.({ contact_id: c.contact_id, name: c.name, email: c.email, firm_id: c.firm_id || trial.firm_id });
      patchTrial?.(trial.trial_id, {
        contact_id: c.contact_id, contact_ids: c.contact_id, firm_id: c.firm_id || trial.firm_id,
        suggested_contact_id: '', suggested_contact_name: '',
      });
      setLinkOpen(false); setLinkQuery(''); setLinkResults([]); onRefresh?.();
    } catch (e) { setError(e.message); }
    finally { setLinkBusy(false); }
  }

  // BQ DATE/TIMESTAMP cells can come back as { value: '…' } objects.
  const _bqStr = (v) => (v && typeof v === 'object' && 'value' in v) ? String(v.value ?? '') : String(v ?? '');
  const isChurned     = (trial.status ?? '').toLowerCase() === 'churned';
  const lockedDateStr = _bqStr(trial.locked_date).trim();
  const lockedAtStr   = _bqStr(trial.locked_at).trim();
  const borderColor = trial._group === 'act_today' ? 'var(--red)' : trial._group === 'this_week' ? 'var(--amber)' : trial._health?.score === 'red' ? 'var(--red)' : 'var(--border)';
  const [datePopoverOpen,   setDatePopoverOpen]   = useState(false);
  // Bulk-apply rule (same as Customers): editing dates on a row that is part
  // of a multi-selection applies the change to every selected trial.
  const dateEditTargets = isSelected && selectedTrials.length > 1 ? selectedTrials : [trial];
  const [datePopoverPos,    setDatePopoverPos]    = useState(null);
  const [extendPopoverOpen, setExtendPopoverOpen] = useState(false);
  const [extendPopoverPos,  setExtendPopoverPos]  = useState(null);
  const isExpiredGroup = trial._group === 'expired';
  const isConvertCandidate = !isExpiredGroup && trial._health?.score === 'green' && trial._daysLeft <= 14;
  const reasonLine = healthReasonLine(trial);
  const nextAction = nextActionLabel(trial);
  const healthColor = trial._health?.score === 'red' ? 'var(--red)' : trial._health?.score === 'amber' ? 'var(--amber)' : 'var(--green)';

  // Rows lift above siblings while a popover/menu is open so absolutely
  // positioned dropdowns aren't stacked under the next row.
  const rowLifted = rowMenuOpen || linkOpen || mergeContactId || datePopoverOpen || extendPopoverOpen;

  return (
    <>
      <tr
        ref={rowRef}
        className={`trial-row-item${isSelected ? ' trial-row-item--selected' : ''}${isHighlighted ? ' trial-card--highlight' : ''}`}
        onAnimationEnd={isHighlighted ? onClearHighlight : undefined}
        style={{ cursor: 'pointer', ...(rowLifted ? { position: 'relative', zIndex: 40 } : {}) }}
        onClick={(e) => { if (e.target.closest('button,input,a,select,textarea')) return; setExpanded((v) => !v); }}
      >
        {/* Checkbox — left border carries the urgency color the old grouped view showed */}
        <td onClick={(e) => e.stopPropagation()} style={{ width: 36, borderLeft: `3px solid ${borderColor}` }}>
          <input type="checkbox" checked={isSelected} onChange={onToggleSelect} style={{ cursor: 'pointer', margin: 0 }} />
        </td>

        {/* Contact: avatar + name (or the link-contact flow for roster rows) */}
        <td style={{ minWidth: 170, maxWidth: 240 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, minWidth: 0 }}>
            <div className="contact-initials" style={{ background: '#556579', marginTop: 1 }}>{initials}</div>
            <div style={{ minWidth: 0, flex: 1 }}>
          {noContact ? (
            <div ref={linkBoxRef} onClick={(e) => e.stopPropagation()} style={{ position: 'relative' }}>
              {(trial.clerk_name || trial.amplitude_user_id) && (
                <div style={{ fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {trial.clerk_name || trial.amplitude_user_id} <NoAppAccountChip trial={trial} />
                </div>
              )}
              {trial.clerk_name && trial.amplitude_user_id && (
                <div style={{ fontSize: 11, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {trial.amplitude_user_id}
                </div>
              )}
              {trial.suggested_contact_id && (
                <button
                  className="trial-suggest-chip"
                  title="Looks like the same person under an older email — click to review and merge"
                  onClick={() => { closeLinkSearch(); setMergeContactId(trial.suggested_contact_id); }}
                >
                  ✦ Suggested: <b>{trial.suggested_contact_name || 'existing contact'}</b>
                  <span className="trial-suggest-chip__hint">merge →</span>
                </button>
              )}
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <button className="btn btn--ghost btn--sm" style={{ fontSize: 11 }}
                  onClick={(e) => {
                    setMergeContactId(null);
                    setLinkPos(fixedPopupPos(e.currentTarget.getBoundingClientRect(), { height: 280, width: 300 }));
                    (linkOpen ? closeLinkSearch() : setLinkOpen(true));
                  }}>
                  Link contact
                </button>
                {onOpenAddContact && (
                  <button
                    className="btn btn--ghost btn--sm" style={{ fontSize: 11 }}
                    title="Create a new contact prefilled from the Clerk account (name, email; firm suggested by email domain) — review before saving"
                    onClick={() => onOpenAddContact({
                      // Clerk accounts often have no profile name — fall back to a
                      // title-cased guess from the email ("anvit.reddy@…" → "Anvit Reddy").
                      name: trial.clerk_name || String(trial.amplitude_user_id ?? '').split('@')[0].replace(/[._-]+/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase()),
                      email: trial.amplitude_user_id || '',
                      source: 'Clerk Trial',
                    })}
                  >
                    + New contact
                  </button>
                )}
              </div>
              {mergeContactId && (
                <TrialMergeModal
                  trial={trial} contactId={mergeContactId} firms={firms} getToken={getToken}
                  onClose={() => setMergeContactId(null)}
                  onMerged={(r) => {
                    setMergeContactId(null);
                    // Optimistic: show the merged contact + firm the moment the
                    // dialog closes; the bundle refresh confirms shortly after.
                    if (r?.contact?.contact_id) {
                      upsertContact?.({ contact_id: r.contact.contact_id, name: r.contact.name, email: r.newEmail, firm_id: r.firmId });
                      patchTrial?.(trial.trial_id, {
                        contact_id: r.contact.contact_id, contact_ids: r.contact.contact_id, firm_id: r.firmId,
                        suggested_contact_id: '', suggested_contact_name: '',
                      });
                    }
                    onRefresh?.();
                  }}
                />
              )}
              {error && <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 2 }}>{error}</div>}
              {linkOpen && (
                <div style={{ position: 'fixed', top: linkPos?.top, left: linkPos?.left, zIndex: 9000, width: 300, maxWidth: '80vw', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,.14)', padding: 8, display: 'flex', flexDirection: 'column', gap: 4, whiteSpace: 'normal' }}>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <input autoFocus className="form-input" style={{ fontSize: 12, padding: '4px 8px', flex: 1 }} placeholder="Search contacts… (Esc to close)" value={linkQuery} onChange={handleLinkQueryChange} />
                    <button className="btn btn--ghost btn--sm" style={{ fontSize: 12, padding: '3px 8px' }} onClick={closeLinkSearch} title="Close">✕</button>
                  </div>
                  {linkResults.length > 0 && (
                    <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, overflow: 'auto', maxHeight: 220 }}>
                      {linkResults.map((c) => (
                        <button key={c.contact_id} className="trial-link-result" disabled={linkBusy} onClick={() => handleLinkContact(c)}>
                          <span style={{ fontWeight: 500 }}>{c.name || c.email}</span>
                          {c.name && <span style={{ color: 'var(--text-3)', marginLeft: 6 }}>{c.email}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <>
              <button className="link" style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)', display: 'block', textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}
                onClick={(e) => { e.stopPropagation(); onOpenTimeline?.(primaryContacts[0]); }}>
                {primaryName}
              </button>
              <div style={{ fontSize: 11, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {primaryContacts[0]?.email ?? trial.amplitude_user_id ?? ''}
                <NoAppAccountChip trial={trial} />
              </div>
              <CoverageListChip watchlist={watchlistData} />
            </>
          )}
            </div>
          </div>
        </td>

        {/* Org / Role / Sector — their own columns now (IMP-1806 table) */}
        <td style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150, fontSize: 12.5 }}>
          {trial._firm.firm_name || '—'}
        </td>
        <td style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 130, fontSize: 12.5 }} title={primaryContacts[0]?.role || undefined}>
          {primaryContacts[0]?.role || '—'}
        </td>
        <td
          style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 130, fontSize: 12.5 }}
          title={trialProductSector(trial)
            ? `From their Implied watchlist${trialProductSector(trial).pack ? ` · pack: ${trialProductSector(trial).pack}` : ''}`
            : (trialSectors(trial).join(', ') || undefined)}
        >
          {trialSectors(trial).join(', ') || '—'}
        </td>

        {/* Started — opens the same start/end date popover as the Ends cell */}
        <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap', fontSize: 12.5, color: 'var(--text-2)' }}>
          <button
            onClick={(e) => {
              setDatePopoverPos(fixedPopupPos(e.currentTarget.getBoundingClientRect(), { height: 280, width: 240 }));
              setDatePopoverOpen((v) => !v);
            }}
            title="Click to change the start/end dates"
            style={{ background: 'none', border: 'none', borderBottom: '1px dashed var(--border)', cursor: 'pointer', fontSize: 12.5, color: 'var(--text-2)', padding: 0 }}
          >
            {fmt(trial.start_date)}
          </button>
        </td>

        {/* Ends: clickable end date → date popover (the inline edit), days left/overdue badge */}
        <td onClick={(e) => e.stopPropagation()} style={{ position: 'relative', whiteSpace: 'nowrap', fontSize: 12.5 }}>
          <button
            onClick={(e) => {
              setDatePopoverPos(fixedPopupPos(e.currentTarget.getBoundingClientRect(), { height: 280, width: 240 }));
              setDatePopoverOpen((v) => !v);
            }}
            title="Click to change the end date or extend"
            style={{ background: 'none', border: 'none', borderBottom: '1px dashed var(--border)', cursor: 'pointer', fontSize: 12.5, color: 'var(--text-2)', padding: 0 }}
          >
            {fmt(trial.end_date)}
          </button>
          <span style={{
            marginLeft: 6, fontSize: 10.5, fontWeight: 700, padding: '1px 6px', borderRadius: 5,
            background: trial._daysOverdue > 0 ? 'var(--red-bg)' : trial._daysLeft <= 7 ? 'var(--amber-bg, #fffbeb)' : 'var(--surface-2)',
            color: trial._daysOverdue > 0 ? 'var(--red)' : trial._daysLeft <= 7 ? 'var(--amber)' : 'var(--text-3)',
          }}>
            {trial._daysOverdue > 0 ? `Expired ${trial._daysOverdue}d` : `${trial._daysLeft}d left`}
          </span>
          {datePopoverOpen && (
            <TrialDatePopover trial={trial} targets={dateEditTargets} getToken={getToken} onRefresh={onRefresh} pos={datePopoverPos} onClose={() => setDatePopoverOpen(false)} />
          )}
        </td>

        {/* Last login (IMP-1806): explicit "Never" beats a blank. The value is
            max(Clerk sign-in, Amplitude activity) — the tooltip shows both, so
            "Today" next to older product usage doesn't read as a bug. */}
        <td
          title={hasAmpId ? [
            trial.clerk_last_sign_in ? `Signed in (Clerk): ${String(trial.clerk_last_sign_in).slice(0, 10)}` : 'Signed in (Clerk): —',
            trial.last_login ? `Product activity (Amplitude): ${String(trial.last_login).slice(0, 10)}` : 'Product activity (Amplitude): —',
          ].join('\n') : undefined}
          style={{
          whiteSpace: 'nowrap', fontSize: 12, fontWeight: 600,
          color: !hasAmpId ? 'var(--text-3)'
            : lastLoginDaysAgo == null ? 'var(--red)'
            : lastLoginDaysAgo > 7 ? 'var(--amber)' : 'var(--text-2)',
        }}>
          {!hasAmpId ? '—'
            : lastLoginDaysAgo == null ? 'Never'
            : lastLoginDaysAgo <= 0 ? 'Today'
            : `${lastLoginDaysAgo}d ago`}
        </td>

        {/* Feature depth */}
        <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}
          title={rowFeatList.length ? rowFeatList.join(', ') : undefined}>
          <span style={{ fontWeight: 600, color: !hasAmpId ? 'var(--text-3)' : rowFeatCount === 0 ? 'var(--red)' : 'var(--text-2)' }}>
            {!hasAmpId ? '—' : rowFeatCount}
          </span>
        </td>

        {/* Product-usage score — same number the expanded Product Usage panel shows */}
        <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
          {contactAmpScore ? (
            <div title="Product usage score — expand the row for the full breakdown">
              <span style={{ fontWeight: 800, fontSize: 14, color: TIER_COLOR[contactAmpScore.tier] ?? 'var(--text-2)' }}>
                {contactAmpScore.score}
              </span>
              <div style={{ fontSize: 10.5, fontWeight: 600, color: TIER_COLOR[contactAmpScore.tier] ?? 'var(--text-3)' }}>
                {TIER_LABEL[contactAmpScore.tier] ?? contactAmpScore.tier}
              </div>
            </div>
          ) : (
            <span style={{ color: 'var(--text-3)' }}>—</span>
          )}
        </td>

        {/* Health with reason + next action */}
        <td style={{ fontSize: 12, minWidth: 130, maxWidth: 200 }}>
          <div style={{ fontWeight: 700, color: healthColor }}>{TIER_LABEL[trial._health?.score === 'red' ? 'at-risk' : trial._health?.score === 'amber' ? 'watch' : 'healthy']}</div>
          <div style={{ fontSize: 11, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={reasonLine}>{reasonLine}</div>
          {nextAction !== '—' && (
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Next: {nextAction}</div>
          )}
        </td>

        {/* Actions — every row gets the same pair: Check in + Update status.
            The status menu is the single home for lifecycle branches (convert /
            churn / other); each will later also kick off its email pipeline,
            so the items carry room for a subtitle describing what fires. */}
        <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
        <div style={{ display: 'flex', gap: 3, flexShrink: 0, position: 'relative', alignItems: 'center', justifyContent: 'flex-end' }}>
          {/* Churned rows: full lock lifecycle (IMP-1808) — schedule a lock,
              edit its date (click the chip), cancel it, or unlock a locked
              account. Reactivating/converting stays in the status menu. */}
          {isChurned && (
            <>
              {lockedAtStr ? (
                <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 5, whiteSpace: 'nowrap', background: 'var(--red-bg)', color: 'var(--red)' }}>
                  🔒 Locked
                </span>
              ) : lockedDateStr ? (
                <button
                  title="Scheduled lock — click to change the date"
                  onClick={(e) => {
                    setLockPopoverPos(fixedPopupPos(e.currentTarget.getBoundingClientRect(), { height: 190, width: 230, align: 'right' }));
                    setLockPopoverOpen((v) => !v);
                  }}
                  style={{
                    fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 5, whiteSpace: 'nowrap',
                    background: 'var(--amber-bg, #fffbeb)', color: 'var(--amber)',
                    border: '1px dashed rgba(217,119,6,.45)', cursor: 'pointer',
                  }}>
                  🔒 Locks {fmt(lockedDateStr)} ✎
                </button>
              ) : (
                <button
                  className="btn btn--ghost btn--sm" style={{ fontSize: 11, padding: '3px 8px' }}
                  title="Schedule the Implied account to be locked by the daily sweep"
                  onClick={(e) => {
                    setLockPopoverPos(fixedPopupPos(e.currentTarget.getBoundingClientRect(), { height: 190, width: 230, align: 'right' }));
                    setLockPopoverOpen((v) => !v);
                  }}>
                  🔒 Schedule lock
                </button>
              )}
              {(lockedAtStr || lockedDateStr) && (
                <button
                  className="btn btn--ghost btn--sm" style={{ fontSize: 11, padding: '3px 8px' }}
                  disabled={unlockBusy}
                  title={lockedAtStr ? 'Unlock the Implied account (they came back)' : 'Cancel the scheduled lock'}
                  onClick={async () => {
                    setUnlockBusy(true); setError(null);
                    try {
                      await apiUnlockTrial(getToken, trial.trial_id);
                      patchTrial?.(trial.trial_id, { locked_date: null, locked_at: null });
                      onRefresh?.();
                    } catch (e) { setError(e.message); }
                    finally { setUnlockBusy(false); }
                  }}>
                  {unlockBusy ? 'Unlocking…' : (lockedAtStr ? 'Unlock' : 'Cancel lock')}
                </button>
              )}
              {lockPopoverOpen && (
                <TrialLockPopover
                  trial={trial}
                  initialDate={lockedDateStr ? lockedDateStr.slice(0, 10) : new Date(Date.now() + 7 * 86_400_000).toISOString().slice(0, 10)}
                  pos={lockPopoverPos}
                  getToken={getToken}
                  onRefresh={onRefresh}
                  patchTrial={patchTrial}
                  onClose={() => setLockPopoverOpen(false)}
                />
              )}
            </>
          )}
          {isExpiredGroup && (
            <>
              <button className="btn btn--ghost btn--sm" style={{ fontSize: 11, padding: '3px 8px' }}
                onClick={(e) => {
                  setExtendPopoverPos(fixedPopupPos(e.currentTarget.getBoundingClientRect(), { height: 230, width: 220, align: 'right' }));
                  setExtendPopoverOpen((v) => !v);
                }}>
                Extend
              </button>
              {extendPopoverOpen && (
                <TrialExtendPopover trial={trial} targets={dateEditTargets} getToken={getToken} onRefresh={onRefresh} pos={extendPopoverPos} onClose={() => setExtendPopoverOpen(false)} />
              )}
            </>
          )}
          <button className="btn btn--ghost btn--sm" style={{ fontSize: 11, padding: '3px 8px' }}
            onClick={() => { setError(null); setDone(null); setManageOpen((v) => !v); if (!expanded) setExpanded(true); }}>
            Check in
          </button>
          <div ref={rowMenuRef} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <button
              className={`btn btn--sm ${isExpiredGroup || isConvertCandidate ? 'btn--primary' : 'btn--ghost'}`}
              style={{ fontSize: 11, padding: '3px 8px' }}
              title={isExpiredGroup ? 'Trial is past its end date — decide Converted / Churned'
                : isConvertCandidate ? 'Healthy with ≤2 weeks left — likely convert candidate'
                : undefined}
              onClick={(e) => {
                setRowMenuPos(fixedPopupPos(e.currentTarget.getBoundingClientRect(), { height: 176, width: 250, align: 'right' }));
                setRowMenuOpen((v) => !v);
              }}>
              Update status
            </button>
            {rowMenuOpen && (
              <div style={{ position: 'fixed', top: rowMenuPos?.top, right: rowMenuPos?.right, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,.16)', zIndex: 9000, minWidth: 250, overflow: 'hidden', whiteSpace: 'normal' }}>
                {[
                  {
                    label: 'Convert to Customer', color: 'var(--green)',
                    sub: 'Clerk + CRM → converted · terms & follow-up',
                    onPick: () => setConvertOpen(true),
                  },
                  {
                    label: 'Mark churned', color: 'var(--red)',
                    sub: 'Clerk + CRM → churned · sets account lock date',
                    onPick: () => setChurnOpen(true),
                  },
                  {
                    label: 'Other…', color: 'var(--text)',
                    sub: 'Extend, reactivate, cancel, end date, notes',
                    onPick: () => { setError(null); setDone(null); setManageOpen(true); if (!expanded) setExpanded(true); },
                  },
                ].map(({ label, color, sub, onPick }) => (
                  <button key={label} className="trial-status-menu__item"
                    onClick={() => { setRowMenuOpen(false); onPick(); }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color }}>{label}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1 }}>{sub}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button className="btn btn--ghost btn--sm" style={{ fontSize: 11, padding: '3px 6px', color: 'var(--text-3)' }}
            onClick={() => setExpanded((v) => !v)} title={expanded ? 'Collapse' : 'Expand'}>
            {expanded ? '▲' : '▼'}
          </button>
        </div>

        {convertOpen && (
          <ConvertTrialModal trial={trial} getToken={getToken} onRefresh={onRefresh} onClose={() => setConvertOpen(false)} />
        )}
        {churnOpen && (
          <ChurnTrialModal trial={trial} getToken={getToken} onRefresh={onRefresh} onClose={() => setChurnOpen(false)} />
        )}
        </td>
      </tr>

      {/* ── Expanded detail row ───────────────────────────────────── */}
      {expanded && (
        <tr>
        <td colSpan={12} style={{ padding: '12px 14px', background: 'var(--surface)', borderLeft: `3px solid ${borderColor}` }}>

          {/* Check in / Manage tabs */}
          {manageOpen && (
            <TrialManageTabs
              trial={trial}
              statusVal={statusVal} setStatusVal={setStatusVal}
              endDate={endDate} setEndDate={setEndDate}
              inlineNote={inlineNote} setInlineNote={setInlineNote}
              logToTimeline={logToTimeline} setLogToTimeline={setLogToTimeline}
              logWeek={logWeek} logText={logText} logSaving={logSaving} logError={logError}
              totalWeeks={totalWeeks} currentWeek={currentWeek}
              onClickWeek={handleWeekClick} onChangeText={setLogText}
              onSave={saveWeekLog} onCancelWeekLog={() => { setLogWeek(null); setLogText(''); }}
              isExpired={trial._group === 'expired'}
              saving={saving} error={error} done={done}
              handleSave={handleSave}
              onClose={() => setManageOpen(false)}
            />
          )}

          {/* Product Usage — full width */}
          <div style={{ marginTop: manageOpen ? 16 : 0, paddingTop: manageOpen ? 14 : 0, borderTop: manageOpen ? '1px solid var(--border)' : 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.5px' }}>Product Usage</span>
              {!noContact && primaryContacts[0] && (
                <button className="link" style={{ fontSize: 11.5, color: 'var(--blue)' }} onClick={() => onOpenTimeline?.(primaryContacts[0])}>
                  View timeline →
                </button>
              )}
            </div>
            <AmplitudeUsagePanel trial={trial} contactAmpScore={contactAmpScore} />
          </div>

          {/* Usage Heatmap + Usage Journal — Amplitude-sourced (IMP-1757/1847).
              The panel renders its own two section headers: the heatmap
              auto-refreshes on open, the journal regenerates on click. */}
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
            <UsageJournalPanel trial={trial} getToken={getToken} />
          </div>

          {/* Internal Notes — trial_notes table (churn feedback drafts land here) */}
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.5px' }}>Internal Notes</span>
            </div>
            <TrialInternalNotesPanel trial={trial} getToken={getToken} />
          </div>

          {/* Weekly Check-ins — full width below usage */}
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.5px' }}>Weekly Check-ins</span>
              {/* Week dots legend */}
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: 'var(--text-3)', marginLeft: 4 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--green)', display: 'inline-block' }} /> logged</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><span style={{ width: 7, height: 7, borderRadius: '50%', border: '2px solid var(--red)', display: 'inline-block' }} /> missed</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><span style={{ width: 7, height: 7, borderRadius: '50%', border: '2px solid var(--amber)', display: 'inline-block' }} /> current</span>
              </span>
            </div>
            <WeeklyLogPanel
              trial={trial} totalWeeks={totalWeeks} currentWeek={currentWeek}
              logWeek={logWeek} logText={logText} logSaving={logSaving} logError={logError} logToTimeline={logToTimeline}
              onClickWeek={handleWeekClick} onChangeText={setLogText}
              onChangeLogToTimeline={setLogToTimeline} onSave={saveWeekLog}
              onCancel={() => { setLogWeek(null); setLogText(''); }}
              isExpired={trial._group === 'expired'}
            />
          </div>

          {/* Quick note — Phase 1 redesign */}
          <QuickNoteForm trial={trial} getToken={getToken} onRefresh={onRefresh} />
        </td>
        </tr>
      )}
    </>
  );
}

// ── WeeklyLogPanel ────────────────────────────────────────────────────────────

function WeeklyLogPanel({ trial, totalWeeks, currentWeek, logWeek, logText, logSaving, logError, logToTimeline, onClickWeek, onChangeText, onChangeLogToTimeline, onSave, onCancel, isExpired = false }) {
  const startMs = new Date(trial.start_date || 0).getTime();
  const [showAllExpired, setShowAllExpired] = useState(false);

  const weeks = Array.from({ length: totalWeeks }, (_, i) => i + 1);

  // For expired trials: only show weeks that have logs, unless expanded
  const visibleWeeks = isExpired && !showAllExpired
    ? weeks.filter((w) => (trial._weekLogs[w]?.length ?? 0) > 0)
    : weeks;

  const hasAnyLog = weeks.some((w) => (trial._weekLogs[w]?.length ?? 0) > 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      {visibleWeeks.map((w) => {
        const weekStartMs = startMs + (w - 1) * 7 * 86_400_000;
        const weekDate    = new Date(weekStartMs).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        const logs        = trial._weekLogs[w] ?? [];
        const hasLog      = logs.length > 0;
        const isPast      = w < currentWeek;
        const isCurrent   = w === currentWeek;
        const isFuture    = w > currentWeek;
        const isLogging   = logWeek === w;
        const dotColor    = hasLog ? 'var(--green)' : isExpired ? 'var(--border)' : isPast ? 'var(--red)' : isCurrent ? 'var(--amber)' : 'var(--border)';

        return (
          <div key={w}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 9, height: 9, borderRadius: '50%', flexShrink: 0, background: hasLog ? dotColor : 'transparent', border: `2px solid ${dotColor}` }} />
              <span style={{ fontSize: 11.5, fontWeight: 600, color: isFuture ? 'var(--text-3)' : 'var(--text-2)', minWidth: 22 }}>W{w}</span>
              <span style={{ fontSize: 10.5, color: 'var(--text-3)' }}>{weekDate}</span>
              {hasLog ? (
                <span style={{ fontSize: 10.5, color: 'var(--green)', marginLeft: 'auto' }}>✓</span>
              ) : !isFuture && (
                <button onClick={() => onClickWeek(w)} style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  + log
                </button>
              )}
            </div>
            {hasLog && logs.map((text, idx) => (
              <div key={idx} style={{ marginLeft: 15, fontSize: 11, color: 'var(--text-2)', paddingLeft: 8, borderLeft: '2px solid var(--border)', fontStyle: 'italic', marginTop: 3 }}>
                {text}
              </div>
            ))}
            {isLogging && (
              <div style={{ marginLeft: 15, marginTop: 5, display: 'flex', flexDirection: 'column', gap: 5 }}>
                <textarea autoFocus value={logText} onChange={(e) => onChangeText(e.target.value)} rows={2} placeholder={`W${w} check-in notes…`} style={{ ...inputStyle, fontSize: 12, resize: 'vertical' }} />
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-2)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={logToTimeline} onChange={(e) => onChangeLogToTimeline(e.target.checked)} />
                  Log to contact timeline
                </label>
                {logError && <div style={{ fontSize: 11.5, color: 'var(--red)' }}>{logError}</div>}
                <div style={{ display: 'flex', gap: 5 }}>
                  <button className="btn btn--ghost btn--sm" style={{ fontSize: 11 }} onClick={onCancel} disabled={logSaving}>Cancel</button>
                  <button className="btn btn--primary btn--sm" style={{ fontSize: 11 }} onClick={onSave} disabled={logSaving || !logText.trim()}>{logSaving ? 'Saving…' : 'Save log'}</button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Expired trials: show toggle and retroactive log entry point */}
      {isExpired && (
        <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 5 }}>
          <button
            onClick={() => setShowAllExpired((v) => !v)}
            style={{ fontSize: 11, color: 'var(--text-3)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }}
          >
            {showAllExpired ? '▴ hide weeks' : `▾ ${totalWeeks - visibleWeeks.length} unlogged week${totalWeeks - visibleWeeks.length !== 1 ? 's' : ''}`}
          </button>
          {logWeek === null && (
            <button
              onClick={() => onClickWeek(Math.min(totalWeeks, currentWeek - 1 || 1))}
              style={{ fontSize: 11, color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }}
            >
              + add retroactive note
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── AmplitudeUsagePanel ────────────────────────────────────────────────────────

function AmplitudeUsagePanel({ trial, contactAmpScore }) {
  // Contact-level amplitude score (rich, from userMonthly data)
  if (contactAmpScore) {
    const { score, tier, breakdown, monthlyValues, months, avgMsgsPerMonth } = contactAmpScore;
    const { recencyPts, volumePts, trendPts, consistencyPts, recencyLabel, trendLabel, recentMsgs, activeCount, monthsInSystem } = breakdown;
    const tierColor = TIER_COLOR[tier] ?? 'var(--text-2)';
    const tierBg    = TIER_BG[tier]    ?? 'var(--surface-2)';
    const tierLabel = TIER_LABEL[tier] ?? tier;
    const barMax    = Math.max(...(monthlyValues ?? [1]), 1);
    const monthLabels = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

    // Compute which bar indices fall within the trial period for the shaded region
    const trialStart = trial.start_date ? new Date(trial.start_date) : null;
    const trialEnd   = trial.end_date   ? new Date(trial.end_date)   : null;
    const trialBarIndices = new Set();
    if (trialStart && trialEnd && months) {
      months.forEach((ym, i) => {
        const [y, m] = ym.split('-').map(Number);
        const moStart = new Date(y, m - 1, 1);
        const moEnd   = new Date(y, m, 0); // last day of month
        if (moStart <= trialEnd && moEnd >= trialStart) trialBarIndices.add(i);
      });
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Score + breakdown row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          {/* Score badge */}
          <div style={{ background: tierBg, border: `1px solid ${tierColor}44`, borderRadius: 8, padding: '8px 14px', textAlign: 'center', flexShrink: 0 }}>
            <div style={{ fontSize: 26, fontWeight: 900, color: tierColor, lineHeight: 1 }}>{score}</div>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: tierColor, marginTop: 2 }}>{tierLabel}</div>
          </div>
          {/* Breakdown bars */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
            {[
              { label: 'Recency',     pts: recencyPts,     max: 30, note: recencyLabel },
              { label: 'Volume',      pts: volumePts,      max: 25, note: `${recentMsgs} msgs` },
              { label: 'Trend',       pts: trendPts,       max: 25, note: trendLabel },
              { label: 'Consistency', pts: consistencyPts, max: 20, note: `${activeCount}/${monthsInSystem} mo active` },
            ].map(({ label, pts, max, note }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 10, color: 'var(--text-3)', width: 70, flexShrink: 0 }}>{label}</span>
                <div style={{ flex: 1, background: 'var(--surface-2)', borderRadius: 2, height: 5, overflow: 'hidden' }}>
                  <div style={{ width: `${(pts / max) * 100}%`, height: '100%', background: pts / max >= 0.8 ? 'var(--green)' : pts / max >= 0.5 ? 'var(--amber)' : 'var(--red)', borderRadius: 2 }} />
                </div>
                <span style={{ fontSize: 10, color: 'var(--text-2)', width: 28, textAlign: 'right', flexShrink: 0 }}>{pts}/{max}</span>
                <span style={{ fontSize: 10, color: 'var(--text-3)', minWidth: 80 }}>{note}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Monthly bar chart */}
        {monthlyValues && monthlyValues.some((v) => v > 0) && (
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 5 }}>
              <span style={{ fontSize: 10, color: 'var(--text-3)' }}>Activity (6 mo)</span>
              <span style={{ fontSize: 10, color: 'var(--text-2)', fontWeight: 600 }}>
                avg {avgMsgsPerMonth} msgs/mo
              </span>
              <span style={{ fontSize: 10, color: 'var(--text-3)' }}>
                ({activeCount} active mo)
              </span>
              {trialBarIndices.size > 0 && (
                <span style={{ fontSize: 10, color: 'var(--primary)', marginLeft: 'auto' }}>
                  ▐ trial period
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 32 }}>
              {monthlyValues.map((v, i) => {
                const mo = months?.[i] ? parseInt(months[i].split('-')[1]) - 1 : i;
                const isCurrentMo    = i === monthlyValues.length - 1;
                const isTrialMonth   = trialBarIndices.has(i);
                const barColor = v > 0
                  ? isTrialMonth
                    ? 'var(--primary)'
                    : isCurrentMo ? 'var(--primary)' : 'var(--primary-muted, #93c5fd)'
                  : isTrialMonth ? 'rgba(37,99,235,.12)' : 'var(--surface-2)';
                return (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, position: 'relative' }}>
                    {isTrialMonth && (
                      <div style={{ position: 'absolute', inset: '-2px -1px', background: 'rgba(37,99,235,.07)', borderRadius: 2, pointerEvents: 'none' }} />
                    )}
                    <div style={{ width: '100%', background: barColor, borderRadius: 2, height: v > 0 ? `${Math.max(4, (v / barMax) * 28)}px` : 4, position: 'relative', zIndex: 1 }} />
                    <span style={{ fontSize: 8.5, color: isCurrentMo || isTrialMonth ? 'var(--primary)' : 'var(--text-3)', fontWeight: isCurrentMo || isTrialMonth ? 700 : 400 }}>{monthLabels[mo]}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Fallback: trial-level amplitude data (from Amplitude sync button)
  const hasAmplitudeId  = Boolean(trial.amplitude_user_id?.trim());
  const weekThis        = parseInt(trial.logins_this_week   ?? '0') || 0;
  const weekLast        = parseInt(trial.logins_last_week   ?? '0') || 0;
  const lastLogin       = trialLastLogin(trial);
  const featCount       = parseInt(trial.features_used_count ?? '0') || 0;
  const featList        = trial.features_used ? trial.features_used.split(',').map((s) => s.trim()).filter(Boolean) : [];
  const hasTrialData    = lastLogin || weekThis > 0 || weekLast > 0 || featCount > 0;

  if (!hasAmplitudeId) return <span style={{ fontSize: 11.5, color: 'var(--text-3)', fontStyle: 'italic' }}>No Amplitude ID linked to this contact</span>;
  if (!hasTrialData)   return <span style={{ fontSize: 11.5, color: 'var(--text-3)', fontStyle: 'italic' }}>No usage data — click Sync Amplitude usage</span>;

  // Bug 6: compute data age so the health score can be read in context.
  const amplitudeDataAgeDays = trial.amplitude_synced_at
    ? Math.round((Date.now() - new Date(trial.amplitude_synced_at).getTime()) / 86_400_000)
    : null;
  // Amplitude has a 24-48h pipeline delay. Warn if health looks bad but data is stale.
  const dataIsStale = amplitudeDataAgeDays !== null && amplitudeDataAgeDays >= 1;

  const trend      = weekThis >= weekLast + 2 ? '↑' : weekThis <= weekLast - 2 ? '↓' : '→';
  const trendColor = trend === '↑' ? 'var(--green)' : trend === '↓' ? 'var(--red)' : 'var(--text-3)';
  const barMax     = Math.max(weekThis, weekLast, 1);
  let loginDaysAgo = null, loginColor = 'var(--text)';
  if (lastLogin) {
    const diff = Math.round((Date.now() - new Date(lastLogin).getTime()) / 86_400_000);
    loginDaysAgo = diff === 0 ? 'Today' : `${diff}d ago`;
    loginColor = diff === 0 ? 'var(--green)' : diff <= 3 ? 'var(--text)' : diff <= 7 ? 'var(--amber)' : 'var(--red)';
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: 22, fontWeight: 800, color: loginColor, lineHeight: 1 }}>{loginDaysAgo ?? '—'}</span>
        <span style={{ fontSize: 10.5, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>last login</span>
        <span style={{ fontSize: 10, color: trendColor, fontWeight: 700, marginLeft: 'auto' }}>{weekThis} sess {trend}</span>
      </div>
      {/* Bug 6: data freshness badge so red health isn't misread as urgent when data is delayed */}
      {dataIsStale && (
        <div style={{ fontSize: 10.5, color: 'var(--amber)', display: 'flex', alignItems: 'center', gap: 4 }}>
          <span>⏱</span>
          <span>Amplitude data {amplitudeDataAgeDays}d old — may not reflect today's activity</span>
        </div>
      )}
      {featList.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 5px' }}>
          {featList.map((f) => (
            <span key={f} style={{ fontSize: 10.5, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 4, padding: '2px 6px', color: 'var(--text-2)' }}>{f}</span>
          ))}
        </div>
      )}
    </div>
  );
}
